import React, { useState, useContext, useMemo, useCallback, useEffect, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  TextField,
  Button,
  Typography,
  Box,
  Collapse,
  Paper,
  Grid,
  Skeleton,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Card,
  Switch,
  Alert,
  Stack,
  Divider,
  InputAdornment,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  useTheme,
  DialogActions,
  Dialog,
  DialogTitle,
  DialogContent,
  FormLabel,
  CircularProgress,
  Autocomplete,
} from "@mui/material";

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  // Print as PrintIcon,
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  LocalOffer as LocalOfferIcon,
  AccountBalance as AccountBalanceIcon,
  Inventory as InventoryIcon,
  Scale as ScaleIcon,
  Edit as EditIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  QrCode,
  Person,
  Phone,
  Home,
  Payments,
  ShoppingBag,
  Calculate,
  Summarize,
} from "@mui/icons-material";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DataContext } from "../../context/DataContext";
import { format } from "date-fns";
import QRScanner from "../../components/QRScanner";
import {
  parseItemObject,
} from "../../components/billPrintUtils";
import WeightEditDialog from "../../components/WeightEditDialog";
import WeightAdjustmentDialog from "../../components/WeightAdjustmentDialog";
import AdvancePaymentSelectorDialog from "../../components/AdvancePaymentSelectorDialog";

// Add these skeleton components at the top of your file (after imports)

// Skeleton Components
const CustomerInfoSkeleton = ({ isDark }) => (
  <Card sx={{ p: 3, borderRadius: 3, bgcolor: "transparent" }}>
    <Stack direction="row" alignItems="center" spacing={1} mb={3}>
      <Skeleton variant="rounded" width={36} height={36} />
      <Skeleton variant="text" width={200} height={32} />
    </Stack>
    <Stack spacing={2}>
      <Skeleton variant="rounded" height={56} />
      <Skeleton variant="rounded" height={56} />
      <Skeleton variant="rounded" height={56} />
      <Skeleton variant="rounded" height={56} />
      <Skeleton variant="rounded" height={56} />
    </Stack>
  </Card>
);

const ItemsSectionSkeleton = ({ isDark }) => (
  <Card sx={{ p: 3, borderRadius: 3, bgcolor: "transparent" }}>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      mb={2}
    >
      <Skeleton variant="text" width={150} height={32} />
      <Stack direction="row" spacing={2}>
        <Skeleton variant="rounded" width={180} height={40} />
        <Skeleton variant="rounded" width={120} height={40} />
      </Stack>
    </Stack>

    <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, bgcolor: "transparent" }}>
      <Skeleton variant="text" width={150} height={24} sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={12} md={3} key={i}>
            <Skeleton variant="rounded" height={40} />
          </Grid>
        ))}
      </Grid>
    </Paper>

    <TableContainer
      component={Paper}
      sx={{ borderRadius: 2, bgcolor: "transparent" }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            {[
              "Item",
              "HUID",
              "Weight",
              "Rate",
              "Wastage",
              "Total",
              "Actions",
            ].map((col) => (
              <TableCell key={col}>
                <Skeleton variant="text" width={60} height={24} />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {[1, 2, 3].map((row) => (
            <TableRow key={row}>
              {[1, 2, 3, 4, 5, 6, 7].map((cell) => (
                <TableCell key={cell}>
                  <Skeleton variant="text" width={80} height={32} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Card>
);

const BillDetailsDialog = ({
  open,
  onClose,
  consolidatedItemGroups,
  calculations,
  items,
  transactionType,
  itemConsolidationMode,
  showGST,
  gstMode,
  gstType
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Collect all items from consolidated groups or separate items
  const getAllItems = () => {
    if (itemConsolidationMode === 'consolidate') {
      const allItems = [];
      Object.values(consolidatedItemGroups).forEach(group => {
        group.items.forEach(item => {
          // Calculate total fine (weight * percentage)
          const totalPercentage = parseFloat(group.totalPercentage) || 0;
          const totalFine = (item.weight || 0) * (totalPercentage / 100);

          // Calculate amount from metal
          const metalRateNum = parseFloat(group.metalRate) || 0;
          let amountFromMetal = 0;
          if (metalRateNum > 0 && totalFine > 0) {
            amountFromMetal = metalRateNum * totalFine;
          }

          // Get stone and HUID charges
          const stonePrice = parseFloat(item.stonePrice) || parseFloat(group.stoneCharges) || 0;
          const huidCharge = parseFloat(item.huidCost) || (item.huid ? 0 : 0);

          // Total amount = amount from metal + stone charges + HUID charges
          const totalAmount = amountFromMetal + stonePrice + huidCharge;

          allItems.push({
            ...item,
            groupName: group.name,
            serialNo: item.serialNo || '-',
            grossWeight: item.grossWeight || 0,
            czWeight: item.czWeight || 0,
            pearlWeight: item.pearlWeight || 0,
            beltWeight: item.beltWeight || 0,
            otherWeight: item.otherWeight || 0,
            netWeight: item.weight || 0,
            stonePrice: stonePrice,
            huidCharge: huidCharge,
            totalPercentage: totalPercentage,
            metalRate: metalRateNum,
            totalFine: totalFine,
            amountFromMetal: amountFromMetal,
            totalAmount: totalAmount
          });
        });
      });
      return allItems;
    } else {
      return items.map(item => {
        const weight = parseFloat(item.weight) || 0;
        const metalRateNum = parseFloat(item.metalRate) || 0;
        const wastageNum = parseFloat(item.wastage) || 0;
        const purityNum = parseFloat(item.melting) || 75;
        const totalPercentage = purityNum + wastageNum;

        const totalFine = weight * (totalPercentage / 100);
        let amountFromMetal = 0;
        if (metalRateNum > 0 && totalFine > 0) {
          amountFromMetal = metalRateNum * totalFine;
        }

        const stonePrice = parseFloat(item.stonePrice) || 0;
        const huidCharge = parseFloat(item.huidCost) || (item.huid ? 0 : 0);
        const totalAmount = amountFromMetal + stonePrice + huidCharge;

        return {
          ...item,
          serialNo: item.serialNo || item.itemObject?.serialNo || '-',
          grossWeight: item.grossWeight || item.weight || 0,
          czWeight: item.czWeight || 0,
          pearlWeight: item.pearlWeight || 0,
          beltWeight: item.beltWeight || 0,
          otherWeight: item.otherWeight || 0,
          netWeight: weight,
          stonePrice: stonePrice,
          huidCharge: huidCharge,
          wastage: wastageNum,
          purity: purityNum,
          totalPercentage: totalPercentage,
          metalRate: metalRateNum,
          totalFine: totalFine,
          amountFromMetal: amountFromMetal,
          totalAmount: totalAmount
        };
      });
    }
  };

  const allItems = getAllItems();

  // Calculate totals including stone and HUID charges
  const totals = {
    totalGrossWeight: allItems.reduce((sum, item) => sum + (parseFloat(item.grossWeight) || 0), 0),
    totalCZWeight: allItems.reduce((sum, item) => sum + (parseFloat(item.czWeight) || 0), 0),
    totalPearlWeight: allItems.reduce((sum, item) => sum + (parseFloat(item.pearlWeight) || 0), 0),
    totalBeltWeight: allItems.reduce((sum, item) => sum + (parseFloat(item.beltWeight) || 0), 0),
    totalOtherWeight: allItems.reduce((sum, item) => sum + (parseFloat(item.otherWeight) || 0), 0),
    totalNetWeight: allItems.reduce((sum, item) => sum + (parseFloat(item.netWeight) || 0), 0),
    totalItems: allItems.length,
    totalStoneCost: allItems.reduce((sum, item) => sum + (parseFloat(item.stonePrice) || 0), 0),
    totalHuidCharge: allItems.reduce((sum, item) => sum + (parseFloat(item.huidCharge) || 0), 0),
    totalFine: allItems.reduce((sum, item) => sum + (parseFloat(item.totalFine) || 0), 0),
    totalAmountFromMetal: allItems.reduce((sum, item) => sum + (parseFloat(item.amountFromMetal) || 0), 0),
    // Total amount without metal rate (only stone + HUID)
    totalAmountWithoutMetal: allItems.reduce((sum, item) => sum + (parseFloat(item.stonePrice) || 0) + (parseFloat(item.huidCharge) || 0), 0),
    totalAmount: allItems.reduce((sum, item) => sum + (parseFloat(item.totalAmount) || 0), 0)
  };

  // Calculate GST on the total amount
  let cgst = 0, sgst = 0, igst = 0, grandTotal = totals.totalAmount;

  if (showGST && totals.totalAmount > 0) {
    if (gstMode === "add") {
      if (gstType === 'igst') {
        igst = totals.totalAmount * 0.03;
        grandTotal = totals.totalAmount + igst;
      } else {
        cgst = totals.totalAmount * 0.015;
        sgst = totals.totalAmount * 0.015;
        grandTotal = totals.totalAmount + cgst + sgst;
      }
    }
  }

  const formatNumber = (num, decimals = 3) => {
    if (!num || isNaN(num)) return "0";
    return parseFloat(num).toFixed(decimals);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backdropFilter: 'blur(20px)',
          backgroundColor: alpha(
            isDark ? theme.palette.background.paper : '#ffffff',
            isDark ? 0.3 : 0.6
          ),
          border: `1px solid ${alpha(
            isDark ? theme.palette.divider : '#e2e8f0',
            isDark ? 0.2 : 0.3
          )}`,
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{
        fontWeight: 700,
        background: isDark
          ? 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)'
          : 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${alpha(
          isDark ? theme.palette.divider : '#e2e8f0',
          isDark ? 0.1 : 0.2
        )}`,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Summarize fontSize="small" />
          Bill Details
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ overflowX: 'auto' }}>
          <TableContainer sx={{ maxHeight: 'calc(90vh - 120px)' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{
                  backgroundColor: alpha(isDark ? '#1e293b' : '#f8fafc', 0.8),
                }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>S.No.</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Serial No.</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Item Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Gross Wt (g)</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>CZ (g)</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Pearl (g)</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Belt (g)</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Other (g)</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Net Wt (g)</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Stone Cost (₹)</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>HUID (₹)</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Total %</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Metal Rate (₹/g)</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Fine Wt (g)</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Fine Amount (₹)</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>Total Amount (₹)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allItems.map((item, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{idx + 1}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{item.serialNo || '-'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{item.name || item.groupName || '-'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{formatNumber(item.grossWeight)}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{formatNumber(item.czWeight)}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{formatNumber(item.pearlWeight)}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{formatNumber(item.beltWeight)}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{formatNumber(item.otherWeight)}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 1, fontWeight: 600, color: '#f59e0b' }}>{formatNumber(item.netWeight)}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>₹{formatNumber(item.stonePrice, 2)}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>₹{formatNumber(item.huidCharge, 2)}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{formatNumber(item.totalPercentage, 1)}%</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>₹{formatNumber(item.metalRate, 2)}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>{formatNumber(item.totalFine, 3)}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>₹{formatNumber(item.amountFromMetal, 2)}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 1, fontWeight: 600 }}>₹{formatNumber(item.totalAmount, 2)}</TableCell>
                  </TableRow>
                ))}

                {/* Totals Row */}
                <TableRow sx={{
                  backgroundColor: alpha(isDark ? '#f59e0b' : '#f59e0b', 0.1),
                  borderTop: `2px solid ${alpha(isDark ? '#f59e0b' : '#f59e0b', 0.3)}`,
                }}>
                  <TableCell colSpan={3} sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                    <strong>TOTALS</strong>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{formatNumber(totals.totalGrossWeight)}</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{formatNumber(totals.totalCZWeight)}</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{formatNumber(totals.totalPearlWeight)}</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{formatNumber(totals.totalBeltWeight)}</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{formatNumber(totals.totalOtherWeight)}</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#f59e0b' }}>{formatNumber(totals.totalNetWeight)}</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>₹{formatNumber(totals.totalStoneCost, 2)}</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>₹{formatNumber(totals.totalHuidCharge, 2)}</TableCell>
                  <TableCell colSpan={2}></TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{formatNumber(totals.totalFine, 3)}</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>₹{formatNumber(totals.totalAmountFromMetal, 2)}</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#f59e0b' }}>₹{formatNumber(totals.totalAmount, 2)}</TableCell>
                </TableRow>

                {/* Additional Summary Row */}
                <TableRow>
                  <TableCell colSpan={16} sx={{ pt: 2, pb: 1 }}>
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 2,
                      p: 2,
                      borderRadius: 2,
                      background: alpha(isDark ? '#1e293b' : '#f8fafc', 0.5),
                    }}>
                      <Stack direction="row" spacing={4} flexWrap="wrap">
                        <Box>
                          <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                            Total Items:
                          </Typography>
                          <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
                            {totals.totalItems}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                            Total Net Weight:
                          </Typography>
                          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#f59e0b' }}>
                            {formatNumber(totals.totalNetWeight)} g
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                            Total Fine:
                          </Typography>
                          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#f59e0b' }}>
                            {formatNumber(totals.totalFine, 3)} g
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                            Stone + HUID:
                          </Typography>
                          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#f59e0b' }}>
                            ₹{formatNumber(totals.totalStoneCost + totals.totalHuidCharge, 2)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                            Fine Amount:
                          </Typography>
                          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#f59e0b' }}>
                            ₹{formatNumber(totals.totalAmountFromMetal, 2)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                            Total Amount:
                          </Typography>
                          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#f59e0b' }}>
                            ₹{formatNumber(totals.totalAmount, 2)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>

                    {/* GST Summary if applicable */}
                    {showGST && totals.totalAmount > 0 && (
                      <Box sx={{
                        mt: 2,
                        p: 2,
                        borderRadius: 2,
                        background: alpha(isDark ? '#1e293b' : '#f8fafc', 0.5),
                      }}>
                        <Stack direction="row" spacing={3} justifyContent="flex-end">
                          <Box>
                            <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                              Subtotal:
                            </Typography>
                            <Typography sx={{ fontWeight: 600 }}>₹{formatNumber(totals.totalAmount, 2)}</Typography>
                          </Box>
                          {gstType === 'igst' ? (
                            <Box>
                              <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                                IGST (3%):
                              </Typography>
                              <Typography sx={{ fontWeight: 600 }}>₹{formatNumber(igst, 2)}</Typography>
                            </Box>
                          ) : (
                            <>
                              <Box>
                                <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                                  CGST (1.5%):
                                </Typography>
                                <Typography sx={{ fontWeight: 600 }}>₹{formatNumber(cgst, 2)}</Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                                  SGST (1.5%):
                                </Typography>
                                <Typography sx={{ fontWeight: 600 }}>₹{formatNumber(sgst, 2)}</Typography>
                              </Box>
                            </>
                          )}
                          <Divider orientation="vertical" flexItem />
                          <Box>
                            <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                              Grand Total (with GST):
                            </Typography>
                            <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#f59e0b' }}>
                              ₹{formatNumber(grandTotal, 2)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: `1px solid ${alpha(isDark ? theme.palette.divider : '#e2e8f0', 0.1)}` }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            borderRadius: 2,
            background: isDark
              ? 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)'
              : 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const GstSectionSkeleton = ({ isDark }) => (
  <Card sx={{ p: 3, borderRadius: 3, bgcolor: "transparent" }}>
    <Skeleton variant="text" width={120} height={28} sx={{ mb: 1 }} />
    <Skeleton variant="rounded" width={200} height={42} sx={{ mb: 2 }} />
    <Skeleton variant="rounded" height={56} />
  </Card>
);

const PaymentSectionSkeleton = ({ isDark }) => (
  <Card sx={{ p: 3, borderRadius: 3, bgcolor: "transparent" }}>
    <Skeleton variant="text" width={150} height={32} sx={{ mb: 2 }} />
    <Grid container spacing={2}>
      {[1, 2, 3, 4].map((i) => (
        <Grid item xs={12} md={6} key={i}>
          <Skeleton variant="rounded" height={56} />
        </Grid>
      ))}
    </Grid>
  </Card>
);

const SummarySkeleton = ({ isDark }) => (
  <Card
    sx={{ p: 0, borderRadius: 3, overflow: "hidden", bgcolor: "transparent" }}
  >
    <Box sx={{ p: 3, pb: 2 }}>
      <Skeleton variant="rounded" width={200} height={36} />
    </Box>
    <Box sx={{ p: 3 }}>
      <Skeleton variant="text" width={150} height={28} sx={{ mb: 2 }} />
      {[1, 2, 3].map((i) => (
        <Box key={i} sx={{ mb: 2 }}>
          <Stack direction="row" justifyContent="space-between" mb={1}>
            <Skeleton variant="text" width={80} height={20} />
            <Skeleton variant="text" width={100} height={20} />
          </Stack>
          <Skeleton variant="rounded" height={4} />
        </Box>
      ))}

      <Box sx={{ p: 2, borderRadius: 1, mt: 2 }}>
        <Stack direction="row" justifyContent="space-between">
          <Skeleton variant="text" width={100} height={28} />
          <Skeleton variant="text" width={120} height={32} />
        </Stack>
      </Box>

      <Skeleton variant="text" width={130} height={28} sx={{ my: 2 }} />
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        {[1, 2].map((i) => (
          <Grid item xs={6} key={i}>
            <Skeleton variant="rounded" height={80} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        {[1, 2, 3].map((i) => (
          <Grid item xs={4} key={i}>
            <Skeleton variant="rounded" height={70} />
          </Grid>
        ))}
      </Grid>
    </Box>
  </Card>
);

const ActionButtonsSkeleton = ({ isDark }) => (
  <Card sx={{ p: 3, borderRadius: 3, bgcolor: "transparent" }}>
    <Stack direction="row" spacing={2}>
      <Skeleton variant="rounded" height={52} sx={{ flex: 1 }} />
      <Skeleton variant="rounded" height={52} sx={{ flex: 1 }} />
      <Skeleton variant="rounded" height={52} sx={{ flex: 1 }} />
    </Stack>
  </Card>
);

// Validation Schema
const validationSchema = Yup.object({
  customerName: Yup.string().required("Customer name is required"),
  // handledBy: Yup.string().required("Seller name is required"),
  gstNumber: Yup.string(),
  panNumber: Yup.string(),
  transactionDate: Yup.date().required("Transaction date is required"),
  newCustomerContact: Yup.string().when(['isNewCustomer'], {
    is: true,
    then: (schema) => schema.required("Contact number is required for new customers"),
    otherwise: (schema) => schema
  }),
  newCustomerAddress: Yup.string(),
  newCustomerDob: Yup.date().nullable(),
  newCustomerAnniversary: Yup.date().nullable(),
  cashPayment: Yup.number().min(0, "Must be positive").default(0),
  onlinePayment: Yup.number().min(0, "Must be positive").default(0),
  chequePayment: Yup.number().min(0, "Must be positive").default(0),
  otherPayment: Yup.number().min(0, "Must be positive").default(0),
  paymentDone: Yup.number().min(0, "Must be positive").default(0),
  discount: Yup.number().min(0, "Must be positive").default(0),
  totalAmount: Yup.number().when(['transactionType'], {
    is: "total",
    then: (schema) => schema.required("Total amount is required").min(0.01, "Must be greater than 0"),
    otherwise: (schema) => schema.min(0, "Must be positive").default(0)
  })
});

const ManualItemDialog = ({
  open,
  onClose,
  onSubmit,
  manualItemDetails,
  setManualItemDetails
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Get company details from context
  const { companyDetails } = useContext(DataContext);

  // State for master data
  const [itemCategories, setItemCategories] = useState([]);
  const [metalTypes, setMetalTypes] = useState([]);

  // Load master data from company details
  useEffect(() => {
    if (companyDetails && companyDetails.length > 0) {
      const company = companyDetails[0];

      if (company.items && Array.isArray(company.items)) {
        setItemCategories(company.items);
      }

      if (company.metals && Array.isArray(company.metals)) {
        setMetalTypes(company.metals);
      }
    }
  }, [companyDetails]);

  const handleChange = (field, value) => {
    const updated = { ...manualItemDetails, [field]: value };

    // Recalculate total when relevant fields change
    if (field === 'weight' || field === 'metalRate' || field === 'wastage' || field === 'labour' || field === 'calculationType') {
      updated.total = calculateManualItemTotal(updated);
    }

    setManualItemDetails(updated);
  };

  const calculateManualItemTotal = (details) => {
    const weight = parseFloat(details.weight) || 0;
    const metalRate = parseFloat(details.metalRate) || 0;
    const wastage = parseFloat(details.wastage) || 0;
    const labour = parseFloat(details.labour) || 0;

    if (details.calculationType === 'labour') {
      return (metalRate + labour) * weight;
    } else {
      // wastage calculation
      return weight * ((100 + wastage) / 100) * metalRate;
    }
  };

  const handleSubmit = () => {
    console.log('Submitting manual item:', manualItemDetails);
    if (manualItemDetails.metal === "Gold") {
      manualItemDetails.silverType = "gold";
    }
    onSubmit(manualItemDetails);
    setManualItemDetails({
      name: '',
      metal: 'Silver',
      silverType: '999silver',
      calculationType: 'labour',
      metalRate: '',
      labour: '',
      wastage: '',
      weight: '',
      hsnCode: '',
      huid: '',
      total: 0,
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{
        fontWeight: 700,
        background: isDark
          ? 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)'
          : 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}>
        <AddIcon />
        Add from Gross Stock
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Item Name with Autocomplete */}
          <Autocomplete
            freeSolo
            options={itemCategories}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return `${option.name} (${option.shortcut})`;
            }}
            value={manualItemDetails.name}
            onChange={(event, newValue) => {
              if (typeof newValue === 'string') {
                handleChange('name', newValue);
              } else if (newValue && newValue.name) {
                handleChange('name', newValue.name);
              }
            }}
            onInputChange={(event, newInputValue) => {
              handleChange('name', newInputValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Item Name *"
                fullWidth
                size="small"
                required
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <Typography variant="body2">{option.name}</Typography>
                  <Chip
                    label={option.shortcut}
                    size="small"
                    sx={{ ml: 1, fontSize: '0.7rem' }}
                  />
                </Box>
              </li>
            )}
          />

          {/* Metal Selection with Autocomplete */}
          <Autocomplete
            options={metalTypes}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return `${option.name} (${option.shortcut})`;
            }}
            value={metalTypes.find(m => m.name === manualItemDetails.metal) || null}
            onChange={(event, newValue) => {
              if (newValue) {
                handleChange('metal', newValue.name);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Metal *"
                fullWidth
                size="small"
                required
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <Typography variant="body2">{option.name}</Typography>
                  <Chip
                    label={option.shortcut}
                    size="small"
                    sx={{ ml: 1, fontSize: '0.7rem' }}
                  />
                </Box>
              </li>
            )}
          />

          {/* Silver Type (only if metal is Silver) */}
          {manualItemDetails.metal === 'Silver' && (
            <FormControl size="small" fullWidth>
              <InputLabel>Silver Type *</InputLabel>
              <Select
                value={manualItemDetails.silverType}
                onChange={(e) => handleChange('silverType', e.target.value)}
                label="Silver Type"
              >
                <MenuItem value="999silver">999 Silver</MenuItem>
                <MenuItem value="925silver">925 Silver</MenuItem>
                <MenuItem value="mixsilver">Mix Silver</MenuItem>
                <MenuItem value="silver">Standard Silver</MenuItem>
                <MenuItem value="oldsilver">Old Silver</MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Weight */}
          <TextField
            label="Weight (g) *"
            type="text"
            value={manualItemDetails.weight}
            onChange={(e) => handleChange('weight', e.target.value)}
            fullWidth
            size="small"
            required
            InputProps={{
              endAdornment: <InputAdornment position="end">g</InputAdornment>,
            }}
          />

          {/* Metal Rate */}
          <TextField
            label="Metal Rate (per gram) *"
            type="text"
            value={manualItemDetails.metalRate}
            onChange={(e) => handleChange('metalRate', e.target.value)}
            fullWidth
            size="small"
            required
            InputProps={{
              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
            }}
          />

          {/* Calculation Type Selection */}
          <FormControl component="fieldset">
            <FormLabel component="legend">Calculation Type *</FormLabel>
            <RadioGroup
              row
              value={manualItemDetails.calculationType}
              onChange={(e) => handleChange('calculationType', e.target.value)}
            >
              <FormControlLabel
                value="wastage"
                control={<Radio size="small" />}
                label="Wastage %"
              />
              <FormControlLabel
                value="labour"
                control={<Radio size="small" />}
                label="Labour (per gram)"
              />
            </RadioGroup>
          </FormControl>

          {/* Wastage or Labour Input */}
          {manualItemDetails.calculationType === 'wastage' ? (
            <TextField
              label="Wastage %"
              type="text"
              value={manualItemDetails.wastage}
              onChange={(e) => handleChange('wastage', e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
          ) : (
            <TextField
              label="Labour (per gram)"
              type="text"
              value={manualItemDetails.labour}
              onChange={(e) => handleChange('labour', e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
          )}

          {/* HSN Code */}
          <TextField
            label="HSN Code"
            value={manualItemDetails.hsnCode}
            onChange={(e) => handleChange('hsnCode', e.target.value)}
            fullWidth
            size="small"
          />

          {/* HUID */}
          <TextField
            label="HUID"
            value={manualItemDetails.huid}
            onChange={(e) => handleChange('huid', e.target.value)}
            fullWidth
            size="small"
          />

          {/* Total (readonly) */}
          <TextField
            label="Total Amount"
            value={manualItemDetails.total.toFixed(2)}
            InputProps={{ readOnly: true }}
            fullWidth
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!manualItemDetails.name || manualItemDetails.weight <= 0 || manualItemDetails.metalRate <= 0}
        >
          Add Item
        </Button>
      </DialogActions>
    </Dialog>
  );
};
// Simple Delete Confirmation Dialog
const SimpleDeleteDialog = ({ open, onClose, onConfirm, item }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [isProcessing, setIsProcessing] = useState(false);

  const handleConfirm = async () => {
    setIsProcessing(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      console.error("Error deleting item:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!isProcessing ? onClose : undefined}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backdropFilter: "blur(20px)",
          backgroundColor: alpha(
            isDark ? theme.palette.background.paper : "#ffffff",
            isDark ? 0.3 : 0.6,
          ),
          border: `1px solid ${alpha(
            isDark ? theme.palette.divider : "#e2e8f0",
            isDark ? 0.2 : 0.3,
          )}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          color: isDark ? "#e2e8f0" : "#1e293b",
          display: "flex",
          alignItems: "center",
          gap: 1,
          pb: 1,
        }}
      >
        <DeleteIcon color="error" />
        Delete Item
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ color: isDark ? "#cbd5e1" : "#475569" }}>
          Are you sure you want to delete "{item?.name || "this item"}"?
        </Typography>
        {/* <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b", display: "block", mt: 2 }}>
          This action cannot be undone.
        </Typography> */}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={isProcessing}
          sx={{
            borderRadius: 2,
            backdropFilter: "blur(10px)",
            backgroundColor: alpha(
              isDark ? theme.palette.background.paper : "#ffffff",
              isDark ? 0.3 : 0.8,
            ),
            color: isDark ? "#cbd5e1" : "#64748b",
            "&:hover": {
              backgroundColor: alpha(
                isDark ? theme.palette.background.paper : "#ffffff",
                isDark ? 0.5 : 0.9,
              ),
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={isProcessing}
          startIcon={
            isProcessing ? <CircularProgress size={20} /> : <DeleteIcon />
          }
          sx={{
            borderRadius: 2,
            background: isDark
              ? "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)"
              : "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
            "&:hover": {
              background: isDark
                ? "linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)"
                : "linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)",
            },
          }}
        >
          {isProcessing ? "Deleting..." : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Warning Delete Dialog for items with weight adjustments
const WeightAdjustmentDeleteDialog = ({ open, onClose, onConfirm, item }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [isProcessing, setIsProcessing] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  const handleConfirm = async () => {
    if (!isChecked) return;
    setIsProcessing(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      console.error("Error deleting item:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setIsChecked(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={!isProcessing ? handleClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backdropFilter: "blur(20px)",
          backgroundColor: alpha(
            isDark ? theme.palette.background.paper : "#ffffff",
            isDark ? 0.3 : 0.6,
          ),
          border: `1px solid ${alpha(
            isDark ? theme.palette.divider : "#e2e8f0",
            isDark ? 0.2 : 0.3,
          )}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          color: isDark ? "#e2e8f0" : "#1e293b",
          display: "flex",
          alignItems: "center",
          gap: 1,
          pb: 1,
          borderBottom: `1px solid ${alpha(
            isDark ? theme.palette.divider : "#e2e8f0",
            isDark ? 0.1 : 0.2,
          )}`,
        }}
      >
        <DeleteIcon color="error" />
        Delete Item - Weight Adjustment Warning
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          {/* Item Details */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: alpha(
                isDark ? "#1e293b" : "#f8fafc",
                isDark ? 0.4 : 0.5,
              ),
              border: `1px solid ${alpha(
                isDark ? theme.palette.divider : "#e2e8f0",
                isDark ? 0.2 : 0.3,
              )}`,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                mb: 1,
                color: isDark ? "#e2e8f0" : "#1e293b",
              }}
            >
              Item Details:
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  sx={{ color: isDark ? "#94a3b8" : "#64748b" }}
                >
                  Item Name:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: isDark ? "#e2e8f0" : "#1e293b",
                  }}
                >
                  {item?.name || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="caption"
                  sx={{ color: isDark ? "#94a3b8" : "#64748b" }}
                >
                  Weight:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: isDark ? "#e2e8f0" : "#1e293b",
                  }}
                >
                  {item?.weight || 0}g
                </Typography>
              </Grid>
              {item?.huid && (
                <Grid item xs={12}>
                  <Typography
                    variant="caption"
                    sx={{ color: isDark ? "#94a3b8" : "#64748b" }}
                  >
                    HUID:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: isDark ? "#e2e8f0" : "#1e293b",
                    }}
                  >
                    {item.huid}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Warning Message */}
          <Alert
            severity="warning"
            icon={<InfoIcon />}
            sx={{
              borderRadius: 2,
              backgroundColor: alpha(
                theme.palette.warning.main,
                isDark ? 0.2 : 0.1,
              ),
              border: `1px solid ${alpha(theme.palette.warning.main, isDark ? 0.3 : 0.2)}`,
              "& .MuiAlert-message": {
                width: "100%",
              },
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              ⚠️ Weight Adjustment Already Applied
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              This item has weight adjustments that have already been saved.
              Deleting this item will:
            </Typography>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              <li>
                <Typography variant="body2">
                  Remove it from the current transaction
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  NOT revert the weight adjustments that were already processed
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  NOT undo stock changes made due to weight adjustments
                </Typography>
              </li>
            </Box>
          </Alert>

          {/* Confirmation Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={isChecked}
                onChange={(e) => setIsChecked(e.target.checked)}
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{ color: isDark ? "#cbd5e1" : "#475569" }}
              >
                I understand that weight adjustments are already saved and
                cannot be reverted
              </Typography>
            }
          />

          <Typography
            variant="caption"
            sx={{ color: isDark ? "#94a3b8" : "#64748b", textAlign: "center" }}
          >
            This action cannot be undone.
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={isProcessing}
          sx={{
            borderRadius: 2,
            backdropFilter: "blur(10px)",
            backgroundColor: alpha(
              isDark ? theme.palette.background.paper : "#ffffff",
              isDark ? 0.3 : 0.8,
            ),
            color: isDark ? "#cbd5e1" : "#64748b",
            "&:hover": {
              backgroundColor: alpha(
                isDark ? theme.palette.background.paper : "#ffffff",
                isDark ? 0.5 : 0.9,
              ),
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={isProcessing || !isChecked}
          startIcon={
            isProcessing ? <CircularProgress size={20} /> : <DeleteIcon />
          }
          sx={{
            borderRadius: 2,
            background: isDark
              ? "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)"
              : "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
            "&:hover": {
              background: isDark
                ? "linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)"
                : "linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)",
            },
            "&.Mui-disabled": {
              background: isDark
                ? "linear-gradient(135deg, #7f8c8d 0%, #7f8c8d 100%)"
                : "linear-gradient(135deg, #95a5a6 0%, #95a5a6 100%)",
            },
          }}
        >
          {isProcessing ? "Deleting..." : "Delete Anyway"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Custom Alert Dialog Component with Enter key support
const CustomAlertDialog = ({ open, onClose, title, message, severity, onConfirm }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // const getSeverityColor = () => {
  //   switch (severity) {
  //     case 'error': return '#EF4444';
  //     case 'warning': return '#F59E0B';
  //     case 'info': return '#3B82F6';
  //     default: return '#10B981';
  //   }
  // };

  const getSeverityGradient = () => {
    switch (severity) {
      case 'error': return 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)';
      case 'warning': return 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)';
      case 'info': return 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)';
      default: return 'linear-gradient(135deg, #10B981 0%, #059669 100%)';
    }
  };

  // Handle keyboard events
  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      if (onConfirm) {
        onConfirm();
      } else {
        onClose();
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  }, [onConfirm, onClose]);

  // Add and remove keyboard event listener
  useEffect(() => {
    if (open) {
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [open, handleKeyDown]);

  return (
    <Dialog
      open={open}
      onClose={!onConfirm ? onClose : undefined}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backdropFilter: "blur(20px)",
          backgroundColor: alpha(
            isDark ? theme.palette.background.paper : "#ffffff",
            isDark ? 0.3 : 0.6,
          ),
          border: `1px solid ${alpha(
            isDark ? theme.palette.divider : "#e2e8f0",
            isDark ? 0.2 : 0.3,
          )}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          color: isDark ? "#e2e8f0" : "#1e293b",
          display: "flex",
          alignItems: "center",
          gap: 1,
          pb: 1,
          borderBottom: `1px solid ${alpha(
            isDark ? theme.palette.divider : "#e2e8f0",
            isDark ? 0.1 : 0.2,
          )}`,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: getSeverityGradient(),
            color: "white",
          }}
        >
          {severity === 'error' && <ErrorIcon fontSize="small" />}
          {severity === 'warning' && <WarningIcon fontSize="small" />}
          {severity === 'info' && <InfoIcon fontSize="small" />}
          {severity === 'success' && <CheckCircleIcon fontSize="small" />}
        </Box>
        {title}
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Typography
          sx={{
            color: isDark ? "#cbd5e1" : "#475569",
            whiteSpace: "pre-line" // This preserves line breaks in the message
          }}
        >
          {message}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: 2,
            backdropFilter: "blur(10px)",
            backgroundColor: alpha(
              isDark ? theme.palette.background.paper : "#ffffff",
              isDark ? 0.3 : 0.8,
            ),
            color: isDark ? "#cbd5e1" : "#64748b",
            "&:hover": {
              backgroundColor: alpha(
                isDark ? theme.palette.background.paper : "#ffffff",
                isDark ? 0.5 : 0.9,
              ),
            },
          }}
        >
          {onConfirm ? "Cancel" : "Close"}
        </Button>
        {onConfirm && (
          <Button
            onClick={onConfirm}
            variant="contained"
            autoFocus // Auto-focus on the OK button
            sx={{
              borderRadius: 2,
              background: getSeverityGradient(),
              "&:hover": {
                background: getSeverityGradient(),
                filter: "brightness(0.9)",
              },
            }}
          >
            OK
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// Consolidated Item Row Component - Fixed with all functions properly defined inside
const ConsolidatedItemRow = ({
  groupName,
  items,
  consolidatedData,
  onUpdateConsolidatedItem,
  onRemoveConsolidatedItem,
  showGST,
  gstMode,
  gstType,
  calculations,
  isDark,
  theme,
  metalTypes,
  itemCategories
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Local state for temporary weight values during editing
  const [tempWeights, setTempWeights] = useState({});

  // Calculate totals from individual items
  const totalGrossWeight = items.reduce((sum, item) => sum + (parseFloat(item.grossWeight) || parseFloat(item.weight) || 0), 0);
  const totalLessWeight = items.reduce((sum, item) => sum + (parseFloat(item.lessWeight) || 0), 0);
  const totalNetWeight = totalGrossWeight - totalLessWeight;

  // Get consolidated item data or initialize with defaults
  const consolidatedItem = useMemo(() => {
    return consolidatedData || {
      id: `consolidated_${Date.now()}_${groupName}`,
      name: groupName,
      items: items,
      totalWeight: totalNetWeight,
      metalRate: '',
      totalPercentage: '',
      calculationType: 'wastage',
      stoneCharges: 0,
      huidCharges: 0,
      totalFine: 0,
      totalAmount: 0,
      metal: items[0]?.metal || 'Gold',
      itemType: items[0]?.name || groupName,
      consolidated: true
    };
  }, [consolidatedData, items, groupName, totalNetWeight]);

  // Update total HUID charge and distribute to all items equally
  const updateTotalHuidCharge = useCallback((totalHuidValue) => {
    const totalHuidNum = parseFloat(totalHuidValue) || 0;
    const itemsCount = items.length;

    if (itemsCount === 0) return;

    // Calculate equal distribution per item
    const perItemHuid = totalHuidNum / itemsCount;

    const updatedItems = items.map(item => ({
      ...item,
      huidCost: perItemHuid
    }));

    onUpdateConsolidatedItem({
      ...consolidatedItem,
      items: updatedItems,
      huidCharges: totalHuidNum
    });
  }, [items, consolidatedItem, onUpdateConsolidatedItem]);

  // Update total stone charge and distribute to all items equally
  const updateTotalStoneCharge = useCallback((totalStoneValue) => {
    const totalStoneNum = parseFloat(totalStoneValue) || 0;
    const itemsCount = items.length;

    if (itemsCount === 0) return;

    // Calculate equal distribution per item
    const perItemStone = totalStoneNum / itemsCount;

    const updatedItems = items.map(item => ({
      ...item,
      stonePrice: perItemStone
    }));

    onUpdateConsolidatedItem({
      ...consolidatedItem,
      items: updatedItems,
      stoneCharges: totalStoneNum
    });
  }, [items, consolidatedItem, onUpdateConsolidatedItem]);

  // Update individual HUID cost and recalculate total
  const updateIndividualHuidCost = useCallback((itemId, value) => {
    const huidValue = parseFloat(value) || 0;

    const updatedItems = items.map(item => {
      if (item.id === itemId) {
        return { ...item, huidCost: huidValue };
      }
      return item;
    });

    // Recalculate total HUID charge
    const newTotalHuidCharge = updatedItems.reduce((sum, item) => sum + (parseFloat(item.huidCost) || 0), 0);

    onUpdateConsolidatedItem({
      ...consolidatedItem,
      items: updatedItems,
      huidCharges: newTotalHuidCharge
    });
  }, [items, consolidatedItem, onUpdateConsolidatedItem]);

  // Update individual stone cost and recalculate total
  const updateIndividualStoneCost = useCallback((itemId, value) => {
    const stoneValue = parseFloat(value) || 0;

    const updatedItems = items.map(item => {
      if (item.id === itemId) {
        return { ...item, stonePrice: stoneValue };
      }
      return item;
    });

    // Recalculate total stone charge
    const newTotalStoneCharge = updatedItems.reduce((sum, item) => sum + (parseFloat(item.stonePrice) || 0), 0);

    onUpdateConsolidatedItem({
      ...consolidatedItem,
      items: updatedItems,
      stoneCharges: newTotalStoneCharge
    });
  }, [items, consolidatedItem, onUpdateConsolidatedItem]);

  // Calculate fine and amount based on combined percentage
  const calculateFineAndAmount = useCallback((metalRate, totalPercentage, totalWeightVal) => {
    const totalPercentageNum = parseFloat(totalPercentage) || 0;
    const totalWeightNum = parseFloat(totalWeightVal) || 0;

    // Total Fine = (totalPercentage / 100) * total weight
    const totalFine = totalWeightNum * (totalPercentageNum / 100);

    let totalAmount = 0;
    const metalRateNum = parseFloat(metalRate) || 0;

    // Only calculate money amount if metal rate is provided
    if (metalRateNum > 0 && totalFine > 0) {
      totalAmount = metalRateNum * totalFine;
    }

    return {
      totalFine: parseFloat(totalFine.toFixed(4)),
      totalAmount: parseFloat(totalAmount.toFixed(2))
    };
  }, []);

  // Update consolidated item
  const updateConsolidatedItem = useCallback((field, value) => {
    const updated = { ...consolidatedItem, [field]: value };

    if (field === 'totalPercentage' || field === 'metalRate') {
      const { totalFine, totalAmount } = calculateFineAndAmount(
        field === 'metalRate' ? value : consolidatedItem.metalRate,
        field === 'totalPercentage' ? value : consolidatedItem.totalPercentage,
        totalNetWeight
      );
      updated.totalFine = totalFine;
      updated.totalAmount = totalAmount;

      // Also update the total fine for each individual item proportionally
      if (field === 'totalPercentage' && totalNetWeight > 0) {
        const totalPercentageNum = parseFloat(value) || 0;
        const updatedItems = items.map(item => {
          const itemWeight = parseFloat(item.weight) || 0;
          const itemTotalFine = itemWeight * (totalPercentageNum / 100);
          return {
            ...item,
            totalFine: itemTotalFine
          };
        });
        updated.items = updatedItems;
      }
    }

    onUpdateConsolidatedItem(updated);
  }, [consolidatedItem, totalNetWeight, calculateFineAndAmount, onUpdateConsolidatedItem, items]);

  // Update individual item fields
  const updateItemField = useCallback((itemId, field, value) => {
    const updatedItems = items.map(item => {
      if (item.id === itemId) {
        const updated = { ...item, [field]: value };

        // Recalculate net weight if gross or less weight changes
        if (field === 'grossWeight' || field === 'lessWeight') {
          const gross = field === 'grossWeight' ? parseFloat(value) || 0 : (parseFloat(item.grossWeight) || parseFloat(item.weight) || 0);
          const less = field === 'lessWeight' ? parseFloat(value) || 0 : (parseFloat(item.lessWeight) || 0);
          updated.netWeight = gross - less;
          updated.weight = gross - less;
        }

        // Recalculate total fine and amount if percentage, weight, or metal rate changes
        if (field === 'melting' || field === 'metalRate' || field === 'weight') {
          const metalRateNum = parseFloat(field === 'metalRate' ? value : updated.metalRate) || 0;
          const purityNum = parseFloat(field === 'melting' ? value : updated.melting) || 75;
          const weightNum = parseFloat(field === 'weight' ? value : updated.weight) || 0;
          const wastageNum = parseFloat(consolidatedItem.wastage) || 0;
          const totalPercentage = purityNum + wastageNum;
          const totalFine = weightNum * (totalPercentage / 100);

          if (metalRateNum > 0) {
            updated.amountFromMetal = metalRateNum * totalFine;
          }
          updated.totalFine = totalFine;
        }

        return updated;
      }
      return item;
    });

    const newTotalWeight = updatedItems.reduce((sum, i) => sum + (parseFloat(i.netWeight) || parseFloat(i.weight) || 0), 0);

    onUpdateConsolidatedItem({
      ...consolidatedItem,
      items: updatedItems,
      totalWeight: newTotalWeight
    });
  }, [items, consolidatedItem, onUpdateConsolidatedItem]);

  // Remove individual item from group
  const onRemoveItem = useCallback((itemId) => {
    const updatedItems = items.filter(item => item.id !== itemId);
    if (updatedItems.length === 0) {
      onRemoveConsolidatedItem();
    } else {
      const newTotalWeight = updatedItems.reduce((sum, i) => sum + (parseFloat(i.netWeight) || parseFloat(i.weight) || 0), 0);
      onUpdateConsolidatedItem({
        ...consolidatedItem,
        items: updatedItems,
        totalWeight: newTotalWeight
      });
    }
  }, [items, consolidatedItem, onRemoveConsolidatedItem, onUpdateConsolidatedItem]);

  // Update individual item weight fields - using onBlur instead of onChange
  const updateItemWeightFields = useCallback((itemId, field, value) => {
    const updatedItems = items.map(item => {
      if (item.id === itemId) {
        const gross = field === 'grossWeight'
          ? parseFloat(value) || 0
          : (parseFloat(item.grossWeight) || parseFloat(item.weight) || 0);
        const less = field === 'lessWeight'
          ? parseFloat(value) || 0
          : (parseFloat(item.lessWeight) || 0);
        const netWeight = gross - less;

        return {
          ...item,
          grossWeight: gross,
          lessWeight: less,
          netWeight: netWeight,
          weight: netWeight
        };
      }
      return item;
    });

    const newTotalWeight = updatedItems.reduce((sum, i) => sum + (parseFloat(i.netWeight) || parseFloat(i.weight) || 0), 0);

    onUpdateConsolidatedItem({
      ...consolidatedItem,
      items: updatedItems,
      totalWeight: newTotalWeight
    });
  }, [items, consolidatedItem, onUpdateConsolidatedItem]);

  // Handle weight change - store in temp state
  const handleWeightChange = useCallback((itemId, field, value) => {
    setTempWeights(prev => ({
      ...prev,
      [`${itemId}_${field}`]: value
    }));
  }, []);

  // Handle weight blur - save to actual state
  const handleWeightBlur = useCallback((itemId, field, value) => {
    setTempWeights(prev => {
      const newState = { ...prev };
      delete newState[`${itemId}_${field}`];
      return newState;
    });
    updateItemWeightFields(itemId, field, value);
  }, [updateItemWeightFields]);

  // Get display value for weight field
  const getDisplayWeight = useCallback((item, field) => {
    const tempKey = `${item.id}_${field}`;
    if (tempWeights[tempKey] !== undefined) {
      return tempWeights[tempKey];
    }
    if (field === 'grossWeight') {
      return (item.grossWeight || item.weight || 0).toFixed(3);
    }
    if (field === 'lessWeight') {
      return (item.lessWeight || 0).toFixed(3);
    }
    return '0';
  }, [tempWeights]);

  const formatNumber = (num) => {
    if (!num || num === "" || isNaN(num)) return "0";
    return parseFloat(num).toFixed(3);
  };

  // Get item shortcut
  const getItemShortcut = (itemName) => {
    if (!itemName) return '';
    const item = itemCategories.find(cat =>
      cat.name?.toLowerCase() === itemName?.toLowerCase()
    );
    return item ? item.shortcut : itemName.substring(0, 3).toUpperCase();
  };

  // Get metal shortcut
  // const getMetalShortcut = (metalName) => {
  //   if (!metalName) return '';
  //   const metal = metalTypes.find(m =>
  //     m.name?.toLowerCase() === metalName?.toLowerCase()
  //   );
  //   return metal ? metal.shortcut : metalName.substring(0, 2).toUpperCase();
  // };

  return (
    <Paper
      sx={{
        mb: 2,
        borderRadius: 2,
        backdropFilter: 'blur(10px)',
        backgroundColor: alpha(
          isDark ? '#1e293b' : '#ffffff',
          isDark ? 0.4 : 0.7
        ),
        border: `1px solid ${alpha(
          isDark ? '#f59e0b' : '#f59e0b',
          isDark ? 0.3 : 0.2
        )}`,
        overflow: 'hidden',
      }}
    >
      {/* Header - Click to expand/collapse */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          backgroundColor: alpha(
            isDark ? '#f59e0b' : '#f59e0b',
            isDark ? 0.1 : 0.05
          ),
          '&:hover': {
            backgroundColor: alpha(
              isDark ? '#f59e0b' : '#f59e0b',
              isDark ? 0.15 : 0.1
            ),
          },
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
            }}
          >
            <LocalOfferIcon fontSize="small" />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>
              {groupName} ({getItemShortcut(groupName)})
            </Typography>
            <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
              {items.length} items • Net Weight: {formatNumber(totalNetWeight)}g • Gross: {formatNumber(totalGrossWeight)}g • Less: {formatNumber(totalLessWeight)}g
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {consolidatedItem.totalAmount > 0 ? (
            <Typography sx={{ fontWeight: 700, color: '#f59e0b' }}>
              ₹{consolidatedItem.totalAmount.toFixed(2)}
            </Typography>
          ) : consolidatedItem.totalFine > 0 ? (
            <Typography sx={{ fontWeight: 700, color: '#f59e0b' }}>
              {consolidatedItem.totalFine.toFixed(4)}g Fine
            </Typography>
          ) : (
            <Typography sx={{ fontWeight: 700, color: '#f59e0b' }}>
              Pending
            </Typography>
          )}
          <IconButton size="small" onClick={(e) => {
            e.stopPropagation();
            onRemoveConsolidatedItem();
          }}>
            <DeleteIcon fontSize="small" sx={{ color: isDark ? '#94a3b8' : '#64748b' }} />
          </IconButton>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
      </Box>

      {/* Expanded Content */}
      <Collapse in={isExpanded}>
        <Box sx={{ p: 2 }}>
          {/* Individual Items List with All Editable Fields */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: isDark ? '#e2e8f0' : '#1e293b' }}>
            Included Items (Click fields to edit):
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 2, borderRadius: 1, overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1200 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha(isDark ? '#1e293b' : '#f8fafc', 0.5) }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>S.No.</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Serial No.</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Gross Wt (g)</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>CZ (g)</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Pearl (g)</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Belt (g)</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Other (g)</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Net Wt (g)</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Stone Price (₹)</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>HUID (₹)</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Metal</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Purity (%)</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, idx) => {
                  const grossWt = item.grossWeight || item.weight || 0;
                  const lessWt = item.lessWeight || 0;
                  const netWt = grossWt - lessWt;
                  const czWt = item.czWeight || 0;
                  const pearlWt = item.pearlWeight || 0;
                  const beltWt = item.beltWeight || 0;
                  const otherWt = item.otherWeight || 0;
                  const stonePrice = item.stonePrice || 0;
                  const huidCost = item.huidCost || (item.huid ? 0 : 0);

                  return (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Chip
                          label={item.serialNo || (idx + 1).toString()}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell sx={{ p: 0.5 }}>
                        <TextField
                          type="text"
                          value={item.serialNo || ''}
                          onChange={(e) => updateItemField(item.id, 'serialNo', e.target.value)}
                          size="small"
                          variant="standard"
                          sx={{ width: '80px' }}
                        />
                      </TableCell>
                      <TableCell sx={{ p: 0.5 }}>
                        <TextField
                          type="text"
                          value={getDisplayWeight(item, 'grossWeight')}
                          onChange={(e) => handleWeightChange(item.id, 'grossWeight', e.target.value)}
                          onBlur={(e) => handleWeightBlur(item.id, 'grossWeight', e.target.value)}
                          size="small"
                          variant="standard"
                          inputProps={{ step: "0.001" }}
                          sx={{ width: '80px' }}
                        />
                      </TableCell>
                      <TableCell sx={{ p: 0.5 }}>
                        <TextField
                          type="text"
                          value={czWt}
                          onChange={(e) => updateItemField(item.id, 'czWeight', e.target.value)}
                          size="small"
                          variant="standard"
                          inputProps={{ step: "0.001" }}
                          sx={{ width: '80px' }}
                        />
                      </TableCell>
                      <TableCell sx={{ p: 0.5 }}>
                        <TextField
                          type="text"
                          value={pearlWt}
                          onChange={(e) => updateItemField(item.id, 'pearlWeight', e.target.value)}
                          size="small"
                          variant="standard"
                          inputProps={{ step: "0.001" }}
                          sx={{ width: '80px' }}
                        />
                      </TableCell>
                      <TableCell sx={{ p: 0.5 }}>
                        <TextField
                          type="text"
                          value={beltWt}
                          onChange={(e) => updateItemField(item.id, 'beltWeight', e.target.value)}
                          size="small"
                          variant="standard"
                          inputProps={{ step: "0.001" }}
                          sx={{ width: '80px' }}
                        />
                      </TableCell>
                      <TableCell sx={{ p: 0.5 }}>
                        <TextField
                          type="text"
                          value={otherWt}
                          onChange={(e) => updateItemField(item.id, 'otherWeight', e.target.value)}
                          size="small"
                          variant="standard"
                          inputProps={{ step: "0.001" }}
                          sx={{ width: '80px' }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#f59e0b', fontSize: '0.8rem' }}>
                        {formatNumber(netWt)}g
                      </TableCell>
                      <TableCell sx={{ p: 0.5 }}>
                        <TextField
                          type="text"
                          value={stonePrice}
                          onChange={(e) => updateIndividualStoneCost(item.id, e.target.value)}
                          size="small"
                          variant="standard"
                          inputProps={{ step: "1" }}
                          sx={{ width: '80px' }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ p: 0.5 }}>
                        <TextField
                          type="text"
                          value={huidCost}
                          onChange={(e) => updateIndividualHuidCost(item.id, e.target.value)}
                          size="small"
                          variant="standard"
                          inputProps={{ step: "1" }}
                          sx={{ width: '80px' }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Autocomplete
                            options={metalTypes}
                            getOptionLabel={(option) => {
                              if (typeof option === 'string') return option;
                              return option.name;
                            }}
                            value={item.metal || ''}
                            onChange={(event, newValue) => {
                              if (newValue) {
                                updateItemField(item.id, 'metal', typeof newValue === 'string' ? newValue : newValue.name);
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                variant="standard"
                                sx={{ width: '100px' }}
                              />
                            )}
                            sx={{ width: '100px' }}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ p: 0.5 }}>
                        <TextField
                          type="text"
                          value={item.melting || 75}
                          onChange={(e) => updateItemField(item.id, 'melting', e.target.value)}
                          size="small"
                          variant="standard"
                          inputProps={{ step: "0.1" }}
                          sx={{ width: '70px' }}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => onRemoveItem(item.id)}
                          size="small"
                          sx={{
                            borderRadius: 1,
                            backgroundColor: alpha(theme.palette.error.main, 0.1),
                            color: theme.palette.error.main,
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {/* Totals Row */}
                <TableRow sx={{
                  backgroundColor: alpha(isDark ? '#f59e0b' : '#f59e0b', 0.1),
                  borderTop: `2px solid ${alpha(isDark ? '#f59e0b' : '#f59e0b', 0.3)}`
                }}>
                  <TableCell colSpan={2}>
                    <Typography sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>
                      TOTALS
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>
                    {formatNumber(totalGrossWeight)}g
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>
                    {formatNumber(items.reduce((sum, i) => sum + (parseFloat(i.czWeight) || 0), 0))}g
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>
                    {formatNumber(items.reduce((sum, i) => sum + (parseFloat(i.pearlWeight) || 0), 0))}g
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>
                    {formatNumber(items.reduce((sum, i) => sum + (parseFloat(i.beltWeight) || 0), 0))}g
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>
                    {formatNumber(items.reduce((sum, i) => sum + (parseFloat(i.otherWeight) || 0), 0))}g
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#f59e0b', fontSize: '1rem' }}>
                    {formatNumber(totalNetWeight)}g
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#f59e0b' }}>
                    ₹{formatNumber(items.reduce((sum, i) => sum + (parseFloat(i.stonePrice) || 0), 0), 2)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#f59e0b' }}>
                    ₹{formatNumber(items.reduce((sum, i) => sum + (parseFloat(i.huidCost) || (i.huid ? 0 : 0)), 0), 2)}
                  </TableCell>
                  <TableCell colSpan={3}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Consolidated Item Details */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: isDark ? '#e2e8f0' : '#1e293b' }}>
            Consolidated Details:
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Metal Rate (₹/gm)"
                type="text"
                value={consolidatedItem.metalRate}
                onChange={(e) => updateConsolidatedItem('metalRate', e.target.value)}
                fullWidth
                size="small"
                helperText={!consolidatedItem.metalRate ? "Leave empty to track fine weight only" : "Amount will be calculated from fine"}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Total Percentage (Purity + Wastage) %"
                type="text"
                value={consolidatedItem.totalPercentage}
                onChange={(e) => updateConsolidatedItem('totalPercentage', e.target.value)}
                fullWidth
                size="small"
                helperText="Enter combined value (e.g., 93% = 75% purity + 18% wastage)"
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  inputProps: { step: "0.1" }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Stone Charges (Total ₹)"
                type="text"
                value={consolidatedItem.stoneCharges}
                onChange={(e) => updateTotalStoneCharge(e.target.value)}
                fullWidth
                size="small"
                helperText="Total stone cost - will be distributed equally"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="HUID Cost (Total ₹)"
                type="text"
                value={consolidatedItem.huidCharges}
                onChange={(e) => updateTotalHuidCharge(e.target.value)}
                fullWidth
                size="small"
                helperText="Total HUID cost - will be distributed equally among items"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
            </Grid>
          </Grid>

          {/* Results Section */}
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 1,
              background: alpha(
                isDark ? '#1e293b' : '#f8fafc',
                isDark ? 0.4 : 0.5
              ),
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                  Net Weight:
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#f59e0b' }}>
                  {formatNumber(totalNetWeight)} gm
                </Typography>
              </Grid>

              <Grid item xs={6} md={3}>
                <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                  Combined Percentage:
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#f59e0b' }}>
                  {consolidatedItem.totalPercentage || 0}%
                </Typography>
              </Grid>

              <Grid item xs={6} md={3}>
                <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                  Total Fine Weight:
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#f59e0b' }}>
                  {consolidatedItem.totalFine.toFixed(4)} gm
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                  ({totalNetWeight}gm × {consolidatedItem.totalPercentage || 0}%)
                </Typography>
              </Grid>

              {consolidatedItem.metalRate && parseFloat(consolidatedItem.metalRate) > 0 ? (
                <>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                      Total Amount:
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#f59e0b' }}>
                      ₹{consolidatedItem.totalAmount.toFixed(2)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                      ({consolidatedItem.totalFine.toFixed(4)}gm × ₹{consolidatedItem.metalRate})
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                      Stone + HUID:
                    </Typography>
                    <Typography sx={{ fontWeight: 500 }}>
                      ₹{(parseFloat(consolidatedItem.stoneCharges || 0) + parseFloat(consolidatedItem.huidCharges || 0)).toFixed(2)}
                    </Typography>
                  </Grid>
                </>
              ) : (
                <Grid item xs={12} md={6}>
                  <Alert severity="info" sx={{ py: 0 }}>
                    <Typography variant="caption">
                      Fine Mode: {consolidatedItem.totalFine.toFixed(4)}gm fine will be recorded.
                      Enter Metal Rate to convert to amount.
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

const CustomerTransaction = ({ onBack }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const hasShownAdvanceDialogRef = useRef(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Add this with your other state declarations (around line where you have other useState hooks)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // 'success', 'error', 'warning', 'info'
  });

  const [customAlert, setCustomAlert] = useState({
    open: false,
    title: "",
    message: "",
    severity: "success", // 'success', 'error', 'warning', 'info'
    onConfirm: null,
  });


  // Update your delete confirmation state to track if weight adjustments exist
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    itemToDelete: null,
    hasWeightAdjustment: false,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);
  const {
    customers,
    addCustomer,
    customerTransactions,
    stock,
    addCustomerTransactionAndUpdateStock,
    companyDetails,
    silverStock,
    applyAdvanceToBill,
    fetchCustomerAdvanceBalance,
  } = useContext(DataContext);

  // Use master data
  const [itemCategories, setItemCategories] = useState([]);
  const [metalTypes, setMetalTypes] = useState([]);

  // Load master data from company details
  useEffect(() => {
    if (companyDetails && companyDetails.length > 0) {
      const company = companyDetails[0];

      // Load item categories
      if (company.items && Array.isArray(company.items)) {
        setItemCategories(company.items);
      }

      // Load metal types
      if (company.metals && Array.isArray(company.metals)) {
        setMetalTypes(company.metals);
      }
    }
  }, [companyDetails]);

  // Helper function to get item shortcut
  // const getItemShortcut = useCallback((itemName) => {
  //   if (!itemName) return '';
  //   const item = itemCategories.find(cat =>
  //     cat.name.toLowerCase() === itemName.toLowerCase()
  //   );
  //   return item ? item.shortcut : itemName.substring(0, 3).toUpperCase();
  // }, [itemCategories]);

  // // Helper function to get metal shortcut
  // const getMetalShortcut = useCallback((metalName) => {
  //   if (!metalName) return '';
  //   const metal = metalTypes.find(m =>
  //     m.name.toLowerCase() === metalName.toLowerCase()
  //   );
  //   return metal ? metal.shortcut : metalName.substring(0, 2).toUpperCase();
  // }, [metalTypes]);

  // State variables
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [showNewCustomerFields, setShowNewCustomerFields] = useState(false);
  const [transactionType, setTransactionType] = useState("detailed");
  const [items, setItems] = useState([]);
  const [showGST, setShowGST] = useState(false);
  const [gstMode, setGstMode] = useState("add"); // "add" or "adjust"
  const [gstType, setGstType] = useState('cgst_sgst'); // "cgst_sgst" or "igst"
  const [enableAdvancePayment, setEnableAdvancePayment] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentModes, setPaymentModes] = useState({
    cash: false,
    online: false,
    cheque: false,
    oldGold: false,
    oldSilver: false,
    other: false,
  });
  const [customOther, setCustomOther] = useState("");
  const [advancePayments, setAdvancePayments] = useState({});
  const [otherOptions, setOtherOptions] = useState([]);
  const [manualItemName, setManualItemName] = useState("");
  const [manualItemMetal, setManualItemMetal] = useState("");
  const [manualSerial, setManualSerial] = useState("");
  const [scannerOpen, setScannerOpen] = useState(false);
  const [manualItemDialogOpen, setManualItemDialogOpen] = useState(false);
  const [isTransactionSaved, setIsTransactionSaved] = useState(false);
  const [manualItemDetails, setManualItemDetails] = useState({
    name: '',
    metal: 'Silver',
    silverType: '999silver',
    calculationType: 'labour',
    metalRate: '',
    labour: '',
    wastage: '',
    weight: '',
    hsnCode: '',
    huid: '',
    total: 0,
  });
  const [gstBillNo, setGstBillNo] = useState(null);
  const [nonGstBillNo, setNonGstBillNo] = useState(null);
  const [currentBillNo, setCurrentBillNo] = useState(null);
  const [isLoadingBillNo, setIsLoadingBillNo] = useState(false);
  // const [usedBillNo, setUsedBillNo] = useState(null);
  // const [weightAdjustmentDialog, setWeightAdjustmentDialog] = useState({
  //   open: false,
  //   item: null,
  //   originalWeight: 0,
  //   newWeight: 0,
  //   weightDifference: 0,
  //   isWeightIncreased: false,
  // });
  // const [pendingWeightChange, setPendingWeightChange] = useState(null);
  const [weightAdjustments, setWeightAdjustments] = useState({}); // Track weight adjustments per item
  const [currentAdjustmentItem, setCurrentAdjustmentItem] = useState(null);
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [editingItemWeight, setEditingItemWeight] = useState(null);
  const [weightEditDialogOpen, setWeightEditDialogOpen] = useState(false);
  // const [tempWeight, setTempWeight] = useState("");
  const [pendingWeightAdjustments, setPendingWeightAdjustments] = useState(null);
  // Advance payment related states
  const [advanceSelectorOpen, setAdvanceSelectorOpen] = useState(false);
  const [customerAdvanceBalance, setCustomerAdvanceBalance] = useState(null);
  const [selectedAdvanceUsage, setSelectedAdvanceUsage] = useState(null);
  const [itemConsolidationMode, setItemConsolidationMode] = useState('consolidate'); // 'separate' or 'consolidate'
  const [consolidatedItemGroups, setConsolidatedItemGroups] = useState({}); // Store consolidated items by name
  const [finePayment, setFinePayment] = useState({
    enabled: false,
    weight: '',
    purity: '',
    actualFine: 0
  });

  const [calculatedFinePayment, setCalculatedFinePayment] = useState(0);
  const [billDetailsOpen, setBillDetailsOpen] = useState(false);
  // const [isLoadingAdvance, setIsLoadingAdvance] = useState(false);

  // Function to open weight edit dialog
  const openWeightEditDialog = useCallback((item) => {
    // Get the original stock weight from the item's itemObject
    const originalStockWeight = item.itemObject?.weight || item.weight;

    setEditingItemWeight({
      id: item.id,
      name: item.name,
      originalStockWeight: originalStockWeight,
      currentWeight: item.weight,
    });
    setWeightEditDialogOpen(true);
  }, []);

  useEffect(() => {
    if (finePayment.enabled && finePayment.weight && finePayment.purity) {
      const weight = parseFloat(finePayment.weight) || 0;
      const purity = parseFloat(finePayment.purity) || 0;
      const actualFine = weight * (purity / 100);
      setCalculatedFinePayment(actualFine);
      setFinePayment(prev => ({ ...prev, actualFine }));
    } else {
      setCalculatedFinePayment(0);
    }
  }, [finePayment.weight, finePayment.purity, finePayment.enabled]);

  // Function to handle weight change confirmation
  const handleWeightChangeConfirm = useCallback(
    (newWeight) => {
      if (!editingItemWeight) return;

      const originalStockWeight = editingItemWeight.originalStockWeight;
      // const weightDifference = newWeight - originalStockWeight;

      // Update the weight in the UI
      setItems((prev) =>
        prev.map((item) => {
          if (item.id === editingItemWeight.id) {
            return { ...item, weight: newWeight };
          }
          return item;
        }),
      );

      // Find the full item object for adjustment dialog
      const fullItem = items.find((item) => item.id === editingItemWeight.id);

      // Track that this item needs adjustment
      setPendingWeightAdjustments((prev) => ({
        ...prev,
        [editingItemWeight.id]: {
          originalWeight: originalStockWeight,
          newWeight: newWeight,
          item: fullItem,
          needsAdjustment: true,
        },
      }));

      // Show the adjustment dialog
      setCurrentAdjustmentItem({
        ...fullItem,
        originalWeight: originalStockWeight,
        newWeight: newWeight,
      });
      setAdjustmentDialogOpen(true);
      setEditingItemWeight(null);
    },
    [editingItemWeight, items],
  );

  // const updateItemField = useCallback((itemId, field, value) => {
  //   const updatedItems = items.map(item => {
  //     if (item.id === itemId) {
  //       const updated = { ...item, [field]: value };

  //       // Recalculate net weight if gross or less weight changes
  //       if (field === 'grossWeight' || field === 'lessWeight') {
  //         const gross = field === 'grossWeight' ? parseFloat(value) || 0 : (parseFloat(item.grossWeight) || parseFloat(item.weight) || 0);
  //         const less = field === 'lessWeight' ? parseFloat(value) || 0 : (parseFloat(item.lessWeight) || 0);
  //         updated.netWeight = gross - less;
  //         updated.weight = gross - less;
  //       }

  //       // Recalculate total fine and amount if percentage, weight, or metal rate changes
  //       if (field === 'melting' || field === 'metalRate' || field === 'weight') {
  //         const metalRateNum = parseFloat(field === 'metalRate' ? value : updated.metalRate) || 0;
  //         const purityNum = parseFloat(field === 'melting' ? value : updated.melting) || 75;
  //         const weightNum = parseFloat(field === 'weight' ? value : updated.weight) || 0;
  //         const wastageNum = parseFloat(consolidatedItem.wastage) || 0;
  //         const totalPercentage = purityNum + wastageNum;
  //         const totalFine = weightNum * (totalPercentage / 100);

  //         if (metalRateNum > 0) {
  //           updated.amountFromMetal = metalRateNum * totalFine;
  //         }
  //         updated.totalFine = totalFine;
  //       }

  //       return updated;
  //     }
  //     return item;
  //   });

  //   // Calculate new totals after updating items
  //   const newTotalWeight = updatedItems.reduce((sum, i) => sum + (parseFloat(i.netWeight) || parseFloat(i.weight) || 0), 0);

  //   // Recalculate total HUID charge from all items
  //   const newTotalHuidCharge = updatedItems.reduce((sum, i) => sum + (parseFloat(i.huidCost) || 0), 0);

  //   // Recalculate total stone charge from all items
  //   const newTotalStoneCharge = updatedItems.reduce((sum, i) => sum + (parseFloat(i.stonePrice) || 0), 0);

  //   onUpdateConsolidatedItem({
  //     ...consolidatedItem,
  //     items: updatedItems,
  //     totalWeight: newTotalWeight,
  //     huidCharges: newTotalHuidCharge,  // Update total HUID charge
  //     stoneCharges: newTotalStoneCharge  // Update total stone charge
  //   });
  // }, [items, consolidatedItem, onUpdateConsolidatedItem]);

  // // Add remove item function
  // const onRemoveItem = (itemId) => {
  //   const updatedItems = items.filter(item => item.id !== itemId);
  //   if (updatedItems.length === 0) {
  //     onRemoveConsolidatedItem();
  //   } else {
  //     onUpdateConsolidatedItem({
  //       ...consolidatedItem,
  //       items: updatedItems,
  //       totalWeight: updatedItems.reduce((sum, i) => sum + (parseFloat(i.netWeight) || parseFloat(i.weight) || 0), 0)
  //     });
  //   }
  // };

  // Add this function to handle manual weight adjustment
  const handleManualWeightAdjustment = useCallback((item) => {
    setCurrentAdjustmentItem(item);
    setAdjustmentDialogOpen(true);
  }, []);

  // Add this function to handle adjustment completion
  const handleAdjustmentComplete = useCallback(
    (result) => {
      if (!currentAdjustmentItem) return;

      // Update the item with adjustment info
      setItems((prev) =>
        prev.map((item) => {
          if (item.id === currentAdjustmentItem.id) {
            return {
              ...item,
              weightAdjusted: true,
              weightAdjustmentData: {
                ...item.weightAdjustmentData,
                ...result,
                adjustedAt: new Date().toISOString(),
              },
              adjustmentApplied: true,
            };
          }
          return item;
        }),
      );

      // Store adjustment in state for later processing
      setWeightAdjustments((prev) => ({
        ...prev,
        [currentAdjustmentItem.id]: {
          item: currentAdjustmentItem,
          adjustment: result,
        },
      }));

      setCurrentAdjustmentItem(null);
      alert("Weight adjustment applied successfully!");
    },
    [currentAdjustmentItem],
  );

  // Add this function to handle skip adjustment
  const handleSkipAdjustment = useCallback(() => {
    if (!currentAdjustmentItem) return;

    setItems((prev) =>
      prev.map((item) => {
        if (item.id === currentAdjustmentItem.id) {
          return {
            ...item,
            weightAdjusted: false,
            adjustmentSkipped: true,
          };
        }
        return item;
      }),
    );

    setCurrentAdjustmentItem(null);
    alert(
      "Weight adjustment skipped. Item will be saved with new weight without stock adjustment.",
    );
  }, [currentAdjustmentItem]);

  // Add this function to handle weight changes
  // const handleWeightChangeWithAdjustment = useCallback(
  //   async (itemId, newWeight) => {
  //     const item = items.find((i) => i.id === itemId);
  //     if (!item) return;

  //     const originalWeight = parseFloat(item.weight) || 0;
  //     const weightDifference = newWeight - originalWeight;
  //     const isWeightIncreased = weightDifference > 0;

  //     // If no weight change, just update
  //     if (Math.abs(weightDifference) < 0.001) {
  //       updateItemField(itemId, "weight", newWeight);
  //       return;
  //     }

  //     // Store the pending change and show dialog
  //     setPendingWeightChange({
  //       itemId,
  //       newWeight,
  //       originalWeight,
  //       weightDifference,
  //       isWeightIncreased,
  //     });
  //     setWeightAdjustmentDialog({
  //       open: true,
  //       item,
  //       originalWeight,
  //       newWeight,
  //       weightDifference: Math.abs(weightDifference),
  //       isWeightIncreased,
  //     });
  //   },
  //   [items, updateItemField],
  // );

  // Add this function to handle adjustment completion
  // const handleWeightAdjustmentComplete = useCallback(
  //   async (result) => {
  //     if (!pendingWeightChange) return;

  //     const { itemId, newWeight, originalWeight } = pendingWeightChange;

  //     // Update the item weight in the UI
  //     updateItemField(itemId, "weight", newWeight);

  //     // Store adjustment info in the item for later processing when saving
  //     setItems((prev) =>
  //       prev.map((item) => {
  //         if (item.id === itemId) {
  //           return {
  //             ...item,
  //             weightAdjusted: true,
  //             weightAdjustmentData: {
  //               originalWeight,
  //               newWeight,
  //               weightDifference: Math.abs(originalWeight - newWeight),
  //               isWeightIncreased: originalWeight < newWeight,
  //               adjustmentAction: result.action,
  //               adjustmentDetails: result,
  //             },
  //           };
  //         }
  //         return item;
  //       }),
  //     );

  //     // Clear pending change
  //     setPendingWeightChange(null);
  //   },
  //   [pendingWeightChange, updateItemField],
  // );

  // Formik initialization
  const formik = useFormik({
    initialValues: {
      customerName: "",
      handledBy: "",
      gstNumber: "",
      panNumber: "",
      transactionDate: new Date(),
      newCustomerContact: "",
      newCustomerAddress: "",
      newCustomerDob: null,
      newCustomerAnniversary: null,
      cashPayment: "",
      onlinePayment: "",
      chequePayment: "",
      otherPayment: "",
      paymentDone: 0,
      discount: "",
      totalAmount: 0,
      isNewCustomer: false
    },
    validationSchema,
    onSubmit: async (values) => {
      await handleSubmit(values);
    },
  });


  const handleUseAdvance = useCallback((usageData) => {
    const { usageAmounts, totalAmount } = usageData;

    console.log("Usage Amounts from selector:", usageAmounts);
    console.log("Total Amount:", totalAmount);

    // Enable advance payment
    setEnableAdvancePayment(true);

    // Create a new paymentModes object to enable only the modes that have amounts
    const newPaymentModes = { ...paymentModes };

    // Track which modes have amounts to enable them
    const modesWithAmounts = Object.keys(usageAmounts);

    // Enable all modes that have amounts
    modesWithAmounts.forEach(mode => {
      if (usageAmounts[mode] > 0) {
        newPaymentModes[mode] = true;
      }
    });

    // Update payment modes state
    setPaymentModes(newPaymentModes);

    // // Update cash payment
    // if (usageAmounts.cash && parseFloat(usageAmounts.cash) > 0) {
    //   const currentCash = parseFloat(formik.values.cashPayment) || 0;
    //   formik.setFieldValue('cashPayment', currentCash + parseFloat(usageAmounts.cash));
    // }

    // // Update online payment
    // if (usageAmounts.online && parseFloat(usageAmounts.online) > 0) {
    //   const currentOnline = parseFloat(formik.values.onlinePayment) || 0;
    //   formik.setFieldValue('onlinePayment', currentOnline + parseFloat(usageAmounts.online));
    // }

    // // Update cheque payment
    // if (usageAmounts.cheque && parseFloat(usageAmounts.cheque) > 0) {
    //   const currentCheque = parseFloat(formik.values.chequePayment) || 0;
    //   formik.setFieldValue('chequePayment', currentCheque + parseFloat(usageAmounts.cheque));
    // }

    // // For old gold and old silver, add to advance payments section
    // if ((usageAmounts.oldGold && parseFloat(usageAmounts.oldGold) > 0) ||
    //   (usageAmounts.oldSilver && parseFloat(usageAmounts.oldSilver) > 0)) {

    //   if (usageAmounts.oldGold && parseFloat(usageAmounts.oldGold) > 0) {
    //     setAdvancePayments(prev => ({
    //       ...prev,
    //       oldGold: (parseFloat(prev.oldGold) || 0) + parseFloat(usageAmounts.oldGold)
    //     }));
    //   }

    //   if (usageAmounts.oldSilver && parseFloat(usageAmounts.oldSilver) > 0) {
    //     setAdvancePayments(prev => ({
    //       ...prev,
    //       oldSilver: (parseFloat(prev.oldSilver) || 0) + parseFloat(usageAmounts.oldSilver)
    //     }));
    //   }
    // }

    // Also update advancePayments for cash/online/cheque if they were selected
    // (though these are typically handled by the payment fields, we still add them to advancePayments for tracking)
    if (usageAmounts.cash && parseFloat(usageAmounts.cash) > 0) {
      setAdvancePayments(prev => ({
        ...prev,
        cash: (parseFloat(prev.cash) || 0) + parseFloat(usageAmounts.cash)
      }));
    }

    if (usageAmounts.online && parseFloat(usageAmounts.online) > 0) {
      setAdvancePayments(prev => ({
        ...prev,
        online: (parseFloat(prev.online) || 0) + parseFloat(usageAmounts.online)
      }));
    }

    if (usageAmounts.cheque && parseFloat(usageAmounts.cheque) > 0) {
      setAdvancePayments(prev => ({
        ...prev,
        cheque: (parseFloat(prev.cheque) || 0) + parseFloat(usageAmounts.cheque)
      }));
    }

    if (usageAmounts.oldGold && parseFloat(usageAmounts.oldGold) > 0) {
      setAdvancePayments(prev => ({
        ...prev,
        oldGold: (parseFloat(prev.oldGold) || 0) + parseFloat(usageAmounts.oldGold)
      }));
    }

    if (usageAmounts.oldSilver && parseFloat(usageAmounts.oldSilver) > 0) {
      setAdvancePayments(prev => ({
        ...prev,
        oldSilver: (parseFloat(prev.oldSilver) || 0) + parseFloat(usageAmounts.oldSilver)
      }));
    }
    // Store the usage data to be sent to backend when saving
    setSelectedAdvanceUsage(usageData);

    // Show success message
    setSnackbar({
      open: true,
      message: `₹${totalAmount.toFixed(2)} advance payment applied successfully!`,
      severity: 'success'
    });
  }, [paymentModes]);

  // Check for advance balance when customer is selected
  // Check for advance balance when customer is selected
  useEffect(() => {
    const checkAdvanceBalance = async () => {
      if (selectedCustomer?.id && !isTransactionSaved) {
        // Reset the ref when a new customer is selected (different from previous)
        // This ensures dialog shows for new customers but not repeatedly for same customer
        if (hasShownAdvanceDialogRef.current !== selectedCustomer.id) {
          // setIsLoadingAdvance(true);
          try {
            // Use the context function directly
            const advanceData = await fetchCustomerAdvanceBalance(selectedCustomer.id);
            setCustomerAdvanceBalance(advanceData);

            // Show advance selector if customer has advance balance
            if (advanceData && advanceData.total > 0) {
              setAdvanceSelectorOpen(true);
              // Mark that we've shown the dialog for this customer
              hasShownAdvanceDialogRef.current = selectedCustomer.id;
            }
          } catch (error) {
            console.error("Error checking advance balance:", error);
          } finally {
            // setIsLoadingAdvance(false);
          }
        }
      } else if (!selectedCustomer?.id) {
        // Reset the ref when no customer is selected
        hasShownAdvanceDialogRef.current = false;
      }
    };

    checkAdvanceBalance();
  }, [selectedCustomer?.id, isTransactionSaved, fetchCustomerAdvanceBalance]);

  // Helper functions
  const getLastCustomerTransaction = useCallback(() => {
    if (!customerTransactions || customerTransactions.length === 0) {
      return { gst: null, nonGst: null };
    }

    let lastGst = null;
    let lastNonGst = null;

    for (let i = customerTransactions.length - 1; i >= 0; i--) {
      const transaction = customerTransactions[i];
      if (transaction.transactionDetails) {
        const gstFlag = transaction.transactionDetails.gstApplicable;
        if (gstFlag === true && !lastGst) lastGst = transaction;
        if (gstFlag === false && !lastNonGst) lastNonGst = transaction;
      }
      if (lastGst && lastNonGst) break;
    }

    return { gst: lastGst, nonGst: lastNonGst };
  }, [customerTransactions]);

  // Add this function to fetch last bill numbers
  const fetchLastBillNumbers = useCallback(async () => {
    setIsLoadingBillNo(true);
    try {
      // Get last transactions from context
      const lastTransaction = getLastCustomerTransaction();
      console.log("Last GST Transaction:", lastTransaction.gst);
      let lastGstNo = 0;
      let lastNonGstNo = 0;

      if (lastTransaction?.gst?.transactionDetails?.billNo) {
        lastGstNo = parseInt(lastTransaction.gst.transactionDetails.billNo) || 0;
      }

      if (lastTransaction?.nonGst?.transactionDetails?.billNo) {
        lastNonGstNo = parseInt(lastTransaction.nonGst.transactionDetails.billNo) || 0;
      }

      setGstBillNo(lastGstNo);
      setNonGstBillNo(lastNonGstNo);

      // Set current bill number based on GST status
      if (showGST) {
        setCurrentBillNo(lastGstNo + 1);
      } else {
        setCurrentBillNo(lastNonGstNo + 1);
      }
    } catch (error) {
      console.error("Error fetching bill numbers:", error);
      // Set default values
      setGstBillNo(0);
      setNonGstBillNo(0);
      setCurrentBillNo(1);
    } finally {
      setIsLoadingBillNo(false);
    }
  }, [getLastCustomerTransaction, showGST]);

  // Add this useEffect to load bill numbers when component mounts or GST status changes
  useEffect(() => {
    fetchLastBillNumbers();
  }, [fetchLastBillNumbers, showGST]);

  const calculateItemTotal = useCallback((weight, metalRate, wastage, labour, calculationType, adjustForGST = false, targetAmount = null) => {
    const weightNum = parseFloat(weight) || 0;
    const metalRateNum = parseFloat(metalRate) || 0;
    const wastageNum = parseFloat(wastage) || 0;
    const labourNum = parseFloat(labour) || 0;

    let total = 0;

    if (calculationType === 'labour') {
      total = (metalRateNum + labourNum) * weightNum;
    } else {
      // wastage calculation
      total = metalRateNum * weightNum * ((wastageNum + 100) / 100);
    }

    // If we need to adjust for GST to keep amount same
    if (adjustForGST && targetAmount && metalRateNum > 0 && weightNum > 0) {
      const denominator = 1.03 * metalRateNum * weightNum;
      if (denominator > 0) {
        const adjustedWastage = ((targetAmount / denominator) - 1) * 100;
        const newTotal = metalRateNum * weightNum * ((adjustedWastage + 100) / 100);
        return {
          total: newTotal,
          adjustedWastage: parseFloat(adjustedWastage.toFixed(2)),
          adjustedLabour: parseFloat((adjustedWastage * metalRateNum / 100).toFixed(2))
        };
      }
    }
    console.log("Calculated total:", total);
    return total;
  }, []);

  const calculations = useMemo(() => {
    let subtotal = 0;
    let totalStoneCharge = 0;
    let totalHuidCharge = 0;
    let totalFineWeight = 0;
    let totalAmountFromMetal = 0;

    if (transactionType === "detailed") {
      if (itemConsolidationMode === 'consolidate') {
        Object.values(consolidatedItemGroups).forEach(group => {
          const totalPercentage = parseFloat(group.totalPercentage) || 0;
          const totalWeightNum = parseFloat(group.totalWeight) || 0;
          const metalRateNum = parseFloat(group.metalRate) || 0;
          const stoneCharges = parseFloat(group.stoneCharges) || 0;
          const huidCharges = parseFloat(group.huidCharges) || 0;

          const totalFine = totalWeightNum * (totalPercentage / 100);

          if (metalRateNum > 0) {
            const amountFromFine = metalRateNum * totalFine;
            subtotal += amountFromFine + stoneCharges + huidCharges;
            totalAmountFromMetal += amountFromFine;
          } else {
            totalFineWeight += totalFine;
            subtotal += stoneCharges + huidCharges; // Even if metal rate is 0, we still add stone and HUID charges to subtotal
          }

          totalStoneCharge += stoneCharges;
          totalHuidCharge += huidCharges;
        });
      } else {
        items.forEach(item => {
          const weight = parseFloat(item.weight) || 0;
          const metalRateNum = parseFloat(item.metalRate) || 0;
          const wastageNum = parseFloat(item.wastage) || 0;
          const purityNum = parseFloat(item.melting) || 75;
          const stoneCharges = parseFloat(item.stonePrice) || 0;
          const huidCharges = item.huid ? parseFloat(item.huidCost) || 0 : 0;

          const totalPercentage = purityNum + wastageNum;
          const totalFine = weight * (totalPercentage / 100);

          if (metalRateNum > 0) {
            const amountFromFine = metalRateNum * totalFine;
            subtotal += amountFromFine + stoneCharges + huidCharges;
            totalAmountFromMetal += amountFromFine;
          } else {
            totalFineWeight += totalFine;
          }

          totalStoneCharge += stoneCharges;
          totalHuidCharge += huidCharges;
        });
      }
    } else {
      subtotal = parseFloat(formik.values.totalAmount) || 0;
    }

    const roundedFineWeight = Math.round(totalFineWeight * 1000) / 1000;
    const roundedAmountFromMetal = Math.round(totalAmountFromMetal * 100) / 100;

    const finePaymentAmount = finePayment.enabled ? calculatedFinePayment : 0;
    const fineBalance = roundedFineWeight - finePaymentAmount;

    let sub = subtotal > 0 ? subtotal : roundedAmountFromMetal;
    let discountAmount = parseFloat(formik.values.discount) || 0;
    let cgst = 0, sgst = 0, igst = 0, grandTotal = sub;

    // GST calculation on the amount that includes stone and HUID charges
    if (showGST && sub > 0) {
      if (gstMode === "add") {
        if (gstType === 'igst') {
          igst = sub * 0.03;
          grandTotal = sub + igst - discountAmount;
        } else {
          cgst = sub * 0.015;
          sgst = sub * 0.015;
          grandTotal = sub + cgst + sgst - discountAmount;
        }
      } else {
        if (gstType === 'igst') {
          igst = sub * 0.03;
          grandTotal = sub + igst - discountAmount;
        } else {
          cgst = sub * 0.015;
          sgst = sub * 0.015;
          grandTotal = sub + cgst + sgst - discountAmount;
        }
      }
    } else {
      grandTotal = sub - discountAmount;
    }

    return {
      subtotal: sub,
      cgst,
      sgst,
      igst,
      grandTotal: grandTotal > 0 ? grandTotal : 0,
      totalStoneCharge,
      totalHuidCharge,
      totalFineWeight: roundedFineWeight,
      totalAmountFromMetal: roundedAmountFromMetal,
      finePaymentReceived: finePaymentAmount,
      fineBalance: roundedFineWeight > 0 ? fineBalance : 0,
      isFineMode: subtotal === 0 && roundedFineWeight > 0,
    };
  }, [items, formik.values.totalAmount, formik.values.discount, showGST, gstMode, gstType, transactionType, itemConsolidationMode, consolidatedItemGroups, finePayment.enabled, calculatedFinePayment]);

  const advanceTotal = useMemo(
    () => Object.values(advancePayments).reduce((sum, val) => sum + (parseFloat(val) || 0), 0),
    [advancePayments]
  );

  const totalPayments = useMemo(() => {
    return (
      (parseFloat(formik.values.cashPayment) || 0) +
      (parseFloat(formik.values.onlinePayment) || 0) +
      (parseFloat(formik.values.chequePayment) || 0) +
      (parseFloat(formik.values.otherPayment) || 0) +
      advanceTotal
    );
  }, [
    formik.values.cashPayment,
    formik.values.onlinePayment,
    formik.values.chequePayment,
    formik.values.otherPayment,
    advanceTotal,
  ]);

  const pendingAmount = useMemo(
    () => calculations.grandTotal - totalPayments,
    [calculations.grandTotal, totalPayments]
  );

  const { setFieldValue } = formik;

  // Update paymentDone when totalPayments changes
  useEffect(() => {
    setFieldValue("paymentDone", totalPayments);
  }, [totalPayments, setFieldValue]);

  // Recalculate items when GST mode changes
  useEffect(() => {
    if (items.length === 0) return;

    // When GST is turned OFF, restore original values
    if (!showGST) {
      const restoredItems = items.map((item) => {
        if (item.currentWastage !== undefined && item.currentTotal !== undefined) {
          return {
            ...item,
            wastage: item.currentWastage,
            labour: item.currentLabour ?? item.labour,
            total: item.currentTotal,
            gstAdjusted: false,
          };
        }
        return item;
      });

      const hasChanges = restoredItems.some((item, index) =>
        item.total !== items[index].total
      );

      if (hasChanges) {
        setItems(restoredItems);
      }
      return;
    }

    // When GST is turned ON with "add" mode, restore original values (don't modify item totals)
    if (showGST && gstMode === "add") {
      const restoredItems = items.map((item) => {
        // If item was previously adjusted, restore original values
        if (item.gstAdjusted === true && item.currentWastage !== undefined && item.currentTotal !== undefined) {
          return {
            ...item,
            wastage: item.currentWastage,
            labour: item.currentLabour ?? item.labour,
            total: item.total,
            gstAdjusted: false,
          };
        }
        return item;
      });

      const hasChanges = restoredItems.some((item, index) =>
        item.total !== items[index].total
      );

      if (hasChanges) {
        setItems(restoredItems);
      }
      return;
    }

    // When in "adjust" mode with GST on - adjust wastage so that final price with GST matches original
    if (showGST && gstMode === "adjust") {
      console.log("Adjusting items for GST...");
      console.log(items);
      const updatedItems = items.map((item) => {
        // Skip if item doesn't have required fields
        if (!item.weight || !item.metalRate) return item;

        const weight = parseFloat(item.weight) || 0;
        const metalRate = parseFloat(item.metalRate) || 0;

        if (weight === 0 || metalRate === 0) return item;
        console.log(item);
        // Store original values if not already stored
        let originalWastage = item.currentWastage;
        let originalLabour = item.currentLabour;
        let originalTotal = item.total;

        // If no stored values, calculate and store them now
        if (originalWastage === undefined || originalTotal === undefined) {
          if (item.calculationType === "wastage") {
            originalWastage = parseFloat(item.currentWastage) || 0;
            originalTotal = weight * metalRate * ((originalWastage + 100) / 100);
            originalLabour = item.labour;
          } else {
            originalLabour = parseFloat(item.labour) || 0;
            originalTotal = (metalRate + originalLabour) * weight;
            originalWastage = item.wastage;
          }
        }

        let adjustedWastage = originalWastage;
        let adjustedLabour = originalLabour;
        let adjustedTotal = originalTotal;

        if (item.calculationType === "wastage") {
          // Formula: New Wastage = (Original Total) / (1.03 * Metal Rate * Weight) - 1
          // Then multiply by 100 to get percentage
          const denominator = 1.03 * metalRate * weight;
          if (denominator > 0) {
            const wastageRatio = originalTotal / denominator;
            // wastageRatio = (100 + adjustedWastage) / 100
            // So adjustedWastage = (wastageRatio * 100) - 100
            const calculatedWastage = (wastageRatio * 100) - 100;
            adjustedWastage = Math.max(0, parseFloat(calculatedWastage.toFixed(2)));

            // Calculate the new total (this will be the amount before GST)
            adjustedTotal = weight * metalRate * ((adjustedWastage + 100) / 100);

            console.log(`Item ${item.name}: Original Wastage: ${originalWastage}%, Original Total: ${originalTotal}`);
            console.log(`Adjusted Wastage: ${adjustedWastage}%, Adjusted Total: ${adjustedTotal}`);
            console.log(`Verification: ${adjustedTotal} * 1.03 = ${adjustedTotal * 1.03}, Original Total: ${originalTotal}`);
          }
        } else if (item.calculationType === "labour") {
          // For labour calculation: (metalRate + labour) * weight * 1.03 = originalTotal
          // So new labour = (originalTotal / 1.03 / weight) - metalRate
          if (weight > 0 && metalRate > 0) {
            const targetTotalBeforeGST = originalTotal / 1.03;
            const calculatedLabour = (targetTotalBeforeGST / weight) - metalRate;
            adjustedLabour = Math.max(0, parseFloat(calculatedLabour.toFixed(2)));
            adjustedTotal = (metalRate + adjustedLabour) * weight;

            console.log(`Item ${item.name}: Original Labour: ${originalLabour}, Original Total: ${originalTotal}`);
            console.log(`Adjusted Labour: ${adjustedLabour}, Adjusted Total: ${adjustedTotal}`);
            console.log(`Verification: ${adjustedTotal} * 1.03 = ${adjustedTotal * 1.03}, Original Total: ${originalTotal}`);
          }
        }

        // Only update if values are different
        if (item.calculationType === "wastage" && Math.abs(adjustedWastage - (item.wastage || 0)) > 0.01) {
          return {
            ...item,
            total: parseFloat(adjustedTotal.toFixed(2)),
            wastage: adjustedWastage,
            currentWastage: originalWastage,
            currentTotal: originalTotal,
            gstAdjusted: true,
          };
        } else if (item.calculationType === "labour" && Math.abs(adjustedLabour - (item.labour || 0)) > 0.01) {
          return {
            ...item,
            total: parseFloat(adjustedTotal.toFixed(2)),
            labour: adjustedLabour,
            currentLabour: originalLabour,
            currentTotal: originalTotal,
            gstAdjusted: true,
          };
        }

        return item;
      });

      // Only update if there are actual changes
      const hasChanges = updatedItems.some((item, index) => {
        if (item.calculationType === "wastage") {
          return Math.abs(item.wastage - items[index].wastage) > 0.01;
        } else {
          return Math.abs(item.labour - items[index].labour) > 0.01;
        }
      });

      if (hasChanges) {
        setItems(updatedItems);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showGST, gstMode]);

  // const calculateTotalStoneCharges = useCallback((items) => {
  //   return items.reduce((sum, item) => sum + (parseFloat(item.stonePrice) || 0), 0);
  // }, []);

  // Event handlers
  const handleQRScan = (qrData) => {
    const scannedQR = qrData.trim();
    const scannedItem = stock?.find(item => item.qr === scannedQR);

    if (!scannedItem) {
      alert(`Item with QR code "${scannedQR}" not found in inventory`);
      return;
    }

    if (scannedItem?.sold === "Yes") {
      alert('This item has already been sold');
      return;
    }

    // Check if already added (for separate mode)
    if (itemConsolidationMode === 'separate') {
      const alreadyAdded = items.some(item =>
        item.itemObject && item.itemObject.qr === scannedQR
      );

      if (alreadyAdded) {
        alert('Item already added to transaction');
        return;
      }
    }

    // Extract item details
    // const itemName = scannedItem.name || '';
    const weight = parseFloat(scannedItem.weight) || 0;
    const metalRate = parseFloat(scannedItem.metalRate) || 0;
    const makingCharges = parseFloat(scannedItem.makingCharges) || 0;
    const melting = parseFloat(scannedItem.melting) || 75;
    const metal = scannedItem.metal || 'Gold';
    const huid = scannedItem.huid || '';
    const stonePrice = parseFloat(scannedItem.stonePrice) || 0;

    if (itemConsolidationMode === 'consolidate') {
      // CONSOLIDATION MODE: Group items with same name
      const itemName = scannedItem.name || '';

      // const newItem = {
      //   id: scannedItem.id,
      //   qr: scannedItem.qr,
      //   serialNo: scannedItem.serialNo,
      //   weight: weight,
      //   metalRate: metalRate,
      //   makingCharges: makingCharges,
      //   melting: melting,
      //   metal: metal,
      //   huid: huid,
      //   stonePrice: stonePrice,
      //   itemObject: scannedItem
      // };

      const isDuplicate = consolidatedItemGroups[itemName]?.items.some(
        existingItem => existingItem.id === scannedItem.id
      );

      if (isDuplicate) {
        setCustomAlert({
          open: true,
          title: "Duplicate Item",
          message: `Item "${scannedItem.name}" with Serial No: ${scannedItem.serialNo} has already been added.`,
          severity: "warning",
          onConfirm: () => setCustomAlert({ ...customAlert, open: false }),
        });
        return;
      }

      setConsolidatedItemGroups(prev => {
        const existingGroup = prev[itemName];
        // const totalStoneCharges = existingGroup
        // ? (parseFloat(existingGroup.stoneCharges) || 0) + stonePrice
        // : stonePrice;

        if (existingGroup) {
          // Get the existing total percentage from the group
          const existingTotalPercentage = parseFloat(existingGroup.totalPercentage) || 0;
          const newTotalWeight = (parseFloat(existingGroup.totalWeight) || 0) + weight;

          // Calculate total fine using the existing total percentage
          const newTotalFine = newTotalWeight * (existingTotalPercentage / 100);

          // Calculate total amount if metal rate exists
          const metalRateNum = parseFloat(existingGroup.metalRate) || 0;
          let newTotalAmount = 0;
          if (metalRateNum > 0 && newTotalFine > 0) {
            newTotalAmount = metalRateNum * newTotalFine;
          }

          // Add stone and HUID charges
          const existingStoneCharges = parseFloat(existingGroup.stoneCharges) || 0;
          const existingHuidCharges = parseFloat(existingGroup.huidCharges) || 0;
          const finalTotalAmount = newTotalAmount + existingStoneCharges + stonePrice + existingHuidCharges;

          return {
            ...prev,
            [itemName]: {
              ...existingGroup,
              items: [...existingGroup.items, {
                id: scannedItem.id,
                qr: scannedQR,
                weight: weight,
                metalRate: metalRate,
                grossWeight: scannedItem.grossWeight || 0,
                czWeight: scannedItem.czWeight || 0,
                pearlWeight: scannedItem.pearlWeight || 0,
                beltWeight: scannedItem.beltWeight || 0,
                otherWeight: scannedItem.otherWeight || 0,
                lessWeight: scannedItem.lessWeight || 0,
                totalPercentage: existingTotalPercentage, // Use existing total percentage
                makingCharges: makingCharges,
                melting: melting,
                metal: metal,
                huid: huid,
                stonePrice: stonePrice,
                itemObject: scannedItem
              }],
              totalWeight: newTotalWeight,
              totalFine: newTotalFine,
              totalAmount: finalTotalAmount,
              stoneCharges: existingStoneCharges + stonePrice,
              // Keep existing totalPercentage
            }
          };
        } else {
          // Create new group with default totalPercentage (user will set it)
          return {
            ...prev,
            [itemName]: {
              id: `group_${Date.now()}_${itemName}`,
              name: itemName,
              items: [{
                id: scannedItem.id,
                qr: scannedQR,
                weight: weight,
                metalRate: metalRate,
                makingCharges: makingCharges,
                melting: melting,
                metal: metal,
                huid: huid,
                stonePrice: stonePrice,
                itemObject: scannedItem
              }],
              totalWeight: weight,
              totalFine: 0,
              totalAmount: 0,
              metalRate: metalRate,
              totalPercentage: '', // Empty initially, user must enter
              wastage: makingCharges,
              calculationType: 'wastage',
              stoneCharges: stonePrice,
              huidCharges: huid ? parseFloat(scannedItem.huidCost) || 0 : 0,
              metal: metal,
              purity: melting,
              totalStoneCharges: stonePrice,
              totalHuidCharges: huid ? parseFloat(scannedItem.huidCost) || 0 : 0,
              consolidated: true
            }
          };
        }
      });

      alert(`Item "${itemName}" added to consolidated group.`);
    } else {
      // SEPARATE MODE (Original behavior)
      const currentAmount = weight * metalRate * ((makingCharges + 100) / 100);
      let totalAmount = currentAmount;
      let adjustedMakingCharges = makingCharges;

      if (showGST && gstMode === "adjust") {
        const calculation = calculateItemTotal(
          weight,
          metalRate,
          makingCharges,
          0,
          'wastage',
          true,
          currentAmount
        );

        if (typeof calculation === 'object') {
          totalAmount = calculation.total;
          adjustedMakingCharges = calculation.adjustedWastage;
        }
      }

      const newItem = {
        id: Date.now(),
        name: scannedItem.name || '',
        ct: scannedItem.ct || '',
        weight: weight,
        metalRate: metalRate,
        wastage: adjustedMakingCharges,
        total: totalAmount,
        stonePrice: stonePrice,
        huid: huid,
        currentWastage: makingCharges,
        currentTotal: currentAmount,
        gstAdjusted: showGST && gstMode === "adjust",
        calculationType: 'wastage',
        adjustWastage: true,
        itemObject: scannedItem,
        qr: scannedItem?.qr || null,
        metal: metal,
        melting: melting,
      };

      setItems(prev => [...prev, newItem]);

      let message = `Item "${scannedItem.name}" added successfully!`;
      if (newItem.gstAdjusted) {
        message += `\nMaking charges adjusted from ${makingCharges}% to ${adjustedMakingCharges.toFixed(2)}% to include GST`;
      }
      alert(message);
    }

    // setScannerOpen(false);
  };

  const filteredCustomerSuggestions = useMemo(() => {
    if (!customerQuery?.trim()) return [];
    const query = customerQuery.toLowerCase().trim();
    return customers.filter(
      (c) =>
        c.name?.toLowerCase().includes(query) ||
        c.contact?.toString().toLowerCase().includes(query)
    );
  }, [customerQuery, customers]);

  useEffect(() => {
    if (filteredCustomerSuggestions.length > 0) {
      setCustomerSuggestions(filteredCustomerSuggestions);
      setIsNewCustomer(false);
      setShowNewCustomerFields(false);
    } else if (customerQuery?.trim()) {
      setIsNewCustomer(true);
      setCustomerSuggestions([{
        id: "new",
        name: `${customerQuery}`,
        isNew: true,
      }]);
    } else {
      setCustomerSuggestions([]);
      setIsNewCustomer(false);
      setShowNewCustomerFields(false);
    }
    setIsLoading(false)
  }, [filteredCustomerSuggestions, customerQuery]);

  const handleCustomerSelect = useCallback(
    (customer) => {
      if (customer.isNew) {
        formik.setFieldValue("customerName", customerQuery);
        formik.setFieldValue("isNewCustomer", true);
        setShowNewCustomerFields(true);
        setIsNewCustomer(true);
        setSelectedCustomer(null);
        // Clear GST and PAN when adding new customer
        formik.setFieldValue("gstNumber", "");
        formik.setFieldValue("panNumber", "");
      } else {
        setSelectedCustomer(customer);
        formik.setFieldValue("customerName", customer.name);
        // Set GST and PAN from selected customer
        formik.setFieldValue("gstNumber", customer.gstNo || "");
        formik.setFieldValue("panNumber", customer.panNo || "");
        setIsNewCustomer(false);
        setShowNewCustomerFields(false);
      }
      setCustomerQuery(customer.name);
      setCustomerSuggestions([]);
    },
    [customerQuery, formik]
  );

  // Add this after your existing useEffects (around line where you have the companyDetails useEffect)

  // Default customer selection
  useEffect(() => {
    const DEFAULT_CUSTOMER_ID = "b5f19dfe-e322-47fe-ab65-3d8e61dc3f68";

    if (customers && customers.length > 0 && !selectedCustomer && !isTransactionSaved) {
      // Find the customer with the default ID
      const defaultCustomer = customers.find(customer => customer.id === DEFAULT_CUSTOMER_ID);

      if (defaultCustomer) {
        // Set the customer as selected
        setSelectedCustomer(defaultCustomer);
        formik.setFieldValue("customerName", defaultCustomer.name);
        formik.setFieldValue("gstNumber", defaultCustomer.gstNo || "");
        formik.setFieldValue("panNumber", defaultCustomer.panNo || "");
        setCustomerQuery(defaultCustomer.name);
        setIsNewCustomer(false);
        setShowNewCustomerFields(false);

      } else {
        console.warn(`Default customer with ID ${DEFAULT_CUSTOMER_ID} not found`);
      }
    }
  }, [customers, selectedCustomer, isTransactionSaved, formik]);

  const handleAddBySerial = useCallback(() => {
    if (!manualItemName) {
      alert("Please select an item type");
      return;
    }

    if (!manualSerial) {
      alert("Please enter serial number");
      return;
    }

    // Build filter conditions
    const stockItem = stock?.find((item) => {
      const serialMatch = item.serialNo?.toString() === manualSerial?.toString();
      const nameMatch = item.name?.toString() === manualItemName?.toString();
      const metalMatch = manualItemMetal ? item.metal?.toString() === manualItemMetal?.toString() : true;
      const notSold = item.sold === "No";

      return serialMatch && nameMatch && metalMatch && notSold;
    });

    if (stockItem?.sold === "Yes") {
      alert("This item has already been sold.");
      return;
    }

    if (stockItem) {
      const weight = parseFloat(stockItem.weight) || 0;
      const metalRate = parseFloat(stockItem.metalRate) || 0;
      const makingCharges = parseFloat(stockItem.makingCharges) || 0;
      const melting = parseFloat(stockItem.melting) || 75;
      const metal = stockItem.metal || 'Gold';
      const huid = stockItem.huid || '';
      const stonePrice = parseFloat(stockItem.stonePrice) || 0;

      if (itemConsolidationMode === 'consolidate') {
        // CONSOLIDATION MODE
        const itemName = stockItem.name || '';

        // const newItem = {
        //   id: stockItem.id,
        //   qr: stockItem.qr,
        //   serialNo: stockItem.serialNo,
        //   weight: weight,
        //   metalRate: metalRate,
        //   grossWeight: stockItem.grossWeight || 0,
        //   czWeight: stockItem.czWeight || 0,
        //   pearlWeight: stockItem.pearlWeight || 0,
        //   beltWeight: stockItem.beltWeight || 0,
        //   otherWeight: stockItem.otherWeight || 0,
        //   lessWeight: stockItem.lessWeight || 0,
        //   makingCharges: makingCharges,
        //   melting: melting,
        //   metal: metal,
        //   huid: huid,
        //   stonePrice: stonePrice,
        //   itemObject: stockItem
        // };

        const isDuplicate = consolidatedItemGroups[itemName]?.items.some(
          existingItem => existingItem.id === stockItem.id
        );

        if (isDuplicate) {
          setCustomAlert({
            open: true,
            title: "Duplicate Item",
            message: `Item "${stockItem.name}" with Serial No: ${stockItem.serialNo} has already been added.`,
            severity: "warning",
            onConfirm: () => setCustomAlert({ ...customAlert, open: false }),
          });
          return;
        }

        setConsolidatedItemGroups(prev => {
          const existingGroup = prev[itemName];
          // const totalStoneCharges = existingGroup
          //   ? (parseFloat(existingGroup.stoneCharges) || 0) + stonePrice
          //   : stonePrice;

          if (existingGroup) {
            // Get the existing total percentage from the group
            const existingTotalPercentage = parseFloat(existingGroup.totalPercentage) || 0;
            const newTotalWeight = (parseFloat(existingGroup.totalWeight) || 0) + weight;

            // Calculate total fine using the existing total percentage
            const newTotalFine = newTotalWeight * (existingTotalPercentage / 100);

            // Calculate total amount if metal rate exists
            const metalRateNum = parseFloat(existingGroup.metalRate) || 0;
            let newTotalAmount = 0;
            if (metalRateNum > 0 && newTotalFine > 0) {
              newTotalAmount = metalRateNum * newTotalFine;
            }

            // Add stone and HUID charges
            const existingStoneCharges = parseFloat(existingGroup.stoneCharges) || 0;
            const existingHuidCharges = parseFloat(existingGroup.huidCharges) || 0;
            const finalTotalAmount = newTotalAmount + existingStoneCharges + stonePrice + existingHuidCharges;

            return {
              ...prev,
              [itemName]: {
                ...existingGroup,
                items: [...existingGroup.items, {
                  id: stockItem.id,
                  qr: stockItem.qr,
                  serialNo: stockItem.serialNo,
                  weight: weight,
                  metalRate: metalRate,
                  grossWeight: stockItem.grossWeight || 0,
                  czWeight: stockItem.czWeight || 0,
                  pearlWeight: stockItem.pearlWeight || 0,
                  beltWeight: stockItem.beltWeight || 0,
                  otherWeight: stockItem.otherWeight || 0,
                  lessWeight: stockItem.lessWeight || 0,
                  totalPercentage: existingTotalPercentage, // Use existing total percentage
                  makingCharges: makingCharges,
                  melting: melting,
                  metal: metal,
                  huid: huid,
                  stonePrice: stonePrice,
                  itemObject: stockItem
                }],
                totalWeight: newTotalWeight,
                totalFine: newTotalFine,
                totalAmount: finalTotalAmount,
                stoneCharges: existingStoneCharges + stonePrice,
                // Keep existing totalPercentage
              }
            };
          } else {
            // Create new group with default totalPercentage (user will set it)
            return {
              ...prev,
              [itemName]: {
                id: `group_${Date.now()}_${itemName}`,
                name: itemName,
                items: [{
                  id: stockItem.id,
                  qr: stockItem.qr,
                  serialNo: stockItem.serialNo,
                  weight: weight,
                  grossWeight: stockItem.grossWeight || 0,
                  czWeight: stockItem.czWeight || 0,
                  pearlWeight: stockItem.pearlWeight || 0,
                  beltWeight: stockItem.beltWeight || 0,
                  otherWeight: stockItem.otherWeight || 0,
                  lessWeight: stockItem.lessWeight || 0,
                  metalRate: metalRate,
                  makingCharges: makingCharges,
                  melting: melting,
                  metal: metal,
                  huid: huid,
                  stonePrice: stonePrice,
                  itemObject: stockItem
                }],
                totalWeight: weight,
                totalFine: 0,
                totalAmount: 0,
                metalRate: metalRate,
                totalPercentage: '', // Empty initially, user must enter
                wastage: makingCharges,
                calculationType: 'wastage',
                stoneCharges: stonePrice,
                huidCharges: huid ? parseFloat(stockItem.huidCost) || 0 : 0,
                metal: metal,
                purity: melting,
                totalStoneCharges: stonePrice,
                totalHuidCharges: 0,
                consolidated: true
              }
            };
          }
        });

        alert(`Item "${stockItem.name}" added to consolidated group.`);
      } else {
        // SEPARATE MODE
        const currentAmount = weight * metalRate * ((makingCharges + 100) / 100);
        let totalAmount = currentAmount;
        let adjustedMakingCharges = makingCharges;

        if (showGST && gstMode === "adjust") {
          const calculation = calculateItemTotal(
            weight,
            metalRate,
            makingCharges,
            0,
            'wastage',
            true,
            currentAmount
          );

          if (typeof calculation === 'object') {
            totalAmount = calculation.total;
            adjustedMakingCharges = calculation.adjustedWastage;
          }
        }

        const newItem = {
          id: Date.now(),
          name: `${stockItem.name} ${stockItem?.metal || ''}`,
          ct: stockItem.ct || "",
          weight: weight,
          metalRate: metalRate,
          wastage: adjustedMakingCharges,
          total: totalAmount,
          stonePrice: stonePrice,
          huid: huid,
          currentWastage: makingCharges,
          currentTotal: currentAmount,
          gstAdjusted: showGST && gstMode === "adjust",
          calculationType: 'wastage',
          adjustWastage: true,
          itemObject: stockItem,
          qr: stockItem?.qr || null,
          metal: metal,
          melting: melting,
        };

        setItems((prev) => [...prev, newItem]);

        let message = `Item "${stockItem.name}" added successfully!`;
        if (newItem.gstAdjusted) {
          message += `\nMaking charges adjusted from ${makingCharges}% to ${adjustedMakingCharges.toFixed(2)}% to include GST`;
        }
        alert(message);
      }

      // Reset the form fields
      setManualItemName("");
      setManualItemMetal("");
      setManualSerial("");

    } else {
      const metalFilter = manualItemMetal ? `, Metal: ${manualItemMetal}` : '';
      alert(`Item not found in stock. Serial: ${manualSerial}, Name: ${manualItemName}${metalFilter}`);
    }
  }, [manualItemName, manualItemMetal, manualSerial, stock, calculateItemTotal, showGST, gstMode, itemConsolidationMode, consolidatedItemGroups, customAlert]);

  // const removeItemRow = useCallback((id) => {
  //   setItems((prev) => prev.filter((item) => item.id !== id));
  // }, []);

  const removeItemRow = useCallback(
    (id) => {
      // Find the item to delete
      const itemToDelete = items.find((item) => item.id === id);

      if (!itemToDelete) return;

      // Check if this item has weight adjustments
      const hasWeightAdjustment = !!(
        itemToDelete.weightAdjusted ||
        itemToDelete.adjustmentApplied ||
        itemToDelete.weightAdjustmentData ||
        (pendingWeightAdjustments && pendingWeightAdjustments[id])
      );

      // Show appropriate dialog based on whether weight adjustments exist
      setDeleteConfirmDialog({
        open: true,
        itemToDelete: itemToDelete,
        hasWeightAdjustment: hasWeightAdjustment,
      });
    },
    [items, pendingWeightAdjustments],
  );

  // Enhanced confirmDeleteItem function
  const confirmDeleteItem = useCallback(async () => {
    const { itemToDelete, hasWeightAdjustment } = deleteConfirmDialog;

    if (!itemToDelete) return false;

    try {
      // Clean up weight adjustment data if it exists
      if (hasWeightAdjustment) {
        // Log for audit purposes
        console.log(`Deleting item ${itemToDelete.id} with weight adjustments. 
                    Weight adjustments were already saved and won't be reverted.`);

        // Clean up pending weight adjustments
        if (
          pendingWeightAdjustments &&
          pendingWeightAdjustments[itemToDelete.id]
        ) {
          const newPendingAdjustments = { ...pendingWeightAdjustments };
          delete newPendingAdjustments[itemToDelete.id];
          setPendingWeightAdjustments(newPendingAdjustments);
        }

        // Clean up weight adjustments state
        if (weightAdjustments[itemToDelete.id]) {
          const newWeightAdjustments = { ...weightAdjustments };
          delete newWeightAdjustments[itemToDelete.id];
          setWeightAdjustments(newWeightAdjustments);
        }
      }

      // Remove the item from items array
      setItems((prev) => prev.filter((item) => item.id !== itemToDelete.id));

      // Show appropriate message
      let message = `Item "${itemToDelete.name}" removed from transaction.`;
      if (hasWeightAdjustment) {
        message += ` Weight adjustments were already saved and cannot be reverted.`;
      }

      setSnackbar({
        open: true,
        message: message,
        severity: hasWeightAdjustment ? "warning" : "success",
      });

      // Close dialog
      setDeleteConfirmDialog({
        open: false,
        itemToDelete: null,
        hasWeightAdjustment: false,
      });

      return true;
    } catch (error) {
      console.error("Error deleting item:", error);
      setSnackbar({
        open: true,
        message: "Failed to delete item. Please try again.",
        severity: "error",
      });
      return false;
    }
  }, [
    deleteConfirmDialog,
    pendingWeightAdjustments,
    weightAdjustments,
    setItems,
  ]);

  const updateItemField = useCallback(
    (id, field, value) => {
      setItems((prev) =>
        prev.map((item) => {
          if (item.id === id) {
            const updatedItem = { ...item, [field]: value };

            // If adjustWastage is false, don't perform calculations
            if (item.adjustWastage === false && field !== 'total') {
              return updatedItem;
            }

            const weight = parseFloat(field === 'weight' ? value : updatedItem.weight) || 0;
            const metalRate = parseFloat(field === 'metalRate' ? value : updatedItem.metalRate) || 0;
            const wastage = parseFloat(field === 'wastage' ? value : updatedItem.wastage) || 0;
            const labour = parseFloat(field === 'labour' ? value : updatedItem.labour) || 0;
            const total = parseFloat(field === 'total' ? value : updatedItem.total) || 0;

            const calculationType = item.calculationType || 'wastage';

            if (field === 'total') {
              if (weight > 0 && metalRate > 0 && item.adjustWastage !== false) {
                if (calculationType === 'wastage') {
                  const calculatedWastage = ((total / (metalRate * weight)) - 1) * 100;
                  updatedItem.wastage = calculatedWastage > 0 ? parseFloat(calculatedWastage.toFixed(2)) : 0;
                  updatedItem.currentWastage = updatedItem.wastage;
                  updatedItem.currentTotal = total;
                } else {
                  const calculatedLabour = (total / weight) - metalRate;
                  updatedItem.labour = calculatedLabour > 0 ? parseFloat(calculatedLabour.toFixed(2)) : 0;
                  updatedItem.currentLabour = updatedItem.labour;
                  updatedItem.currentTotal = total;
                }
                updatedItem.total = total;
              }
            } else {
              let currentAmount = 0;
              if (calculationType === 'wastage') {
                const currentWastage = field === 'wastage' ? parseFloat(value) || 0 : wastage;
                currentAmount = metalRate * weight * ((currentWastage + 100) / 100);
                updatedItem.currentWastage = currentWastage;
              } else {
                const currentLabour = field === 'labour' ? parseFloat(value) || 0 : labour;
                currentAmount = (metalRate + currentLabour) * weight;
                updatedItem.currentLabour = currentLabour;
              }

              updatedItem.currentTotal = currentAmount;

              // Only apply GST adjustments if adjustWastage is true
              if (showGST && gstMode === "adjust" && item.adjustWastage !== false) {
                if (calculationType === 'wastage') {
                  const denominator = 1.03 * metalRate * weight;
                  if (denominator > 0 && currentAmount > 0) {
                    const adjustedWastage = ((currentAmount / denominator) - 1) * 100;
                    const newAmount = metalRate * weight * ((adjustedWastage + 100) / 100);
                    updatedItem.total = parseFloat(newAmount.toFixed(2));
                    updatedItem.wastage = parseFloat(adjustedWastage.toFixed(2));
                  } else {
                    updatedItem.total = parseFloat(currentAmount.toFixed(2));
                    updatedItem.wastage = field === 'wastage' ? parseFloat(value) || 0 : wastage;
                  }
                } else {
                  if (weight > 0 && currentAmount > 0) {
                    const adjustedLabourPerGram = (currentAmount / (1.03 * weight)) - metalRate;
                    const newAmount = (metalRate + adjustedLabourPerGram) * weight;
                    updatedItem.total = parseFloat(newAmount.toFixed(2));
                    updatedItem.labour = parseFloat(adjustedLabourPerGram.toFixed(2));
                  } else {
                    updatedItem.total = parseFloat(currentAmount.toFixed(2));
                    updatedItem.labour = field === 'labour' ? parseFloat(value) || 0 : labour;
                  }
                }
                updatedItem.gstAdjusted = true;
              } else {
                updatedItem.total = parseFloat(currentAmount.toFixed(2));
                if (calculationType === 'wastage') {
                  updatedItem.wastage = field === 'wastage' ? parseFloat(value) || 0 : wastage;
                } else {
                  updatedItem.labour = field === 'labour' ? parseFloat(value) || 0 : labour;
                }
                updatedItem.gstAdjusted = false;
              }
            }

            return updatedItem;
          }
          return item;
        })
      );
    },
    [showGST, gstMode]
  );

  const handlePaymentModeChange = useCallback(
    (mode) => {
      const newPaymentModes = { ...paymentModes, [mode]: !paymentModes[mode] };
      setPaymentModes(newPaymentModes);

      console.log(`Initializing advance payment for mode: ${mode}`);
      if (newPaymentModes[mode]) {
        // When enabling a mode, initialize it with 0 if not already set

        setAdvancePayments((prev) => {
          if (prev[mode] === undefined) {
            return { ...prev, [mode]: "0" };
          }
          return prev;
        });
      } else {
        // When disabling a mode, keep the value but just hide it
        // Don't delete the value, just keep it
        // setAdvancePayments((prev) => {
        //   const newPayments = { ...prev };
        //   delete newPayments[mode];
        //   return newPayments;
        // });
      }
    },
    [paymentModes]
  );

  const handleAdvancePaymentChange = useCallback((mode, value) => {
    setAdvancePayments((prev) => ({ ...prev, [mode]: value }));
  }, []);

  const handleAddCustomOther = useCallback(() => {
    if (!customOther.trim()) {
      alert("Please enter a custom payment type");
      return;
    }

    const optionName = customOther.trim();
    if (!otherOptions.includes(optionName)) {
      setOtherOptions((prev) => [...prev, optionName]);
      setAdvancePayments((prev) => ({ ...prev, [optionName]: "" }));
      setCustomOther("");
    } else {
      alert("This payment type already exists");
    }
  }, [customOther, otherOptions]);

  const handleCustomOtherPaymentChange = useCallback((optionName, value) => {
    setAdvancePayments((prev) => ({ ...prev, [optionName]: value }));
  }, []);

  const handleRemoveCustomOther = useCallback((optionName) => {
    setOtherOptions((prev) => prev.filter((opt) => opt !== optionName));
    setAdvancePayments((prev) => {
      const newPayments = { ...prev };
      delete newPayments[optionName];
      return newPayments;
    });
  }, []);

  const handleAddManualItem = async (itemDetails) => {
    let totalAmount = itemDetails.total;
    let wastageValue = itemDetails.calculationType === 'wastage' ? parseFloat(itemDetails.wastage) || 0 : 0;
    let labourValue = itemDetails.calculationType === 'labour' ? parseFloat(itemDetails.labour) || 0 : 0;
    let adjustedWastage = wastageValue;
    let adjustedLabour = labourValue;

    const currentAmount = itemDetails.calculationType === 'wastage'
      ? parseFloat(itemDetails.weight) * parseFloat(itemDetails.metalRate) * ((wastageValue + 100) / 100)
      : (parseFloat(itemDetails.metalRate) + labourValue) * parseFloat(itemDetails.weight);

    if (showGST && gstMode === "adjust") {
      if (itemDetails.calculationType === 'wastage') {
        const calculation = calculateItemTotal(
          itemDetails.weight,
          itemDetails.metalRate,
          wastageValue,
          0,
          'wastage',
          true,
          currentAmount
        );

        if (typeof calculation === 'object') {
          totalAmount = calculation.total;
          adjustedWastage = calculation.adjustedWastage;
        }
      } else if (itemDetails.calculationType === 'labour') {
        const weightNum = parseFloat(itemDetails.weight) || 0;
        const metalRateNum = parseFloat(itemDetails.metalRate) || 0;
        if (weightNum > 0) {
          const adjustedLabourPerGram = (currentAmount / (1.03 * weightNum)) - metalRateNum;
          adjustedLabour = parseFloat(adjustedLabourPerGram.toFixed(2));
          totalAmount = (metalRateNum + adjustedLabour) * weightNum;
        }
      }
    }

    const newItem = {
      id: Date.now(),
      name: `${itemDetails.name} ${itemDetails?.huid ? `- ${itemDetails.huid}` : ""}`,
      huid: itemDetails.huid,
      weight: parseFloat(itemDetails.weight) || 0,
      metalRate: parseFloat(itemDetails.metalRate) || 0,
      wastage: adjustedWastage,
      labour: adjustedLabour,
      total: totalAmount,
      currentWastage: wastageValue,
      currentLabour: labourValue,
      currentTotal: currentAmount,
      gstAdjusted: showGST && gstMode === "adjust",
      calculationType: itemDetails.calculationType,
      adjustWastage: true,
      itemObject: null,
      manualItemDetails: {
        ...itemDetails,
        wastage: adjustedWastage,
        labour: adjustedLabour
      },
    };

    setItems(prev => [...prev, newItem]);

    let message = `${itemDetails.name} added successfully!`;
    if (newItem.gstAdjusted) {
      message += ` Making charges adjusted from ${wastageValue}% to ${adjustedWastage.toFixed(2)}% to include GST`;
    }
    alert(message);
  };

  // Function to validate GROSS STOCK availability only
  const validateGrossStockAvailability = useCallback(async () => {
    const grossStockIssues = [];

    // Group items by metal type
    const metalGroups = {};

    for (const item of items) {
      if (!item.itemObject && item.manualItemDetails) {
        const metal = item.manualItemDetails.metal || item.metal;
        const silverType = item.manualItemDetails.silverType || item.silverType;

        let metalKey = metal;
        if (metal === "Silver" && silverType) {
          metalKey = `Silver_${silverType}`;
        }

        if (!metalGroups[metalKey]) {
          metalGroups[metalKey] = {
            metal: metal,
            silverType: silverType,
            totalRequestedWeight: 0,
            items: []
          };
        }

        metalGroups[metalKey].totalRequestedWeight += parseFloat(item.weight) || 0;
        metalGroups[metalKey].items.push(item);
      }
    }

    if (Object.keys(metalGroups).length === 0) {
      return [];
    }

    // Use silverStock from context
    for (const [metalKey, group] of Object.entries(metalGroups)) {
      let availableGrossStock = 0;
      console.log(metalKey);
      if (group.metal === "Gold") {
        availableGrossStock = parseFloat(silverStock?.gold) || 0;
      } else if (group.metal === "Silver") {
        const silverTypeToCheck = group.silverType || 'silver';
        availableGrossStock = parseFloat(silverStock?.[silverTypeToCheck]) || 0;
      }

      if (group.totalRequestedWeight > availableGrossStock) {
        const shortage = group.totalRequestedWeight - availableGrossStock;
        const metalDisplayName = group.metal === "Silver" && group.silverType
          ? `${group.metal} (${group.silverType})`
          : group.metal;

        grossStockIssues.push({
          metalDisplayName: metalDisplayName,
          requestedWeight: group.totalRequestedWeight,
          availableWeight: availableGrossStock,
          shortage: shortage,
          message: `${metalDisplayName}: Requested ${group.totalRequestedWeight.toFixed(2)}g, Available: ${availableGrossStock.toFixed(2)}g (Shortage: ${shortage.toFixed(2)}g)`
        });
      }
    }

    return grossStockIssues;
  }, [items, silverStock]);

  const handleSubmit = async (values) => {
    setIsLoading(true);

    try {
      // Remove customer validation - allow transaction without customer
      // Just proceed with stock validation

      // Check for network connectivity first
      if (!navigator.onLine) {
        setCustomAlert({
          open: true,
          title: "Network Error",
          message: "No internet connection. Please check your network and try again. Transaction has not been saved.",
          severity: "error",
          onConfirm: () => setCustomAlert({ ...customAlert, open: false }),
        });
        setIsLoading(false);
        return;
      }

      // Validate GROSS STOCK availability only
      const grossStockIssues = await validateGrossStockAvailability();

      if (grossStockIssues.length > 0) {
        const issueMessages = grossStockIssues.map(issue => issue.message).join('\n\n');
        const totalShortage = grossStockIssues.reduce((sum, issue) => sum + issue.shortage, 0);

        setCustomAlert({
          open: true,
          title: "⚠️ Gross Stock Insufficient",
          message: `The following metals have insufficient GROSS STOCK:\n\n${issueMessages}\n\n📦 Total Shortage: ${totalShortage.toFixed(2)}g\n\nPlease add more stock to Gross Stock before saving this transaction.`,
          severity: "warning",
          onConfirm: () => setCustomAlert({ ...customAlert, open: false }),
        });
        setIsLoading(false);
        return;
      }

      // Customer is optional - set to null if not selected
      let customerId = selectedCustomer?.id || null;

      // If new customer but not selected, don't create - just use null
      if (isNewCustomer && !selectedCustomer && values.customerName) {
        try {
          const formatDate = (date) => {
            if (!date) return "";
            try {
              return format(date, "dd/MM/yyyy");
            } catch {
              return "";
            }
          };

          const newCustomer = {
            name: values.customerName,
            contact: values.newCustomerContact || "",
            address: values.newCustomerAddress || "",
            dob: formatDate(values.newCustomerDob) || "",
            anniversary: formatDate(values.newCustomerAnniversary) || "",
            dateAdded: new Date().toISOString().split("T")[0],
          };

          const result = await addCustomer(newCustomer);
          customerId = result?.data?.id;
          setSelectedCustomer({ id: customerId, ...newCustomer });
        } catch (error) {
          console.error("Failed to add new customer:", error);
          // Continue without customer - don't block transaction
          customerId = null;
        }
      }

      // Get last bill number - continue without customer
      const billNumberUsed = currentBillNo || 1;
      // setUsedBillNo(billNumberUsed);

      let d = new Date(values.transactionDate);
      let month = "" + (d.getMonth() + 1);
      let year = d.getFullYear();
      let date = "" + d.getDate();
      let itemToUpdateId = [];

      // Prepare items array based on mode (same as before)
      let itemsToSave = [];

      if (transactionType === "detailed") {
        if (itemConsolidationMode === 'consolidate') {
          itemsToSave = Object.entries(consolidatedItemGroups).map(([groupName, groupData], index) => {
            const wastageNum = parseFloat(groupData.wastage) || 0;
            const purityNum = parseFloat(groupData.purity) || 75;
            const totalWeightNum = parseFloat(groupData.totalWeight) || 0;
            const metalRateNum = parseFloat(groupData.metalRate) || 0;
            const totalPercentage = parseFloat(groupData.totalPercentage) || 0;

            const totalFine = totalWeightNum * (totalPercentage / 100);
            const totalAmountFromFine = metalRateNum > 0 ? metalRateNum * totalFine : 0;

            const stoneCharges = parseFloat(groupData.stoneCharges) || 0;
            const huidCharges = parseFloat(groupData.huidCharges) || 0;
            const finalAmount = totalAmountFromFine + stoneCharges + huidCharges;

            groupData.items.forEach(item => {
              if (item.itemObject?.id) {
                itemToUpdateId.push(item.itemObject.id);
                item.itemObject.sold = "Yes";
              }
            });

            return {
              itemNumber: index + 1,
              name: groupName,
              consolidated: true,
              totalPercentage: totalPercentage,
              items: groupData.items.map(item => ({
                id: item.id,
                qr: item.qr,
                serialNo: item.serialNo,
                weight: item.weight,
                metalRate: item.metalRate,
                makingCharges: item.makingCharges,
                melting: item.melting,
                metal: item.metal,
                huid: item.huid,
                stonePrice: item.stonePrice,
                // Include all weight fields
                grossWeight: item.grossWeight || item.weight,
                czWeight: item.czWeight || 0,
                pearlWeight: item.pearlWeight || 0,
                beltWeight: item.beltWeight || 0,
                otherWeight: item.otherWeight || 0,
                totalPercentage: totalPercentage || 0,
              })),
              totalWeight: totalWeightNum,
              totalFine: totalFine,
              metalRate: metalRateNum,
              wastage: wastageNum,
              purity: purityNum,
              stoneCharges: stoneCharges,
              huidCharges: huidCharges,
              totalAmount: finalAmount,
              calculationType: 'wastage',
              adjustWastage: true,
              gstAdjusted: showGST && gstMode === "adjust"
            };
          });
        } else {
          itemsToSave = items.map((item, index) => {
            const { id, itemObject, ...itemData } = item;
            let formattedName = itemData.name;
            let itemJson = itemObject || {};

            if (itemJson?.id) {
              itemToUpdateId.push(itemJson.id);
              itemJson.sold = "Yes";
            }

            let carat = item.ct || "";

            if (itemObject && (!carat || carat === "")) {
              if (itemObject?.melting === 92 || itemObject?.melting === 916) {
                carat = "22K";
              } else if (itemObject?.melting === 75 || itemObject?.melting === 750) {
                carat = "18K";
              } else if (itemObject?.melting === 58 || itemObject?.melting === 585) {
                carat = "14K";
              } else if (itemObject?.melting === 50 || itemObject?.melting === 500) {
                carat = "12K";
              }
              formattedName = `${itemObject.name || itemData.name} ${itemObject.huid ? `- ${itemObject.huid}` : ""}`;
            }

            return {
              itemNumber: index + 1,
              ...itemData,
              name: formattedName,
              ct: carat,
            };
          });
        }
      }

      // Prepare payment history (same as before)
      const initialPaymentHistory = [];
      const totalPaymentAmount = parseFloat(values.paymentDone) || 0;
      const formattedDate = `${date}/${month}/${year}`;

      if (totalPaymentAmount > 0) {
        const initialPaymentEntry = {
          id: `pay_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          date: formattedDate,
          amount: totalPaymentAmount,
          paymentModes: {
            cash: parseFloat(values.cashPayment) || 0,
            online: parseFloat(values.onlinePayment) || 0,
            cheque: parseFloat(values.chequePayment) || 0,
            other: parseFloat(values.otherPayment) || 0,
            advanceBreakup: advancePayments,
            finePayment: finePayment.enabled ? {
              weight: parseFloat(finePayment.weight) || 0,
              purity: parseFloat(finePayment.purity) || 0,
              actualFine: calculatedFinePayment
            } : null
          },
          note: `Initial payment on ${formattedDate}`,
          recordedBy: values.handledBy || "System"
        };
        initialPaymentHistory.push(initialPaymentEntry);
      }

      // Prepare transaction data
      const transactionData = {
        customer: customerId,
        customerName: values.customerName || "Walk-in Customer",
        date: formattedDate,
        transactionDetails: {
          type: transactionType,
          billNo: billNumberUsed,
          gstApplicable: showGST,
          gstMode: showGST ? gstMode : null,
          gstType: showGST ? gstType : 'cgst_sgst',
          originalBillAmount: parseFloat(calculations.grandTotal).toFixed(2),
          balanceDue: parseFloat(pendingAmount).toFixed(2),
          isFullyPaid: pendingAmount <= 0,
          paymentStatus: pendingAmount <= 0 ? "paid" : (totalPaymentAmount > 0 ? "partial" : "unpaid"),
          lastPaymentDate: totalPaymentAmount > 0 ? formattedDate : null,
          lastPaymentAmount: totalPaymentAmount > 0 ? totalPaymentAmount : 0
        },
        calculatedTotals: {
          subtotal: parseFloat(parseFloat(calculations.subtotal).toFixed(2)),
          totalItems: transactionType === "detailed" ?
            (itemConsolidationMode === 'consolidate' ?
              Object.keys(consolidatedItemGroups).length : items.length
            ).toString() : "1",
          totalFineWeight: parseFloat(calculations.totalFineWeight.toFixed(3)),
          finePaymentReceived: parseFloat(calculations.finePaymentReceived.toFixed(3)),
          fineBalance: parseFloat(calculations.fineBalance.toFixed(3))
        },
        paymentDetails: {
          cash: parseFloat(parseFloat(values.cashPayment).toFixed(2)) || 0,
          online: parseFloat(parseFloat(values.onlinePayment).toFixed(2)) || 0,
          cheque: parseFloat(parseFloat(values.chequePayment).toFixed(2)) || 0,
          other: parseFloat(parseFloat(values.otherPayment).toFixed(2)) || 0,
          advancePayment: {
            enabled: enableAdvancePayment,
            total: parseFloat(parseFloat(advanceTotal).toFixed(2)) || 0,
            breakup: advancePayments,
          },
          finePayment: finePayment.enabled ? {
            enabled: true,
            weight: parseFloat(finePayment.weight) || 0,
            purity: parseFloat(finePayment.purity) || 0,
            actualFine: calculatedFinePayment
          } : null,
          totalPaymentDone: totalPaymentAmount,
          discount: parseFloat(parseFloat(values.discount).toFixed(2)) || 0,
          paymentHistory: initialPaymentHistory
        },
        metaDetails: {
          handledBy: values.handledBy,
          gstNumber: values.gstNumber || "",
          panNumber: values.panNumber || "",
        },
        items: itemsToSave,
        totalAmount: transactionType === "total" ? parseFloat(values.totalAmount).toFixed(2) || 0 : 0,
        summary: {
          grandTotal: parseFloat(parseFloat(calculations.grandTotal).toFixed(2)) || 0,
          paymentDone: totalPaymentAmount,
          pendingAmount: parseFloat(parseFloat(pendingAmount).toFixed(2)) || 0,
          cgst: parseFloat(parseFloat(calculations.cgst).toFixed(2)) || 0,
          sgst: parseFloat(parseFloat(calculations.sgst).toFixed(2)) || 0,
          igst: parseFloat(parseFloat(calculations.igst).toFixed(2)) || 0,
        },
        consolidationMode: itemConsolidationMode,
        consolidatedItemGroups: itemConsolidationMode === 'consolidate' ? consolidatedItemGroups : null
      };

      // Save transaction (same as before)
      const savePromise = addCustomerTransactionAndUpdateStock(
        transactionData,
        itemToUpdateId,
      );

      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Request timeout")), 30000);
      });

      const saveResult = await Promise.race([savePromise, timeoutPromise]);

      if (saveResult?.success) {
        // Record advance payment usage if applicable
        if (selectedAdvanceUsage && customerId && saveResult?.data?.transaction?.id) {
          try {
            const billId = saveResult.data.transaction.id;
            const billNoUsed = transactionData.transactionDetails.billNo;

            const advanceResult = await applyAdvanceToBill(
              customerId,
              billId,
              billNoUsed,
              selectedAdvanceUsage.usageAmounts,
              selectedAdvanceUsage.totalAmount
            );

            if (advanceResult.success) {
              setSnackbar({
                open: true,
                message: `Advance payment of ₹${selectedAdvanceUsage.totalAmount.toFixed(2)} applied successfully!`,
                severity: 'success'
              });
            }
          } catch (advanceError) {
            console.error("Error recording advance usage:", advanceError);
          }
        }

        setIsTransactionSaved(true);
        setCustomAlert({
          open: true,
          title: "Transaction Successful",
          message: `Transaction added successfully! Bill No: ${billNumberUsed}`,
          severity: "success",
          onConfirm: () => {
            setCustomAlert({ ...customAlert, open: false });
          },
        });
      }

    } catch (error) {
      console.error("Failed to add transaction:", error);
      // Error handling (same as before)
      if (error.message === "Request timeout") {
        setCustomAlert({
          open: true,
          title: "Request Timeout",
          message: "The request timed out. Please check your network connection and try again. Transaction has not been saved.",
          severity: "error",
          onConfirm: () => setCustomAlert({ ...customAlert, open: false }),
        });
      } else {
        setCustomAlert({
          open: true,
          title: "Transaction Failed",
          message: `Failed to add transaction: ${error.message || "Unknown error"}. Please try again.`,
          severity: "error",
          onConfirm: () => setCustomAlert({ ...customAlert, open: false }),
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Desktop printing function
  // const handleDesktopPrint = useCallback((billHTML, styles) => {
  //   const printWindow = window.open('', '_blank');
  //   if (!printWindow) {
  //     alert('Please allow popups to print the bill');
  //     return;
  //   }

  //   printWindow.document.write(`
  //   <!DOCTYPE html>
  //   <html>
  //     <head>
  //       <title>Print Bill - ${showGST ? 'GST Invoice' : 'Cash Memo'}</title>
  //       <meta charset="UTF-8">
  //       <meta name="viewport" content="width=device-width, initial-scale=1.0">
  //       <style>
  //         ${styles}

  //         @media print {
  //           body {
  //             -webkit-print-color-adjust: exact !important;
  //             color-adjust: exact !important;
  //             print-color-adjust: exact !important;
  //           }

  //           table {
  //             page-break-inside: avoid !important;
  //             break-inside: avoid !important;
  //           }

  //           tr {
  //             page-break-inside: avoid !important;
  //             page-break-after: auto !important;
  //             break-inside: avoid !important;
  //           }

  //           thead {
  //             display: table-header-group !important;
  //           }

  //           tfoot {
  //             display: table-footer-group !important;
  //           }
  //         }

  //         .watermark {
  //           position: fixed !important;
  //           top: 50% !important;
  //           left: 50% !important;
  //           transform: translate(-50%, -50%) rotate(-45deg) !important;
  //           opacity: 0.1 !important;
  //           z-index: -1 !important;
  //           pointer-events: none !important;
  //         }

  //         .watermark img {
  //           width: 400px !important;
  //           height: 400px !important;
  //           object-fit: contain !important;
  //         }
  //       </style>
  //     </head>
  //     <body>
  //       ${billHTML}
  //       <script>
  //         window.onload = function() {
  //           setTimeout(function() {
  //             window.print();
  //             window.onafterprint = function() {
  //               setTimeout(() => window.close(), 100);
  //             };
  //           }, 500);
  //         };
  //       </script>
  //     </body>
  //   </html>
  // `);

  //   printWindow.document.close();
  // }, [showGST]);

  // Mobile-optimized printing function
  // const handleMobilePrint = useCallback((billHTML, styles) => {
  //   const iframe = document.createElement('iframe');
  //   iframe.style.position = 'absolute';
  //   iframe.style.width = '0';
  //   iframe.style.height = '0';
  //   iframe.style.border = 'none';
  //   iframe.style.visibility = 'hidden';

  //   document.body.appendChild(iframe);

  //   const iframeDoc = iframe.contentWindow.document;

  //   iframeDoc.write(`
  //   <!DOCTYPE html>
  //   <html>
  //     <head>
  //       <title>Print Bill - ${showGST ? 'GST Invoice' : 'Cash Memo'}</title>
  //       <meta charset="UTF-8">
  //       <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=yes">
  //       <style>
  //         ${styles}

  //         @media print {
  //           body {
  //             margin: 0;
  //             padding: 0;
  //             -webkit-print-color-adjust: exact !important;
  //             print-color-adjust: exact !important;
  //           }

  //           #billContent, #billContentWithoutGst {
  //             width: 100% !important;
  //             max-width: 100% !important;
  //             margin: 0 !important;
  //             padding: 0 !important;
  //             border: 2px solid #2c3e50 !important;
  //           }

  //           table {
  //             width: 100% !important;
  //             table-layout: fixed !important;
  //           }

  //           td, th {
  //             word-wrap: break-word !important;
  //             white-space: normal !important;
  //           }
  //         }

  //         .print-loading {
  //           position: fixed;
  //           top: 50%;
  //           left: 50%;
  //           transform: translate(-50%, -50%);
  //           background: #2c3e50;
  //           color: white;
  //           padding: 15px 30px;
  //           border-radius: 8px;
  //           font-family: Arial, sans-serif;
  //           z-index: 9999;
  //           text-align: center;
  //           box-shadow: 0 2px 10px rgba(0,0,0,0.2);
  //         }

  //         .print-loading:after {
  //           content: '';
  //           display: inline-block;
  //           width: 20px;
  //           height: 20px;
  //           margin-left: 10px;
  //           border: 3px solid #f3f3f3;
  //           border-top: 3px solid #d4af37;
  //           border-radius: 50%;
  //           animation: spin 1s linear infinite;
  //           vertical-align: middle;
  //         }

  //         @keyframes spin {
  //           0% { transform: rotate(0deg); }
  //           100% { transform: rotate(360deg); }
  //         }
  //       </style>
  //     </head>
  //     <body>
  //       <div class="print-loading">Preparing bill for printing...</div>
  //       ${billHTML}
  //       <script>
  //         window.onload = function() {
  //           const loadingEl = document.querySelector('.print-loading');
  //           if (loadingEl) {
  //             loadingEl.style.display = 'none';
  //           }

  //           setTimeout(function() {
  //             try {
  //               window.print();
  //             } catch(e) {
  //               console.error('Print error:', e);
  //               alert('Unable to print. Please try again or use a different browser.');
  //             }

  //             window.onafterprint = function() {
  //               setTimeout(function() {
  //                 window.parent.document.body.removeChild(window.frameElement);
  //               }, 100);
  //             };

  //             setTimeout(function() {
  //               if (window.parent && window.frameElement) {
  //                 window.parent.document.body.removeChild(window.frameElement);
  //               }
  //             }, 30000);
  //           }, 1000);
  //         };
  //       </script>
  //     </body>
  //   </html>
  // `);

  //   iframeDoc.close();

  //   setTimeout(() => {
  //     iframe.contentWindow.focus();
  //   }, 500);
  // }, [showGST]);

  // const handlePrintBill = useCallback(() => {
  //   if (!formik.values.customerName.trim()) {
  //     alert("Please select or enter a customer name");
  //     return;
  //   }

  //   if (!formik.values.handledBy.trim()) {
  //     alert("Please enter seller name");
  //     return;
  //   }

  //   if (transactionType === "total" && (!formik.values.totalAmount || parseFloat(formik.values.totalAmount) <= 0)) {
  //     alert("Please enter a valid total amount");
  //     return;
  //   }

  //   let billNo = currentBillNo || 1;
  //   if (isTransactionSaved && usedBillNo) {
  //     // If transaction is saved, use the bill number that was actually used
  //     billNo = usedBillNo;
  //   } else {
  //     // If not saved yet, use the current bill number (next available)
  //     billNo = currentBillNo || 1;
  //   }

  //   let d = new Date(formik.values.transactionDate);
  //   let month = "" + (d.getMonth() + 1);
  //   if (month.length === 1) month = "0" + month;
  //   let year = d.getFullYear();
  //   let date = "" + d.getDate();
  //   if (date.length === 1) date = "0" + date;

  //   const itemsForBill = transactionType === "detailed"
  //     ? items.map((item, index) => {
  //       const { id, itemObject, ...itemData } = item;
  //       let displayName = itemData.name;
  //       let carat = item.ct || "";
  //       if (itemObject && (!carat || carat === "")) {
  //         const itemObj = parseItemObject(itemObject);
  //         if (itemObj?.melting === 92 || itemObj?.melting === 916) {
  //           carat = "22K";
  //         } else if (itemObj?.melting === 75 || itemObj?.melting === 750) {
  //           carat = "18K";
  //         } else if (itemObj?.melting === 58 || itemObj?.melting === 585) {
  //           carat = "14K";
  //         } else if (itemObj?.melting === 50 || itemObj?.melting === 500) {
  //           carat = "12K";
  //         }
  //         displayName = `${itemObj.name || itemData.name} ${itemObj.huid ? `- ${itemObj.huid}` : ""}`;
  //       }
  //       return {
  //         ...itemData,
  //         name: displayName,
  //         ct: carat,
  //         itemObject: itemObject || null,
  //       };
  //     })
  //     : [];

  //   const advanceTotalForBill = enableAdvancePayment
  //     ? Object.values(advancePayments).reduce((sum, val) => sum + (parseFloat(val) || 0), 0)
  //     : 0;

  //   const transactionDataForBill = {
  //     transactionDetails: {
  //       type: transactionType,
  //       billNo,
  //       gstApplicable: showGST,
  //       gstMode: showGST ? gstMode : null,
  //       gstType: showGST ? gstType : 'cgst_sgst',
  //     },
  //     date: `'${date}/${month}/${year}`,
  //     calculatedTotals: {
  //       subtotal: calculations.subtotal,
  //     },
  //     paymentDetails: {
  //       cash: parseFloat(formik.values.cashPayment) || 0,
  //       online: parseFloat(formik.values.onlinePayment) || 0,
  //       cheque: parseFloat(formik.values.chequePayment) || 0,
  //       other: parseFloat(formik.values.otherPayment) || 0,
  //       advancePayment: {
  //         enabled: enableAdvancePayment,
  //         total: advanceTotalForBill,
  //         breakup: advancePayments,
  //       },
  //       discount: parseFloat(formik.values.discount) || 0,
  //     },
  //     metaDetails: {
  //       handledBy: formik.values.handledBy,
  //       gstNumber: formik.values.gstNumber || "",
  //       panNumber: formik.values.panNumber || "",
  //     },
  //     totalAmount: transactionType === "total" ? parseFloat(formik.values.totalAmount) || 0 : 0,
  //     summary: {
  //       grandTotal: calculations.grandTotal,
  //       paymentDone: parseFloat(formik.values.paymentDone) || 0,
  //       pendingAmount: pendingAmount > 0 ? pendingAmount : 0,
  //       cgst: calculations.cgst,
  //       sgst: calculations.sgst,
  //       igst: calculations.igst,
  //     },
  //   };

  //   const billData = {
  //     type: showGST ? 'gst' : 'non-gst',
  //     data: transactionDataForBill,
  //     items: itemsForBill,
  //     payments: transactionDataForBill.paymentDetails,
  //     customer: selectedCustomer || {
  //       name: formik.values.customerName,
  //       contact: formik.values.newCustomerContact || "",
  //       address: formik.values.newCustomerAddress || ""
  //     },
  //     advanceData: advancePayments
  //   };

  //   const billHTML = generateBillHTML(billData);
  //   const styles = getBillStyles();

  //   const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  //   if (isMobile) {
  //     handleMobilePrint(billHTML, styles);
  //   } else {
  //     handleDesktopPrint(billHTML, styles);
  //   }
  // }, [formik.values, selectedCustomer, showGST, gstMode, gstType, transactionType, items, enableAdvancePayment, advancePayments, calculations, pendingAmount, handleDesktopPrint, handleMobilePrint, currentBillNo, isTransactionSaved, usedBillNo]);

  const resetForm = useCallback(() => {
    formik.resetForm();
    setIsTransactionSaved(false);
    // setUsedBillNo(null);
    setSelectedCustomer(null);
    setCustomerQuery("");
    setIsNewCustomer(false);
    setShowNewCustomerFields(false);
    setItems([]);
    setTransactionType("detailed");
    setShowGST(false);
    setGstMode("add");
    setGstType("cgst_sgst");
    setEnableAdvancePayment(false);
    setPaymentModes({
      cash: false,
      online: false,
      oldGold: false,
      oldSilver: false,
      other: false,
    });
    setAdvancePayments({});
    setOtherOptions([]);
    setManualItemName("");
    setManualSerial("");
    setFinePayment({
      enabled: false,
      weight: '',
      purity: '',
      actualFine: 0
    });
    setCalculatedFinePayment(0);
    setConsolidatedItemGroups({});
    fetchLastBillNumbers();
    hasShownAdvanceDialogRef.current = false;
  }, [formik, fetchLastBillNumbers]);

  const renderItemRows = useMemo(
    () =>
      items.map((item) => {
        const displayWeight = item.weight || 0;
        const displayMetalRate = item.metalRate || '';
        const displayWastage = item.wastage || 0;
        const itemTotal = typeof item.total === 'number' ? item.total : parseFloat(item.total) || 0;
        const hasStockItem = !!item.itemObject;
        // const isWeightAdjusted = item.weightAdjusted || item.adjustmentApplied;
        const needsAdjustment =
          pendingWeightAdjustments?.[item?.id]?.needsAdjustment;

        // Use item.ct for user-editable carat display (not item.carat)
        // Priority: user-edited ct > auto-calculated from melting
        let displayCarat = item.ct || "";
        let displayName = item.name;

        // Check if we have an itemObject to parse for melting value
        if (item.itemObject) {
          const itemObj = parseItemObject(item.itemObject);

          // Only auto-calculate carat if there's NO ct value already set OR if ct is empty string
          if ((!item.ct || item.ct === "") && itemObj?.melting) {
            // Calculate carat from melting
            const melting = parseFloat(itemObj.melting);
            if (melting === 92 || melting === 916) {
              displayCarat = "22K";
            } else if (melting === 75 || melting === 750) {
              displayCarat = "18K";
            } else if (melting === 58 || melting === 585) {
              displayCarat = "14K";
            } else if (melting === 50 || melting === 500) {
              displayCarat = "12K";
            } else if (melting === 999) {
              displayCarat = "24K";
            }

            // Update the item's ct field if it's empty (not carat)
            if (!item.ct || item.ct === "") {
              updateItemField(item.id, "ct", displayCarat);
            }
          }

          // Set display name
          displayName = `${itemObj.name || item.name} ${itemObj.huid ? `- ${itemObj.huid}` : ""}`;
        }

        return (
          <TableRow
            key={item.id}
            sx={{
              "&:last-child td, &:last-child th": { border: 0 },
              display: { xs: 'block', md: 'table-row' },
            }}
          >
            {/* Item Name */}
            <TableCell
              sx={{
                display: { xs: 'flex', md: 'table-cell' },
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                py: { xs: 1.5, md: 1 },
                '&:before': {
                  content: { xs: '"Item:"', md: 'none' },
                  fontWeight: 600,
                  minWidth: '80px',
                  color: isDark ? '#94a3b8' : '#64748b',
                }
              }}
            >
              <TextField
                value={displayName}
                onChange={(e) =>
                  updateItemField(item.id, "name", e.target.value)
                }
                fullWidth
                variant="standard"
                sx={{
                  flex: 1,
                  minWidth: { xs: '120px', md: 'auto' },
                  '& .MuiInput-root': {
                    '&::before': {
                      borderBottom: `1px solid ${alpha(
                        isDark ? theme.palette.divider : '#e2e8f0',
                        isDark ? 0.2 : 0.3
                      )}`,
                    },
                    '&:hover:not(.Mui-disabled):before': {
                      borderBottom: `2px solid ${alpha(
                        isDark ? '#60a5fa' : '#3b82f6',
                        isDark ? 0.5 : 0.7
                      )}`,
                    },
                  }
                }}
              />
            </TableCell>

            {/* Carat */}
            <TableCell
              sx={{
                display: { xs: 'flex', md: 'table-cell' },
                justifyContent: 'space-between',
                alignItems: 'center',
                py: { xs: 1, md: 1 },
                '&:before': {
                  content: { xs: '"Ct.:"', md: 'none' },
                  fontWeight: 600,
                  minWidth: '80px',
                  color: isDark ? '#94a3b8' : '#64748b',
                }
              }}
            >
              <TextField
                value={displayCarat}
                onChange={(e) => {
                  updateItemField(item.id, "ct", e.target.value);
                }}
                fullWidth
                size="small"
                variant="standard"
                sx={{
                  flex: 1,
                  minWidth: { xs: '80px', md: 'auto' },
                  '& .MuiInput-root': {
                    '&::before': {
                      borderBottom: `1px solid ${alpha(
                        isDark ? theme.palette.divider : '#e2e8f0',
                        isDark ? 0.2 : 0.3
                      )}`,
                    },
                    '&:hover:not(.Mui-disabled):before': {
                      borderBottom: `2px solid ${alpha(
                        isDark ? '#60a5fa' : '#3b82f6',
                        isDark ? 0.5 : 0.7
                      )}`,
                    },
                  }
                }}
              />
            </TableCell>

            {/* Weight */}
            <TableCell
              sx={{
                display: { xs: 'flex', md: 'table-cell' },
                justifyContent: 'space-between',
                alignItems: 'center',
                py: { xs: 1, md: 1 },
                '&:before': {
                  content: { xs: '"Weight:"', md: 'none' },
                  fontWeight: 600,
                  minWidth: '80px',
                  color: isDark ? '#94a3b8' : '#64748b',
                }
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: 1,
                  gap: 0.5,
                  flexWrap: 'wrap',
                }}
              >
                <TextField
                  type="text"
                  value={displayWeight}
                  onClick={() => hasStockItem && openWeightEditDialog(item)}
                  fullWidth
                  size="small"
                  variant="standard"
                  inputProps={{
                    step: "0.01",
                    readOnly: hasStockItem,
                    style: { cursor: hasStockItem ? "pointer" : "text" },
                  }}
                  sx={{
                    flex: 1,
                    minWidth: { xs: '80px', md: 'auto' },
                    "& .MuiInput-root": {
                      "&::before": {
                        borderBottom: `1px solid ${alpha(
                          isDark ? theme.palette.divider : "#e2e8f0",
                          isDark ? 0.2 : 0.3,
                        )}`,
                      },
                    },
                    "& .MuiInput-input": {
                      color:
                        item.weightAdjusted && !needsAdjustment
                          ? theme.palette.success.main
                          : needsAdjustment
                            ? theme.palette.warning.main
                            : "inherit",
                      fontWeight: needsAdjustment ? 600 : "normal",
                      cursor: hasStockItem ? "pointer" : "text",
                    },
                    "& .MuiInput-input:hover": {
                      cursor: hasStockItem ? "pointer" : "text",
                    },
                  }}
                />
                {hasStockItem && (
                  <IconButton
                    size="small"
                    onClick={() => openWeightEditDialog(item)}
                    sx={{ p: 0.5 }}
                    title="Click to edit weight"
                  >
                    <EditIcon fontSize="small" sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
                {item.weightAdjusted && !needsAdjustment && (
                  <Chip
                    label="Adjusted"
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ height: 20, fontSize: "0.6rem", fontWeight: 600 }}
                  />
                )}
                {needsAdjustment && (
                  <Chip
                    label="Pending"
                    size="small"
                    color="warning"
                    sx={{ height: 20, fontSize: "0.6rem", fontWeight: 600 }}
                  />
                )}
              </Box>
            </TableCell>

            {/* Rate */}
            <TableCell
              sx={{
                display: { xs: 'flex', md: 'table-cell' },
                justifyContent: 'space-between',
                alignItems: 'center',
                py: { xs: 1, md: 1 },
                '&:before': {
                  content: { xs: '"Rate:"', md: 'none' },
                  fontWeight: 600,
                  minWidth: '80px',
                  color: isDark ? '#94a3b8' : '#64748b',
                }
              }}
            >
              <TextField
                type="text"
                value={displayMetalRate}
                onChange={(e) =>
                  updateItemField(item.id, "metalRate", e.target.value)
                }
                fullWidth
                size="small"
                variant="standard"
                inputProps={{ step: "0.01" }}
                sx={{
                  flex: 1,
                  minWidth: { xs: '100px', md: 'auto' },
                  '& .MuiInput-root': {
                    '&::before': {
                      borderBottom: `1px solid ${alpha(
                        isDark ? theme.palette.divider : '#e2e8f0',
                        isDark ? 0.2 : 0.3
                      )}`,
                    },
                  }
                }}
              />
            </TableCell>

            {/* Wastage */}
            <TableCell
              sx={{
                display: { xs: 'flex', md: 'table-cell' },
                justifyContent: 'space-between',
                alignItems: 'center',
                py: { xs: 1, md: 1 },
                '&:before': {
                  content: { xs: '"Wastage:"', md: 'none' },
                  fontWeight: 600,
                  minWidth: '80px',
                  color: isDark ? '#94a3b8' : '#64748b',
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, flexWrap: 'wrap' }}>
                <TextField
                  type="text"
                  value={displayWastage}
                  onChange={(e) =>
                    updateItemField(item.id, "wastage", e.target.value)
                  }
                  fullWidth
                  size="small"
                  variant="standard"
                  inputProps={{ step: "0.01" }}
                  disabled={!item.adjustWastage}
                  sx={{
                    flex: 1,
                    minWidth: { xs: '70px', md: 'auto' },
                    '& .MuiInput-root': {
                      '&::before': {
                        borderBottom: `1px solid ${alpha(
                          isDark ? theme.palette.divider : '#e2e8f0',
                          isDark ? 0.2 : 0.3
                        )}`,
                      },
                    },
                    '& .MuiInput-input': {
                      color: !item.adjustWastage ? alpha(isDark ? '#94a3b8' : '#64748b', 0.7) : 'inherit',
                    }
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={item.adjustWastage !== false}
                      onChange={(e) => {
                        const newAdjustWastage = e.target.checked;
                        setItems(prev =>
                          prev.map(i =>
                            i.id === item.id
                              ? {
                                ...i,
                                adjustWastage: newAdjustWastage,
                                wastage: newAdjustWastage ? i.wastage : 0
                              }
                              : i
                          )
                        );
                      }}
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                      Adj
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />
              </Box>
            </TableCell>

            {/* Total */}
            <TableCell
              sx={{
                display: { xs: 'flex', md: 'table-cell' },
                justifyContent: 'space-between',
                alignItems: 'center',
                py: { xs: 1, md: 1 },
                '&:before': {
                  content: { xs: '"Total:"', md: 'none' },
                  fontWeight: 600,
                  minWidth: '80px',
                  color: isDark ? '#94a3b8' : '#64748b',
                }
              }}
            >
              <TextField
                type="text"
                value={itemTotal.toFixed(2)}
                onChange={(e) => updateItemField(item.id, "total", e.target.value)}
                fullWidth
                size="small"
                variant="standard"
                inputProps={{ step: "0.01" }}
                sx={{
                  flex: 1,
                  minWidth: { xs: '100px', md: 'auto' },
                  '& .MuiInput-root': {
                    '&::before': {
                      borderBottom: `1px solid ${alpha(
                        isDark ? theme.palette.divider : '#e2e8f0',
                        isDark ? 0.2 : 0.3
                      )}`,
                    },
                    '&:hover:not(.Mui-disabled):before': {
                      borderBottom: `2px solid ${alpha(
                        isDark ? '#60a5fa' : '#3b82f6',
                        isDark ? 0.5 : 0.7
                      )}`,
                    },
                  },
                  '& .MuiInput-input': {
                    fontWeight: 600,
                    color: isDark ? '#e2e8f0' : '#1e293b',
                  }
                }}
              />
            </TableCell>

            {/* Actions */}
            <TableCell
              sx={{
                display: { xs: 'flex', md: 'table-cell' },
                justifyContent: 'space-between',
                alignItems: 'center',
                py: { xs: 1.5, md: 1 },
                '&:before': {
                  content: { xs: '"Actions:"', md: 'none' },
                  fontWeight: 600,
                  minWidth: '80px',
                  color: isDark ? '#94a3b8' : '#64748b',
                }
              }}
            >
              <Stack direction="row" spacing={1}>
                {hasStockItem && (
                  <IconButton
                    onClick={() => openWeightEditDialog(item)}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      backgroundColor: needsAdjustment
                        ? alpha(theme.palette.warning.main, isDark ? 0.3 : 0.2)
                        : alpha(theme.palette.info.main, isDark ? 0.2 : 0.1),
                      color: needsAdjustment
                        ? theme.palette.warning.main
                        : theme.palette.info.main,
                      border: `1px solid ${alpha(
                        needsAdjustment
                          ? theme.palette.warning.main
                          : theme.palette.info.main,
                        isDark ? 0.3 : 0.2,
                      )}`,
                      "&:hover": {
                        backgroundColor: alpha(
                          needsAdjustment
                            ? theme.palette.warning.main
                            : theme.palette.info.main,
                          isDark ? 0.4 : 0.25,
                        ),
                      },
                    }}
                    title={
                      needsAdjustment
                        ? "Review weight adjustment"
                        : "Edit weight"
                    }
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton
                  onClick={() => removeItemRow(item.id)}
                  size="small"
                  sx={{
                    borderRadius: 2,
                    backgroundColor: alpha(
                      theme.palette.error.main,
                      isDark ? 0.2 : 0.1,
                    ),
                    color: theme.palette.error.main,
                    border: `1px solid ${alpha(theme.palette.error.main, isDark ? 0.3 : 0.2)}`,
                    "&:hover": {
                      backgroundColor: alpha(
                        theme.palette.error.main,
                        isDark ? 0.3 : 0.15,
                      ),
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </TableCell>
          </TableRow>
        );
      }),
    [
      items,
      updateItemField,
      removeItemRow,
      isDark,
      theme,
      pendingWeightAdjustments,
      openWeightEditDialog,
      setItems
    ],
  );

  // Function to download Excel file
  const handleDownloadExcel = useCallback(() => {
    if (items.length === 0 && Object.keys(consolidatedItemGroups).length === 0 && transactionType !== "total") {
      alert("No items to export");
      return;
    }

    // Import xlsx dynamically
    import('xlsx').then(XLSX => {
      // Prepare data for Excel
      const excelData = [];

      // Add header row
      excelData.push([
        'S.No.',
        'Serial No.',
        'Item Name',
        'Gross Wt (g)',
        'CZ (g)',
        'Pearl (g)',
        'Belt (g)',
        'Other (g)',
        'Net Wt (g)',
        'Stone Cost (₹)',
        'HUID (₹)',
        'Total %',
        'Metal Rate (₹/g)',
        'Fine Wt (g)',
        'Fine Amount (₹)',
        'Total Amount (₹)'
      ]);

      // Collect all items
      let allItems = [];
      if (itemConsolidationMode === 'consolidate') {
        Object.values(consolidatedItemGroups).forEach(group => {
          group.items.forEach(item => {
            const totalPercentage = parseFloat(group.totalPercentage) || 0;
            const totalFine = (item.weight || 0) * (totalPercentage / 100);
            const metalRateNum = parseFloat(group.metalRate) || 0;
            let amountFromMetal = 0;
            if (metalRateNum > 0 && totalFine > 0) {
              amountFromMetal = metalRateNum * totalFine;
            }
            const stonePrice = parseFloat(item.stonePrice) || parseFloat(group.stoneCharges) || 0;
            const huidCharge = parseFloat(item.huidCost) || 0;
            const totalAmount = amountFromMetal + stonePrice + huidCharge;

            allItems.push({
              serialNo: item.serialNo || '-',
              name: item.name || group.name,
              grossWeight: item.grossWeight || 0,
              czWeight: item.czWeight || 0,
              pearlWeight: item.pearlWeight || 0,
              beltWeight: item.beltWeight || 0,
              otherWeight: item.otherWeight || 0,
              netWeight: item.weight || 0,
              stonePrice: stonePrice,
              huidCharge: huidCharge,
              totalPercentage: totalPercentage,
              metalRate: metalRateNum,
              totalFine: totalFine,
              amountFromMetal: amountFromMetal,
              totalAmount: totalAmount
            });
          });
        });
      } else {
        allItems = items.map(item => {
          const weight = parseFloat(item.weight) || 0;
          const metalRateNum = parseFloat(item.metalRate) || 0;
          const wastageNum = parseFloat(item.wastage) || 0;
          const purityNum = parseFloat(item.melting) || 75;
          const totalPercentage = purityNum + wastageNum;
          const totalFine = weight * (totalPercentage / 100);
          let amountFromMetal = 0;
          if (metalRateNum > 0 && totalFine > 0) {
            amountFromMetal = metalRateNum * totalFine;
          }
          const stonePrice = parseFloat(item.stonePrice) || 0;
          const huidCharge = parseFloat(item.huidCost) || 0;
          const totalAmount = amountFromMetal + stonePrice + huidCharge;

          return {
            serialNo: item.serialNo || item.itemObject?.serialNo || '-',
            name: item.name,
            grossWeight: item.grossWeight || item.weight || 0,
            czWeight: item.czWeight || 0,
            pearlWeight: item.pearlWeight || 0,
            beltWeight: item.beltWeight || 0,
            otherWeight: item.otherWeight || 0,
            netWeight: weight,
            stonePrice: stonePrice,
            huidCharge: huidCharge,
            totalPercentage: totalPercentage,
            metalRate: metalRateNum,
            totalFine: totalFine,
            amountFromMetal: amountFromMetal,
            totalAmount: totalAmount
          };
        });
      }

      // Add item rows
      allItems.forEach((item, idx) => {
        excelData.push([
          idx + 1,
          item.serialNo,
          item.name,
          item.grossWeight.toFixed(3),
          item.czWeight.toFixed(3),
          item.pearlWeight.toFixed(3),
          item.beltWeight.toFixed(3),
          item.otherWeight.toFixed(3),
          item.netWeight.toFixed(3),
          item.stonePrice.toFixed(2),
          item.huidCharge.toFixed(2),
          item.totalPercentage.toFixed(1) + '%',
          item.metalRate.toFixed(2),
          item.totalFine.toFixed(3),
          item.amountFromMetal.toFixed(2),
          item.totalAmount.toFixed(2)
        ]);
      });

      // Calculate totals
      const totals = {
        totalGrossWeight: allItems.reduce((sum, i) => sum + i.grossWeight, 0),
        totalCZWeight: allItems.reduce((sum, i) => sum + i.czWeight, 0),
        totalPearlWeight: allItems.reduce((sum, i) => sum + i.pearlWeight, 0),
        totalBeltWeight: allItems.reduce((sum, i) => sum + i.beltWeight, 0),
        totalOtherWeight: allItems.reduce((sum, i) => sum + i.otherWeight, 0),
        totalNetWeight: allItems.reduce((sum, i) => sum + i.netWeight, 0),
        totalStoneCost: allItems.reduce((sum, i) => sum + i.stonePrice, 0),
        totalHuidCharge: allItems.reduce((sum, i) => sum + i.huidCharge, 0),
        totalFine: allItems.reduce((sum, i) => sum + i.totalFine, 0),
        totalAmountFromMetal: allItems.reduce((sum, i) => sum + i.amountFromMetal, 0),
        totalAmount: allItems.reduce((sum, i) => sum + i.totalAmount, 0)
      };

      // Add totals row
      excelData.push([
        'TOTALS',
        '',
        '',
        totals.totalGrossWeight.toFixed(3),
        totals.totalCZWeight.toFixed(3),
        totals.totalPearlWeight.toFixed(3),
        totals.totalBeltWeight.toFixed(3),
        totals.totalOtherWeight.toFixed(3),
        totals.totalNetWeight.toFixed(3),
        totals.totalStoneCost.toFixed(2),
        totals.totalHuidCharge.toFixed(2),
        '',
        '',
        totals.totalFine.toFixed(3),
        totals.totalAmountFromMetal.toFixed(2),
        totals.totalAmount.toFixed(2)
      ]);

      // Add empty row
      excelData.push([]);

      // Add summary information
      excelData.push(['TRANSACTION SUMMARY']);
      excelData.push(['Transaction Date:', format(formik.values.transactionDate, 'dd/MM/yyyy')]);
      if (showGST) {
        excelData.push(['GST:', gstType === 'igst' ? `IGST (3%) - ₹${calculations.igst.toFixed(2)}` : `CGST (1.5%) - ₹${calculations.cgst.toFixed(2)}, SGST (1.5%) - ₹${calculations.sgst.toFixed(2)}`]);
      }
      excelData.push(['Grand Total:', `₹${calculations.grandTotal.toFixed(2)}`]);

      // Create worksheet and workbook
      const ws = XLSX.utils.aoa_to_sheet(excelData);

      // Set column widths
      ws['!cols'] = [
        { wch: 8 },   // S.No.
        { wch: 12 },  // Serial No.
        { wch: 25 },  // Item Name
        { wch: 12 },  // Gross Wt
        { wch: 10 },  // CZ
        { wch: 10 },  // Pearl
        { wch: 10 },  // Belt
        { wch: 10 },  // Other
        { wch: 12 },  // Net Wt
        { wch: 12 },  // Stone Cost
        { wch: 10 },  // HUID
        { wch: 10 },  // Total %
        { wch: 14 },  // Metal Rate
        { wch: 12 },  // Fine Wt
        { wch: 14 },  // Fine Amount
        { wch: 14 }   // Total Amount
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Bill Details');

      // Generate filename with current date and time
      const fileName = `Bill_Details_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`;

      // Download file
      XLSX.writeFile(wb, fileName);

      // Show success message
      setSnackbar({
        open: true,
        message: "Excel file downloaded successfully!",
        severity: "success"
      });
    }).catch(error => {
      console.error("Error loading xlsx library:", error);
      setSnackbar({
        open: true,
        message: "Failed to generate Excel file. Please try again.",
        severity: "error"
      });
    });
  }, [items, consolidatedItemGroups, itemConsolidationMode, transactionType, formik.values, showGST, gstType, calculations]);

  const renderCustomerSuggestions = useMemo(
    () =>
      customerSuggestions.map((customer) => (
        <Box
          key={customer.id}
          sx={{
            p: 1.5,
            backgroundColor: alpha(
              isDark ? theme.palette.background.paper : '#ffffff',
              isDark ? 0.3 : 0.6
            ),
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(
              isDark ? theme.palette.divider : '#e2e8f0',
              isDark ? 0.2 : 0.3
            )}`,
            borderRadius: 1,
            mb: 1,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: alpha(
                isDark ? theme.palette.background.paper : '#ffffff',
                isDark ? 0.5 : 0.8
              ),
              borderColor: alpha(
                isDark ? '#60a5fa' : '#3b82f6',
                isDark ? 0.4 : 0.5
              ),
              transform: 'translateX(2px)',
            },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
          onClick={() => handleCustomerSelect(customer)}
        >
          {customer.isNew ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: isDark ? '#60a5fa' : '#3b82f6',
              }}
            >
              <AddIcon sx={{ mr: 1, fontSize: 16 }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Add "{customerQuery}" as new customer
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
              >
                <Person
                  sx={{ fontSize: 16, color: isDark ? "#cbd5e1" : "#64748b" }}
                />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {customer.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: isDark ? "#94a3b8" : "#94a3b8" }}
                >
                  ({customer.contact})
                </Typography>
              </Box>
              {(customer.gstNo || customer.panNo) && (
                <Box sx={{ display: "flex", gap: 1, ml: 3 }}>
                  {customer.gstNo && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: isDark ? "#60a5fa" : "#3b82f6",
                        fontSize: "0.65rem",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <AccountBalanceIcon sx={{ fontSize: 12 }} />
                      GST: {customer.gstNo}
                    </Typography>
                  )}
                  {customer.panNo && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: isDark ? "#94a3b8" : "#64748b",
                        fontSize: "0.65rem",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <InfoIcon sx={{ fontSize: 12 }} />
                      PAN: {customer.panNo}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>
      )),
    [customerSuggestions, customerQuery, handleCustomerSelect, isDark, theme]
  );

  // Show skeleton during initial loading OR actual data loading
  if (initialLoading || isLoading) {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ minHeight: "100vh", py: 3 }}>
          <Box sx={{ maxWidth: "1400px", mx: "auto", px: 2 }}>
            {/* HEADER SKELETON */}
            <Stack direction="row" alignItems="center" spacing={2} mb={4}>
              <Skeleton variant="text" width={300} height={48} />
            </Stack>

            {/* MAIN 3-COLUMN GRID SKELETON */}
            <Grid container spacing={3} alignItems="flex-start">
              {/* LEFT COLUMN SKELETON */}
              <Grid item size={{ xs: 12, md: 12 }}>
                <CustomerInfoSkeleton isDark={isDark} />
              </Grid>

              {/* CENTER COLUMN SKELETON */}
              <Grid item size={{ xs: 12, md: 12 }}>
                <Stack spacing={3}>
                  <ItemsSectionSkeleton isDark={isDark} />
                  <GstSectionSkeleton isDark={isDark} />
                  <PaymentSectionSkeleton isDark={isDark} />
                </Stack>
              </Grid>

              {/* RIGHT COLUMN SKELETON */}
              <Grid item size={{ xs: 12, md: 12 }}>
                <Stack spacing={3} sx={{ position: "sticky", top: 24 }}>
                  <SummarySkeleton isDark={isDark} />
                  <ActionButtonsSkeleton isDark={isDark} />
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          minHeight: "100vh",
          py: 3,
        }}
      >
        <ManualItemDialog
          open={manualItemDialogOpen}
          onClose={() => setManualItemDialogOpen(false)}
          onSubmit={handleAddManualItem}
          manualItemDetails={manualItemDetails}
          setManualItemDetails={setManualItemDetails}
        />

        <Box
          sx={{
            maxWidth: "1400px",
            mx: "auto",
            px: 2,
          }}
        >
          {/* HEADER */}
          <Stack direction="row" alignItems="center" spacing={2} mb={4}>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                background: isDark
                  ? 'linear-gradient(135deg, #93C5FD 0%, #60A5FA 100%)'
                  : 'linear-gradient(135deg, #1E293B 0%, #334155 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.02em',
              }}
            >
              New Customer Transaction
            </Typography>
          </Stack>

          {/* MAIN 3-COLUMN GRID */}
          <Grid container spacing={3} alignItems="flex-start">
            {/* LEFT COLUMN — CUSTOMER INFORMATION PANEL */}
            <Grid item size={{ xs: 12, md: 12 }}>
              <Card
                sx={{
                  p: 3,
                  borderRadius: 3,
                  backdropFilter: 'blur(20px)',
                  backgroundColor: alpha(
                    isDark ? theme.palette.background.paper : '#ffffff',
                    isDark ? 0.3 : 0.6
                  ),
                  border: `1px solid ${alpha(
                    isDark ? theme.palette.divider : '#e2e8f0',
                    isDark ? 0.2 : 0.3
                  )}`,
                  boxShadow: theme.shadows[4],
                }}
              >
                {/* PANEL HEADER */}
                <Stack direction="row" alignItems="center" spacing={1} mb={3}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: isDark
                        ? 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)'
                        : 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '1rem',
                    }}
                  >
                    1
                  </Box>

                  <Typography variant="h6" sx={{
                    fontWeight: 700,
                    color: isDark ? '#e2e8f0' : '#1e293b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    <Person fontSize="small" />
                    Customer Information
                  </Typography>
                </Stack>

                {/* SEARCH CUSTOMER */}
                <Stack spacing={2}>
                  <TextField
                    label="Search Customer"
                    placeholder="Type name or contact..."
                    value={customerQuery}
                    onChange={(e) => setCustomerQuery(e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="medium"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backdropFilter: 'blur(10px)',
                        backgroundColor: alpha(
                          isDark ? theme.palette.background.paper : '#ffffff',
                          isDark ? 0.2 : 0.7
                        ),
                        '&:hover': {
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.3 : 0.8
                          ),
                        },
                        '&.Mui-focused': {
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.3 : 0.8
                          ),
                        }
                      },
                      '& .MuiInputLabel-root': {
                        color: isDark ? '#cbd5e1' : '#64748b',
                      },
                      '& .MuiOutlinedInput-input': {
                        color: isDark ? '#e2e8f0' : '#1e293b',
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: isDark ? '#cbd5e1' : '#64748b' }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* SUGGESTIONS DROPDOWN */}
                  {customerSuggestions.length > 0 && (
                    <Paper
                      elevation={0}
                      sx={{
                        mt: 1,
                        borderRadius: 2,
                        backdropFilter: 'blur(20px)',
                        backgroundColor: alpha(
                          isDark ? theme.palette.background.paper : '#ffffff',
                          isDark ? 0.3 : 0.85
                        ),
                        border: `1px solid ${alpha(
                          isDark ? theme.palette.divider : '#e2e8f0',
                          isDark ? 0.2 : 0.3
                        )}`,
                        boxShadow: theme.shadows[4],
                        overflow: 'hidden',
                        maxHeight: customerSuggestions.length > 5 ? '260px' : 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      {/* DROPDOWN HEADER */}
                      <Box
                        sx={{
                          p: 1.5,
                          borderBottom: `1px solid ${alpha(
                            isDark ? theme.palette.divider : '#e2e8f0',
                            isDark ? 0.1 : 0.2
                          )}`,
                          backgroundColor: alpha(
                            isDark ? '#1e293b' : '#f8fafc',
                            isDark ? 0.4 : 0.5
                          ),
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: isDark ? '#cbd5e1' : '#475569',
                            fontSize: '0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <SearchIcon fontSize="inherit" />
                          {customerSuggestions.length} suggestion{customerSuggestions.length !== 1 ? 's' : ''} found
                        </Typography>
                      </Box>

                      {/* SCROLLABLE SUGGESTIONS LIST */}
                      <Box
                        sx={{
                          overflowY: 'auto',
                          maxHeight: '210px',
                          '&::-webkit-scrollbar': {
                            width: '6px',
                          },
                          '&::-webkit-scrollbar-track': {
                            backgroundColor: alpha(
                              isDark ? '#1e293b' : '#f1f5f9',
                              isDark ? 0.3 : 0.5
                            ),
                            borderRadius: '3px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: alpha(
                              isDark ? '#60a5fa' : '#3b82f6',
                              isDark ? 0.4 : 0.5
                            ),
                            borderRadius: '3px',
                            '&:hover': {
                              backgroundColor: alpha(
                                isDark ? '#3b82f6' : '#2563eb',
                                isDark ? 0.6 : 0.7
                              ),
                            },
                          },
                        }}
                      >
                        {renderCustomerSuggestions}
                      </Box>

                      {/* SHOW MORE INDICATOR */}
                      {customerSuggestions.length > 5 && (
                        <Box
                          sx={{
                            p: 1,
                            borderTop: `1px solid ${alpha(
                              isDark ? theme.palette.divider : '#e2e8f0',
                              isDark ? 0.1 : 0.2
                            )}`,
                            textAlign: 'center',
                            backgroundColor: alpha(
                              isDark ? '#1e293b' : '#f8fafc',
                              isDark ? 0.4 : 0.5
                            ),
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDark ? '#94a3b8' : '#64748b',
                              fontSize: '0.7rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 0.5,
                            }}
                          >
                            <span>Scroll to see {customerSuggestions.length - 5} more</span>
                            <span style={{ transform: 'rotate(90deg)' }}>›</span>
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  )}

                  {/* NEW CUSTOMER INFO ALERT */}
                  {isNewCustomer && !selectedCustomer && customerQuery.trim() && (
                    <Alert
                      severity="info"
                      icon={<InfoIcon fontSize="small" />}
                      sx={{
                        borderRadius: 2,
                        alignItems: 'center',
                        py: 1,
                        backdropFilter: 'blur(10px)',
                        backgroundColor: alpha(
                          isDark ? theme.palette.info.main : theme.palette.info.main,
                          isDark ? 0.2 : 0.1
                        ),
                        border: `1px solid ${alpha(
                          isDark ? theme.palette.info.main : theme.palette.info.main,
                          isDark ? 0.3 : 0.2
                        )}`,
                        '& .MuiAlert-message': {
                          flex: 1,
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: isDark ? '#e2e8f0' : '#1e293b' }}>
                          Customer not found. Add "{customerQuery}" as new customer.
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            formik.setFieldValue("customerName", customerQuery);
                            formik.setFieldValue("isNewCustomer", true);
                            setShowNewCustomerFields(true);
                            setIsNewCustomer(true);
                            setSelectedCustomer(null);
                            setCustomerSuggestions([]);
                          }}
                          sx={{
                            ml: 2,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            py: 0.25,
                            backdropFilter: 'blur(10px)',
                            backgroundColor: alpha(
                              isDark ? theme.palette.background.paper : '#ffffff',
                              isDark ? 0.3 : 0.8
                            ),
                            borderColor: alpha(
                              isDark ? '#60a5fa' : '#3b82f6',
                              isDark ? 0.4 : 0.5
                            ),
                            color: isDark ? '#60a5fa' : '#3b82f6',
                            '&:hover': {
                              backgroundColor: alpha(
                                isDark ? theme.palette.background.paper : '#ffffff',
                                isDark ? 0.5 : 0.9
                              ),
                              borderColor: alpha(
                                isDark ? '#60a5fa' : '#3b82f6',
                                isDark ? 0.6 : 0.7
                              ),
                            }
                          }}
                        >
                          Add Now
                        </Button>
                      </Box>
                    </Alert>
                  )}
                </Stack>

                {/* SELECTED CUSTOMER BANNER */}
                {selectedCustomer && (
                  <Paper
                    sx={{
                      mt: 3,
                      p: 2.5,
                      borderRadius: 2,
                      backdropFilter: 'blur(20px)',
                      background: isDark
                        ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.1) 100%)'
                        : 'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(5, 150, 105, 0.08) 100%)',
                      border: `1px solid ${alpha(
                        isDark ? '#10b981' : '#059669',
                        isDark ? 0.25 : 0.2
                      )}`,
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '60px',
                        height: '60px',
                        background: isDark
                          ? 'linear-gradient(135deg, transparent 50%, rgba(16, 185, 129, 0.1) 50%)'
                          : 'linear-gradient(135deg, transparent 50%, rgba(5, 150, 105, 0.1) 50%)',
                      }}
                    />

                    <Stack direction="row" spacing={2} alignItems="center" position="relative" zIndex={1}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: isDark
                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                            : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          color: 'white',
                        }}
                      >
                        <Person fontSize="small" />
                      </Box>

                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b', mb: 0.5 }}>
                          {selectedCustomer.name}
                          {selectedCustomer.contact && (
                            <Typography component="span" sx={{ color: isDark ? '#94a3b8' : '#64748b', ml: 1, fontWeight: 400 }}>
                              ({selectedCustomer.contact})
                            </Typography>
                          )}
                        </Typography>

                        {selectedCustomer.address && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDark ? '#cbd5e1' : '#475569',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <Home fontSize="inherit" />
                            {selectedCustomer.address}
                          </Typography>
                        )}
                      </Box>

                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedCustomer(null);
                          setCustomerQuery('');
                          formik.setFieldValue("customerName", '');
                        }}
                        sx={{
                          borderRadius: 1.5,
                          backdropFilter: 'blur(10px)',
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.3 : 0.8
                          ),
                          border: `1px solid ${alpha(
                            isDark ? theme.palette.divider : '#e2e8f0',
                            isDark ? 0.2 : 0.3
                          )}`,
                          color: isDark ? '#cbd5e1' : '#64748b',
                          '&:hover': {
                            backgroundColor: alpha(
                              isDark ? theme.palette.background.paper : '#ffffff',
                              isDark ? 0.5 : 0.9
                            ),
                            color: isDark ? '#ef4444' : '#dc2626',
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Paper>
                )}

                {/* NEW CUSTOMER DETAILS */}
                {showNewCustomerFields && (
                  <Card
                    sx={{
                      mt: 3,
                      p: 2.5,
                      borderRadius: 2,
                      backdropFilter: 'blur(10px)',
                      backgroundColor: alpha(
                        isDark ? theme.palette.background.paper : '#ffffff',
                        isDark ? 0.3 : 0.7
                      ),
                      border: `1px solid ${alpha(
                        isDark ? theme.palette.divider : '#e2e8f0',
                        isDark ? 0.2 : 0.3
                      )}`,
                      boxShadow: 'none',
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: isDark ? '#e2e8f0' : '#1e293b',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <Person fontSize="small" />
                      New Customer Details
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          label="Contact Number *"
                          name="newCustomerContact"
                          value={formik.values.newCustomerContact}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.newCustomerContact && Boolean(formik.errors.newCustomerContact)}
                          helperText={formik.touched.newCustomerContact && formik.errors.newCustomerContact}
                          fullWidth
                          size="small"
                          required
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1.5,
                              backgroundColor: alpha(
                                isDark ? theme.palette.background.paper : '#ffffff',
                                isDark ? 0.2 : 0.7
                              ),
                              '&:hover': {
                                backgroundColor: alpha(
                                  isDark ? theme.palette.background.paper : '#ffffff',
                                  isDark ? 0.3 : 0.8
                                ),
                              }
                            },
                            '& .MuiOutlinedInput-input': {
                              color: isDark ? '#e2e8f0' : '#1e293b',
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Phone fontSize="small" sx={{ color: isDark ? '#cbd5e1' : '#64748b' }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label="Address"
                          name="newCustomerAddress"
                          value={formik.values.newCustomerAddress}
                          onChange={formik.handleChange}
                          multiline
                          rows={2}
                          fullWidth
                          size="small"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1.5,
                              backgroundColor: alpha(
                                isDark ? theme.palette.background.paper : '#ffffff',
                                isDark ? 0.2 : 0.7
                              ),
                              '&:hover': {
                                backgroundColor: alpha(
                                  isDark ? theme.palette.background.paper : '#ffffff',
                                  isDark ? 0.3 : 0.8
                                ),
                              }
                            },
                            '& .MuiOutlinedInput-input': {
                              color: isDark ? '#e2e8f0' : '#1e293b',
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Home fontSize="small" sx={{ color: isDark ? '#cbd5e1' : '#64748b' }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <DatePicker
                          label="Date of Birth"
                          value={formik.values.newCustomerDob}
                          format="dd/MM/yyyy"
                          onChange={(date) => formik.setFieldValue("newCustomerDob", date)}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              sx: {
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  backgroundColor: alpha(
                                    isDark ? theme.palette.background.paper : '#ffffff',
                                    isDark ? 0.2 : 0.7
                                  ),
                                  '&:hover': {
                                    backgroundColor: alpha(
                                      isDark ? theme.palette.background.paper : '#ffffff',
                                      isDark ? 0.3 : 0.8
                                    ),
                                  }
                                },
                                '& .MuiOutlinedInput-input': {
                                  color: isDark ? '#e2e8f0' : '#1e293b',
                                },
                              }
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <DatePicker
                          label="Anniversary"
                          value={formik.values.newCustomerAnniversary}
                          format="dd/MM/yyyy"
                          onChange={(date) => formik.setFieldValue("newCustomerAnniversary", date)}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              sx: {
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  backgroundColor: alpha(
                                    isDark ? theme.palette.background.paper : '#ffffff',
                                    isDark ? 0.2 : 0.7
                                  ),
                                  '&:hover': {
                                    backgroundColor: alpha(
                                      isDark ? theme.palette.background.paper : '#ffffff',
                                      isDark ? 0.3 : 0.8
                                    ),
                                  }
                                },
                                '& .MuiOutlinedInput-input': {
                                  color: isDark ? '#e2e8f0' : '#1e293b',
                                },
                              }
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Card>
                )}

                {/* SELLER & BASE FIELDS */}
                <Divider sx={{
                  my: 3,
                  borderColor: alpha(
                    isDark ? theme.palette.divider : '#e2e8f0',
                    isDark ? 0.1 : 0.2
                  ),
                }} />

                <Stack spacing={2}>
                  <TextField
                    label="Seller Name"
                    name="handledBy"
                    value={formik.values.handledBy}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.handledBy && Boolean(formik.errors.handledBy)}
                    helperText={formik.touched.handledBy && formik.errors.handledBy}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backdropFilter: 'blur(10px)',
                        backgroundColor: alpha(
                          isDark ? theme.palette.background.paper : '#ffffff',
                          isDark ? 0.3 : 0.8
                        ),
                        '&:hover': {
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.4 : 0.85
                          ),
                        },
                        '&.Mui-focused': {
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.4 : 0.85
                          ),
                        }
                      },
                      '& .MuiInputLabel-root': {
                        color: isDark ? '#cbd5e1' : '#64748b',
                      },
                      '& .MuiOutlinedInput-input': {
                        color: isDark ? '#e2e8f0' : '#1e293b',
                      },
                    }}
                  />

                  <DatePicker
                    label="Transaction Date *"
                    value={formik.values.transactionDate}
                    onChange={(date) => formik.setFieldValue("transactionDate", date)}
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backdropFilter: 'blur(10px)',
                            backgroundColor: alpha(
                              isDark ? theme.palette.background.paper : '#ffffff',
                              isDark ? 0.3 : 0.8
                            ),
                            '&:hover': {
                              backgroundColor: alpha(
                                isDark ? theme.palette.background.paper : '#ffffff',
                                isDark ? 0.4 : 0.85
                              ),
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            color: isDark ? '#e2e8f0' : '#1e293b',
                          },
                        }
                      },
                    }}
                  />

                  <TextField
                    label="GST Number"
                    name="gstNumber"
                    value={formik.values.gstNumber}
                    onChange={formik.handleChange}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backdropFilter: 'blur(10px)',
                        backgroundColor: alpha(
                          isDark ? theme.palette.background.paper : '#ffffff',
                          isDark ? 0.3 : 0.8
                        ),
                        '&:hover': {
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.4 : 0.85
                          ),
                        }
                      },
                      '& .MuiOutlinedInput-input': {
                        color: isDark ? '#e2e8f0' : '#1e293b',
                      },
                    }}
                  />

                  <TextField
                    label="PAN Number"
                    name="panNumber"
                    value={formik.values.panNumber}
                    onChange={formik.handleChange}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backdropFilter: 'blur(10px)',
                        backgroundColor: alpha(
                          isDark ? theme.palette.background.paper : '#ffffff',
                          isDark ? 0.3 : 0.8
                        ),
                        '&:hover': {
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.4 : 0.85
                          ),
                        }
                      },
                      '& .MuiOutlinedInput-input': {
                        color: isDark ? '#e2e8f0' : '#1e293b',
                      },
                    }}
                  />
                </Stack>
              </Card>
            </Grid>

            {/* CENTER COLUMN — ITEMS • GST • PAYMENT PANEL */}
            <Grid item size={{ xs: 12, md: 12 }}>
              <Stack spacing={3}>
                {/* DETAILED ITEM ENTRY SECTION */}
                {transactionType === "detailed" && (
                  <Card
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      backdropFilter: 'blur(20px)',
                      backgroundColor: alpha(
                        isDark ? theme.palette.background.paper : '#ffffff',
                        isDark ? 0.3 : 0.6
                      ),
                      border: `1px solid ${alpha(
                        isDark ? theme.palette.divider : '#e2e8f0',
                        isDark ? 0.2 : 0.3
                      )}`,
                      boxShadow: theme.shadows[4],
                    }}
                  >
                    {/* HEADER */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="subtitle1" sx={{
                        fontWeight: 700,
                        color: isDark ? '#e2e8f0' : '#1e293b',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}>
                        <ShoppingBag fontSize="small" />
                        Purchase Items
                      </Typography>

                      <Stack direction="row" spacing={2}>
                        <Button
                          startIcon={<AddIcon />}
                          variant="contained"
                          size="medium"
                          onClick={() => setManualItemDialogOpen(true)}
                          sx={{
                            borderRadius: 2,
                            background: isDark
                              ? 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)'
                              : 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
                            '&:hover': {
                              background: isDark
                                ? 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)'
                                : 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)',
                            }
                          }}
                        >
                          Add from Gross Stock
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={<QrCode />}
                          onClick={() => setScannerOpen(true)}
                          sx={{
                            borderRadius: 2,
                            background: isDark
                              ? 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)'
                              : 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                            boxShadow: '0 4px 12px rgba(139, 92, 246, 0.25)',
                            '&:hover': {
                              background: isDark
                                ? 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)'
                                : 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
                            }
                          }}
                        >
                          Scan QR
                        </Button>
                      </Stack>

                      <QRScanner
                        open={scannerOpen}
                        onClose={() => setScannerOpen(false)}
                        onScan={handleQRScan}
                        existingScannedIds={items.map(item => item.qr).filter(Boolean)}
                      />
                    </Stack>

                    {/* QUICK ADD FROM STOCK */}
                    {/* QUICK ADD FROM STOCK */}
                    <Paper
                      sx={{
                        p: 2.5,
                        mb: 3,
                        borderRadius: 2,
                        backdropFilter: 'blur(10px)',
                        backgroundColor: alpha(
                          isDark ? '#1e293b' : '#ffffff',
                          isDark ? 0.4 : 0.7
                        ),
                        border: `1px solid ${alpha(
                          isDark ? '#334155' : '#e2e8f0',
                          isDark ? 0.2 : 0.3
                        )}`,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: isDark ? '#e2e8f0' : '#1e293b',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <InventoryIcon fontSize="small" />
                        Quick Add from Stock
                      </Typography>

                      {/* Mode Selector Toggle */}
                      <Paper
                        sx={{
                          p: 2,
                          mb: 3,
                          borderRadius: 2,
                          backdropFilter: 'blur(10px)',
                          backgroundColor: alpha(
                            isDark ? '#1e293b' : '#ffffff',
                            isDark ? 0.4 : 0.7
                          ),
                          border: `1px solid ${alpha(
                            isDark ? '#334155' : '#e2e8f0',
                            isDark ? 0.2 : 0.3
                          )}`,
                        }}
                      >
                        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b', mb: 0.5 }}>
                              Item Addition Mode
                            </Typography>
                            <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                              {itemConsolidationMode === 'separate'
                                ? 'Each scanned item appears as a separate line item'
                                : 'Items with same name are consolidated into one line item with combined weight'}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              variant={itemConsolidationMode === 'separate' ? 'contained' : 'outlined'}
                              onClick={() => {
                                if (Object.keys(consolidatedItemGroups).length > 0 && itemConsolidationMode === 'consolidate') {
                                  if (window.confirm('Switching mode will clear all consolidated items. Continue?')) {
                                    setConsolidatedItemGroups({});
                                    setItemConsolidationMode('separate');
                                  }
                                } else {
                                  setItemConsolidationMode('separate');
                                }
                              }}
                              sx={{
                                borderRadius: 2,
                                ...(itemConsolidationMode === 'separate' && {
                                  background: isDark
                                    ? 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)'
                                    : 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                                })
                              }}
                            >
                              Separate Items
                            </Button>

                            <Button
                              variant={itemConsolidationMode === 'consolidate' ? 'contained' : 'outlined'}
                              onClick={() => {
                                if (items.length > 0 && itemConsolidationMode === 'separate') {
                                  if (window.confirm('Switching mode will clear all separate items. Continue?')) {
                                    setItems([]);
                                    setItemConsolidationMode('consolidate');
                                  }
                                } else {
                                  setItemConsolidationMode('consolidate');
                                }
                              }}
                              sx={{
                                borderRadius: 2,
                                ...(itemConsolidationMode === 'consolidate' && {
                                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                })
                              }}
                            >
                              Consolidate Items
                            </Button>
                          </Box>
                        </Stack>
                      </Paper>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3} sx={{ width: '250px' }}>
                          <Autocomplete
                            options={itemCategories}
                            getOptionLabel={(option) => {
                              if (typeof option === 'string') return option;
                              return `${option.name} (${option.shortcut})`;
                            }}
                            value={itemCategories.find(i => i.name === manualItemName) || null}
                            onChange={(event, newValue, reason) => {
                              if (reason === "clear") {
                                setManualItemName('');
                                return;
                              }

                              if (newValue) {
                                setManualItemName(newValue.name);
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Item Type"
                                size="small"
                                fullWidth
                              />
                            )}
                            renderOption={(props, option) => (
                              <li {...props}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                  <Typography variant="body2">{option.name}</Typography>
                                  <Chip
                                    label={option.shortcut}
                                    size="small"
                                    sx={{ ml: 1, fontSize: '0.7rem' }}
                                  />
                                </Box>
                              </li>
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} md={3} sx={{ width: '250px' }}>
                          <Autocomplete
                            options={metalTypes}
                            getOptionLabel={(option) => {
                              if (typeof option === 'string') return option;
                              return `${option.name} (${option.shortcut})`;
                            }}
                            value={metalTypes.find(m => m.name === manualItemMetal) || null}
                            onChange={(event, newValue, reason) => {
                              if (reason === "clear") {
                                setManualItemMetal('');
                                return;
                              }

                              if (newValue) {
                                setManualItemMetal(newValue.name);
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Metal"
                                size="small"
                                fullWidth
                              />
                            )}
                            renderOption={(props, option) => (
                              <li {...props}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                  <Typography variant="body2">{option.name}</Typography>
                                  <Chip
                                    label={option.shortcut}
                                    size="small"
                                    sx={{ ml: 1, fontSize: '0.7rem' }}
                                  />
                                </Box>
                              </li>
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} md={3} sx={{ width: '250px' }}>
                          <TextField
                            label="Serial No."
                            value={manualSerial}
                            onChange={(e) => setManualSerial(e.target.value)}
                            size="small"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                backdropFilter: 'blur(10px)',
                                backgroundColor: alpha(
                                  isDark ? theme.palette.background.paper : '#ffffff',
                                  isDark ? 0.2 : 0.7
                                ),
                                '&:hover': {
                                  backgroundColor: alpha(
                                    isDark ? theme.palette.background.paper : '#ffffff',
                                    isDark ? 0.3 : 0.8
                                  ),
                                }
                              },
                              '& .MuiOutlinedInput-input': {
                                color: isDark ? '#e2e8f0' : '#1e293b',
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={3}>
                          <Button
                            fullWidth
                            startIcon={<AddIcon />}
                            variant="contained"
                            onClick={handleAddBySerial}
                            sx={{
                              borderRadius: 1.5,
                              height: '40px',
                              width: '200px',
                              background: isDark
                                ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                                : 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)',
                              '&:hover': {
                                background: isDark
                                  ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                                  : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                              }
                            }}
                          >
                            Add from Stock
                          </Button>
                        </Grid>
                      </Grid>
                    </Paper>

                    {itemConsolidationMode === 'consolidate' && Object.keys(consolidatedItemGroups).length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: isDark ? '#e2e8f0' : '#1e293b' }}>
                          Consolidated Items
                        </Typography>
                        {Object.entries(consolidatedItemGroups).map(([groupName, groupData]) => (
                          <ConsolidatedItemRow
                            key={groupData.id}
                            groupName={groupName}
                            items={groupData.items}
                            consolidatedData={groupData}
                            onUpdateConsolidatedItem={(updated) => {
                              setConsolidatedItemGroups(prev => ({
                                ...prev,
                                [groupName]: updated
                              }));
                            }}
                            onRemoveConsolidatedItem={() => {
                              if (window.confirm(`Remove consolidated group "${groupName}"?`)) {
                                setConsolidatedItemGroups(prev => {
                                  const newGroups = { ...prev };
                                  delete newGroups[groupName];
                                  return newGroups;
                                });
                              }
                            }}
                            showGST={showGST}
                            gstMode={gstMode}
                            gstType={gstType}
                            calculations={calculations}
                            isDark={isDark}
                            theme={theme}
                            metalTypes={metalTypes}
                            itemCategories={itemCategories}
                          />
                        ))}
                      </Box>
                    )}

                    {/* Separate Items Table (Original) */}
                    {itemConsolidationMode === 'separate' && (
                      <>
                        {items.length > 0 ? (
                          <TableContainer
                            component={Paper}
                            sx={{
                              borderRadius: 2,
                              backdropFilter: 'blur(10px)',
                              backgroundColor: alpha(
                                isDark ? theme.palette.background.paper : '#ffffff',
                                isDark ? 0.2 : 0.7
                              ),
                              border: `1px solid ${alpha(
                                isDark ? theme.palette.divider : '#e2e8f0',
                                isDark ? 0.2 : 0.3
                              )}`,
                              overflowX: "auto",
                              overflowY: "hidden",
                            }}
                          >
                            <Table
                              size="small"
                              sx={{
                                minWidth: { xs: '700px', md: '100%' },
                                '& .MuiTableRow-root': {
                                  display: { xs: 'block', md: 'table-row' },
                                },
                                '& .MuiTableCell-root': {
                                  display: { xs: 'block', md: 'table-cell' },
                                },
                              }}
                            >
                              <TableHead
                                sx={{
                                  backgroundColor: alpha(
                                    isDark ? '#1e293b' : '#f8fafc',
                                    isDark ? 0.5 : 0.9
                                  ),
                                  display: { xs: 'none', md: 'table-header-group' },
                                }}
                              >
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>Item</TableCell>
                                  <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>Ct.</TableCell>
                                  <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>Weight</TableCell>
                                  <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>Rate</TableCell>
                                  <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>Wastage</TableCell>
                                  <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>Total</TableCell>
                                  <TableCell sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>Actions</TableCell>
                                </TableRow>
                              </TableHead>

                              <TableBody>
                                {renderItemRows}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : (
                          <Paper
                            sx={{
                              p: 3,
                              textAlign: "center",
                              borderRadius: 2,
                              backdropFilter: 'blur(10px)',
                              backgroundColor: alpha(
                                isDark ? '#1e293b' : '#f8fafc',
                                isDark ? 0.3 : 0.7
                              ),
                              border: `1px dashed ${alpha(
                                isDark ? theme.palette.divider : '#cbd5e1',
                                isDark ? 0.2 : 0.4
                              )}`,
                            }}
                          >
                            <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                              No items added yet. Scan QR or add from stock.
                            </Typography>
                          </Paper>
                        )}
                      </>
                    )}
                  </Card>
                )}

                {/* TOTAL AMOUNT (NON-ITEM) */}
                {transactionType === "total" && (
                  <Card
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      backdropFilter: 'blur(20px)',
                      backgroundColor: alpha(
                        isDark ? theme.palette.background.paper : '#ffffff',
                        isDark ? 0.3 : 0.8
                      ),
                      border: `1px solid ${alpha(
                        isDark ? theme.palette.divider : '#e2e8f0',
                        isDark ? 0.2 : 0.3
                      )}`,
                      boxShadow: theme.shadows[4],
                    }}
                  >
                    <TextField
                      label="Total Amount"
                      type="text"
                      name="totalAmount"
                      value={formik.values.totalAmount}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.totalAmount && Boolean(formik.errors.totalAmount)}
                      helperText={formik.touched.totalAmount && formik.errors.totalAmount}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backdropFilter: 'blur(10px)',
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.2 : 0.7
                          ),
                          '&:hover': {
                            backgroundColor: alpha(
                              isDark ? theme.palette.background.paper : '#ffffff',
                              isDark ? 0.3 : 0.8
                            ),
                          }
                        },
                        '& .MuiOutlinedInput-input': {
                          color: isDark ? '#e2e8f0' : '#1e293b',
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                    />
                  </Card>
                )}

                {/* GST + DISCOUNT SECTION */}
                <Card
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    backdropFilter: 'blur(20px)',
                    backgroundColor: alpha(
                      isDark ? theme.palette.background.paper : '#ffffff',
                      isDark ? 0.3 : 0.6
                    ),
                    border: `1px solid ${alpha(
                      isDark ? theme.palette.divider : '#e2e8f0',
                      isDark ? 0.2 : 0.3
                    )}`,
                    boxShadow: theme.shadows[4],
                  }}
                >
                  <Box sx={{ mb: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <TextField
                        label="Bill Number"
                        value={isLoadingBillNo ? "Loading..." : (currentBillNo || "N/A")}
                        disabled
                        fullWidth
                        sx={{
                          flex: 1,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backdropFilter: 'blur(10px)',
                            backgroundColor: alpha(
                              isDark ? theme.palette.background.paper : '#ffffff',
                              isDark ? 0.2 : 0.7
                            ),
                            '&.Mui-disabled': {
                              backgroundColor: alpha(
                                isDark ? '#1e293b' : '#f1f5f9',
                                isDark ? 0.3 : 0.5
                              ),
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            color: isDark ? '#e2e8f0' : '#1e293b',
                            fontWeight: 600,
                          },
                          '& .MuiInputLabel-root': {
                            color: isDark ? '#cbd5e1' : '#64748b',
                          },
                        }}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                      <Button
                        variant="outlined"
                        onClick={fetchLastBillNumbers}
                        disabled={isLoadingBillNo}
                        startIcon={isLoadingBillNo ? <CircularProgress size={20} /> : <RefreshIcon />}
                        sx={{
                          minWidth: '100px',
                          borderRadius: 2,
                          backdropFilter: 'blur(10px)',
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.3 : 0.8
                          ),
                          border: `1px solid ${alpha(
                            isDark ? '#60a5fa' : '#3b82f6',
                            isDark ? 0.4 : 0.5
                          )}`,
                          color: isDark ? '#60a5fa' : '#3b82f6',
                          '&:hover': {
                            backgroundColor: alpha(
                              isDark ? theme.palette.background.paper : '#ffffff',
                              isDark ? 0.5 : 0.9
                            ),
                            borderColor: isDark ? '#3b82f6' : '#2563eb',
                          }
                        }}
                      >
                        Refresh
                      </Button>
                    </Stack>
                    <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', mt: 1, display: 'block' }}>
                      {showGST
                        ? `Last GST Bill No: ${gstBillNo || 0} | Next: ${currentBillNo || 1}`
                        : `Last Non-GST Bill No: ${nonGstBillNo || 0} | Next: ${currentBillNo || 1}`
                      }
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b', mb: 1 }}>
                      GST Settings
                    </Typography>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={showGST}
                          onChange={(e) => {
                            setShowGST(e.target.checked);
                            if (!e.target.checked) {
                              setGstMode("add");
                            }
                          }}
                          color="primary"
                        />
                      }
                      label={<Typography sx={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>Apply GST (3%)</Typography>}
                      sx={{ mb: 1 }}
                    />

                    {showGST && (
                      <>
                        <RadioGroup
                          row
                          value={gstMode}
                          onChange={(e) => setGstMode(e.target.value)}
                          sx={{ ml: 2, mb: 2 }}
                        >
                          <FormControlLabel
                            value="add"
                            control={<Radio size="small" />}
                            label={
                              <Typography variant="body2" sx={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>
                                Add GST 3%
                              </Typography>
                            }
                          />
                          <FormControlLabel
                            value="adjust"
                            control={<Radio size="small" />}
                            label={
                              <Typography variant="body2" sx={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>
                                Adjust Making Charges
                              </Typography>
                            }
                          />
                        </RadioGroup>

                        <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                          <InputLabel sx={{ color: isDark ? '#cbd5e1' : '#64748b' }}>GST Type</InputLabel>
                          <Select
                            value={gstType}
                            onChange={(e) => setGstType(e.target.value)}
                            label="GST Type"
                            sx={{
                              borderRadius: 2,
                              backdropFilter: 'blur(10px)',
                              backgroundColor: alpha(
                                isDark ? theme.palette.background.paper : '#ffffff',
                                isDark ? 0.2 : 0.7
                              ),
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: alpha(
                                  isDark ? theme.palette.divider : '#e2e8f0',
                                  isDark ? 0.2 : 0.3
                                ),
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: alpha(
                                  isDark ? '#60a5fa' : '#3b82f6',
                                  isDark ? 0.4 : 0.5
                                ),
                              },
                              '& .MuiSelect-select': {
                                color: isDark ? '#e2e8f0' : '#1e293b',
                              },
                            }}
                          >
                            <MenuItem value="cgst_sgst">CGST + SGST (1.5% each)</MenuItem>
                            <MenuItem value="igst">IGST (3%)</MenuItem>
                          </Select>
                        </FormControl>

                        <Box sx={{
                          mb: 2, p: 2, borderRadius: 2, background: alpha(
                            isDark ? '#1e293b' : '#f8fafc',
                            isDark ? 0.4 : 0.5
                          )
                        }}>
                          <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', display: 'block', mb: 1 }}>
                            {gstType === 'igst'
                              ? 'IGST (Integrated GST) - 3% applicable for inter-state transactions'
                              : 'CGST + SGST (Central & State GST) - 1.5% each applicable for intra-state transactions'}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? '#60a5fa' : '#3b82f6' }}>
                            {gstType === 'igst'
                              ? `IGST Amount: ₹${calculations.igst.toFixed(2)}`
                              : `CGST: ₹${calculations.cgst.toFixed(2)} | SGST: ₹${calculations.sgst.toFixed(2)}`}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>

                  <TextField
                    label="Discount"
                    type="text"
                    name="discount"
                    value={formik.values.discount}
                    onChange={formik.handleChange}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backdropFilter: 'blur(10px)',
                        backgroundColor: alpha(
                          isDark ? theme.palette.background.paper : '#ffffff',
                          isDark ? 0.2 : 0.7
                        ),
                        '&:hover': {
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.3 : 0.8
                          ),
                        }
                      },
                      '& .MuiOutlinedInput-input': {
                        color: isDark ? '#e2e8f0' : '#1e293b',
                      },
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      endAdornment: (
                        <InputAdornment position="end">
                          <LocalOfferIcon fontSize="small" sx={{ color: isDark ? '#cbd5e1' : '#64748b' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Card>

                {/* PAYMENT SECTION */}
                <Card
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    backdropFilter: 'blur(20px)',
                    backgroundColor: alpha(
                      isDark ? theme.palette.background.paper : '#ffffff',
                      isDark ? 0.3 : 0.6
                    ),
                    border: `1px solid ${alpha(
                      isDark ? theme.palette.divider : '#e2e8f0',
                      isDark ? 0.2 : 0.3
                    )}`,
                    boxShadow: theme.shadows[4],
                  }}
                >
                  <Typography variant="h6" sx={{
                    fontWeight: 700,
                    color: isDark ? '#e2e8f0' : '#1e293b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                  }}>
                    <Payments fontSize="small" />
                    Payment Details
                  </Typography>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={finePayment.enabled}
                          onChange={(e) => setFinePayment(prev => ({ ...prev, enabled: e.target.checked }))}
                          color="primary"
                        />
                      }
                      label={
                        <Typography sx={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>
                          Fine Payment (in grams)
                        </Typography>
                      }
                    />
                  </Grid>

                  {finePayment.enabled && (
                    <>
                      {/* First row with both fields */}
                      <Grid item xs={12}>
                        <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                          <TextField
                            label="Fine Weight (gm)"
                            type="text"
                            value={finePayment.weight}
                            onChange={(e) => setFinePayment(prev => ({ ...prev, weight: e.target.value }))}
                            fullWidth
                            size="small"
                            InputProps={{
                              endAdornment: <InputAdornment position="end">gm</InputAdornment>,
                              inputProps: { step: "0.001" }
                            }}
                          />

                          <TextField
                            label="Purity (%)"
                            type="text"
                            value={finePayment.purity}
                            onChange={(e) => setFinePayment(prev => ({ ...prev, purity: e.target.value }))}
                            fullWidth
                            size="small"
                            InputProps={{
                              endAdornment: <InputAdornment position="end">%</InputAdornment>,
                              inputProps: { step: "0.1" }
                            }}
                          />
                        </Stack>
                      </Grid>

                      {/* Second row with Alert */}
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ borderRadius: 1, mt: 1 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2">Actual Fine Received:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {calculatedFinePayment.toFixed(3)} gm
                            </Typography>
                          </Stack>
                          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: isDark ? '#94a3b8' : '#64748b' }}>
                            Calculation: {finePayment.weight || 0} gm × ({finePayment.purity || 0}% ÷ 100) = {calculatedFinePayment.toFixed(3)} gm
                          </Typography>
                        </Alert>
                      </Grid>
                    </>
                  )}

                  <Grid container spacing={2} sx={{ m: 2 }}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Cash"
                        type="text"
                        name="cashPayment"
                        value={formik.values.cashPayment}
                        onChange={formik.handleChange}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            backdropFilter: 'blur(10px)',
                            backgroundColor: alpha(
                              isDark ? theme.palette.background.paper : '#ffffff',
                              isDark ? 0.2 : 0.7
                            ),
                            '&:hover': {
                              backgroundColor: alpha(
                                isDark ? theme.palette.background.paper : '#ffffff',
                                isDark ? 0.3 : 0.8
                              ),
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            color: isDark ? '#e2e8f0' : '#1e293b',
                          },
                        }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Online"
                        type="text"
                        name="onlinePayment"
                        value={formik.values.onlinePayment}
                        onChange={formik.handleChange}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            backdropFilter: 'blur(10px)',
                            backgroundColor: alpha(
                              isDark ? theme.palette.background.paper : '#ffffff',
                              isDark ? 0.2 : 0.7
                            ),
                            '&:hover': {
                              backgroundColor: alpha(
                                isDark ? theme.palette.background.paper : '#ffffff',
                                isDark ? 0.3 : 0.8
                              ),
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            color: isDark ? '#e2e8f0' : '#1e293b',
                          },
                        }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Cheque"
                        type="text"
                        name="chequePayment"
                        value={formik.values.chequePayment}
                        onChange={formik.handleChange}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            backdropFilter: 'blur(10px)',
                            backgroundColor: alpha(
                              isDark ? theme.palette.background.paper : '#ffffff',
                              isDark ? 0.2 : 0.7
                            ),
                            '&:hover': {
                              backgroundColor: alpha(
                                isDark ? theme.palette.background.paper : '#ffffff',
                                isDark ? 0.3 : 0.8
                              ),
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            color: isDark ? '#e2e8f0' : '#1e293b',
                          },
                        }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Other Payment"
                        type="text"
                        name="otherPayment"
                        value={formik.values.otherPayment}
                        onChange={formik.handleChange}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                            backdropFilter: 'blur(10px)',
                            backgroundColor: alpha(
                              isDark ? theme.palette.background.paper : '#ffffff',
                              isDark ? 0.2 : 0.7
                            ),
                            '&:hover': {
                              backgroundColor: alpha(
                                isDark ? theme.palette.background.paper : '#ffffff',
                                isDark ? 0.3 : 0.8
                              ),
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            color: isDark ? '#e2e8f0' : '#1e293b',
                          },
                        }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                      />
                    </Grid>
                  </Grid>

                  {/* ADVANCE PAYMENT SWITCH */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={enableAdvancePayment}
                        onChange={(e) => setEnableAdvancePayment(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <AccountBalanceIcon fontSize="small" />
                        <Typography sx={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>Advance Payments</Typography>
                      </Stack>
                    }
                  />

                  {/* ADVANCE PAYMENT SECTION */}
                  {enableAdvancePayment && (
                    <Box mt={2}>
                      <Typography variant="subtitle2" sx={{
                        fontWeight: 700,
                        color: isDark ? '#e2e8f0' : '#1e293b',
                        m: 1,
                      }}>
                        Select Advance Payment Modes
                      </Typography>

                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {Object.entries(paymentModes).map(([mode, enabled]) => {
                          let displayLabel = mode.charAt(0).toUpperCase() + mode.slice(1);
                          if (mode === 'oldGold') displayLabel = 'Old Gold';
                          if (mode === 'oldSilver') displayLabel = 'Old Silver';

                          return (
                            <Chip
                              key={mode}
                              label={displayLabel}
                              clickable
                              onClick={() => handlePaymentModeChange(mode)}
                              color={enabled ? "primary" : "default"}
                              variant={enabled ? "filled" : "outlined"}
                              sx={{
                                mb: 1,
                                backdropFilter: "blur(10px)",
                                backgroundColor: enabled
                                  ? alpha(
                                    isDark
                                      ? theme.palette.primary.main
                                      : theme.palette.primary.main,
                                    isDark ? 0.3 : 0.2,
                                  )
                                  : "transparent",
                              }}
                            />
                          );
                        })}
                      </Stack>

                      {/* Custom "Other" Option */}
                      {paymentModes.other && (
                        <Stack direction="row" spacing={1} mt={2}>
                          <TextField
                            label="Custom Payment Type"
                            size="small"
                            value={customOther}
                            onChange={(e) => setCustomOther(e.target.value)}
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                                backdropFilter: 'blur(10px)',
                                backgroundColor: alpha(
                                  isDark ? theme.palette.background.paper : '#ffffff',
                                  isDark ? 0.2 : 0.7
                                ),
                              },
                            }}
                          />
                          <Button
                            variant="outlined"
                            onClick={handleAddCustomOther}
                            sx={{
                              borderRadius: 1.5,
                              backdropFilter: 'blur(10px)',
                              backgroundColor: alpha(
                                isDark ? theme.palette.background.paper : '#ffffff',
                                isDark ? 0.3 : 0.8
                              ),
                            }}
                          >
                            Add
                          </Button>
                        </Stack>
                      )}

                      {/* Advance Fields */}
                      <Grid container spacing={2} mt={1}>
                        {Object.entries(paymentModes).map(
                          ([mode, enabled]) =>
                            enabled &&
                            mode !== "other" && (
                              <Grid item xs={12} md={6} key={mode}>
                                <TextField
                                  label={mode === 'oldGold' ? 'Advance Old Gold' : mode === 'oldSilver' ? 'Advance Old Silver' : `Advance ${mode.charAt(0).toUpperCase() + mode.slice(1)}`}
                                  type="text"
                                  size="small"
                                  fullWidth
                                  value={advancePayments[mode] || ""}
                                  onChange={(e) =>
                                    handleAdvancePaymentChange(mode, e.target.value)
                                  }
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 1.5,
                                      backdropFilter: 'blur(10px)',
                                      backgroundColor: alpha(
                                        isDark ? theme.palette.background.paper : '#ffffff',
                                        isDark ? 0.2 : 0.7
                                      ),
                                    },
                                  }}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">₹</InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                            )
                        )}

                        {/* Custom Other Advance Fields */}
                        {otherOptions.map((opt) => (
                          <Grid item xs={12} md={6} key={opt}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <TextField
                                label={`Advance ${opt}`}
                                type="text"
                                size="small"
                                fullWidth
                                value={advancePayments[opt] || ""}
                                onChange={(e) =>
                                  handleCustomOtherPaymentChange(opt, e.target.value)
                                }
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5,
                                    backdropFilter: 'blur(10px)',
                                    backgroundColor: alpha(
                                      isDark ? theme.palette.background.paper : '#ffffff',
                                      isDark ? 0.2 : 0.7
                                    ),
                                  },
                                }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">₹</InputAdornment>
                                  ),
                                }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveCustomOther(opt)}
                                sx={{
                                  borderRadius: 1,
                                  backdropFilter: 'blur(10px)',
                                  backgroundColor: alpha(
                                    isDark ? theme.palette.error.main : theme.palette.error.main,
                                    isDark ? 0.2 : 0.1
                                  ),
                                  color: theme.palette.error.main,
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.error.main, isDark ? 0.3 : 0.15),
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Grid>
                        ))}
                      </Grid>

                      {/* ADVANCE TOTAL */}
                      <TextField
                        label="Advance Total"
                        fullWidth
                        size="small"
                        value={advanceTotal}
                        sx={{ mt: 2 }}
                        InputProps={{
                          readOnly: true,
                          startAdornment: (
                            <InputAdornment position="start">₹</InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  )}

                  {/* PAYMENT DONE */}
                  <TextField
                    label="Total Payment Done"
                    fullWidth
                    margin="normal"
                    size="small"
                    value={formik.values.paymentDone}
                    InputProps={{
                      readOnly: true,
                      startAdornment: (
                        <InputAdornment position="start">₹</InputAdornment>
                      ),
                    }}
                  />
                </Card>
              </Stack>
            </Grid>

            {/* RIGHT COLUMN — SUMMARY + ACTION BUTTONS */}
            <Grid item size={{ xs: 12, md: 12 }}>
              <Stack
                spacing={3}
                sx={{
                  position: "sticky",
                  top: 24,
                }}
              >
                {/* SUMMARY PANEL */}
                <Card
                  sx={{
                    p: 0,
                    borderRadius: 3,
                    backdropFilter: 'blur(20px)',
                    backgroundColor: alpha(
                      isDark ? theme.palette.background.paper : '#ffffff',
                      isDark ? 0.3 : 0.3
                    ),
                    border: `1px solid ${alpha(
                      isDark ? theme.palette.divider : '#e2e8f0',
                      isDark ? 0.2 : 0.3
                    )}`,
                    boxShadow: theme.shadows[4],
                    overflow: 'hidden',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: isDark
                        ? 'linear-gradient(90deg, #60A5FA 0%, #3B82F6 50%, #1D4ED8 100%)'
                        : 'linear-gradient(90deg, #2563EB 0%, #1D4ED8 50%, #1E40AF 100%)',
                    }
                  }}
                >
                  {/* HEADER */}
                  <Box
                    sx={{
                      p: 3,
                      pb: 2,
                      background: alpha(
                        isDark ? '#1e293b' : '#f8fafc',
                        isDark ? 0.4 : 0.5
                      ),
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          background: isDark
                            ? 'linear-gradient(135deg, #93C5FD 0%, #60A5FA 100%)'
                            : 'linear-gradient(135deg, #1E293B 0%, #334155 100%)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: isDark
                              ? 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)'
                              : 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                            color: 'white',
                          }}
                        >
                          <Summarize fontSize="small" />
                        </Box>
                        Transaction Summary
                      </Typography>

                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1.5,
                          background: alpha(
                            isDark ? '#10b981' : '#10b981',
                            isDark ? 0.2 : 0.15
                          ),
                          border: `1px solid ${alpha(
                            isDark ? '#10b981' : '#059669',
                            isDark ? 0.3 : 0.2
                          )}`,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: isDark ? '#34d399' : '#059669',
                            fontSize: '0.7rem',
                          }}
                        >
                          {showGST ? 'GST INVOICE' : 'CASH MEMO'}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography
                      variant="caption"
                      sx={{
                        color: isDark ? '#94a3b8' : '#64748b',
                        display: 'block',
                        pl: 5.5,
                      }}
                    >
                      Real-time calculations • Auto-updating
                    </Typography>
                  </Box>

                  <Box sx={{ p: 3 }}>
                    {/* AMOUNT BREAKDOWN */}
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            color: isDark ? '#e2e8f0' : '#1e293b',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Calculate fontSize="small" />
                          Amount Breakdown
                        </Typography>
                      </Box>

                      {/* SUBTOTAL */}
                      <Box sx={{ mb: 2 }}>
                        {/* <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#64748b', fontSize: '0.8rem' }}>
                            Subtotal
                          </Typography>
                          <Typography sx={{ fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b', fontSize: '0.9rem' }}>
                            ₹{calculations.subtotal.toFixed(2)}
                          </Typography>
                        </Stack> */}
                        {/* Enhanced Amount Breakdown */}
                        <Box sx={{ mb: 2 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#64748b', fontSize: '0.8rem' }}>
                              Subtotal
                            </Typography>
                            <Typography sx={{ fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b', fontSize: '0.9rem' }}>
                              ₹{calculations.subtotal.toFixed(2)}
                            </Typography>
                          </Stack>
                        </Box>

                        {/* Fine Weight Details */}
                        {calculations.totalFineWeight > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                              <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#64748b', fontSize: '0.8rem' }}>
                                Total Fine Weight
                              </Typography>
                              <Typography sx={{ fontWeight: 600, color: isDark ? '#f59e0b' : '#d97706', fontSize: '0.9rem' }}>
                                {calculations.totalFineWeight.toFixed(3)} gm
                              </Typography>
                            </Stack>
                            <Box
                              sx={{
                                height: '2px',
                                borderRadius: '1px',
                                background: alpha(isDark ? '#f59e0b' : '#f59e0b', 0.2),
                                overflow: 'hidden',
                              }}
                            >
                              <Box
                                sx={{
                                  height: '100%',
                                  width: `${Math.min(100, (calculations.totalFineWeight / (calculations.totalFineWeight || 1)) * 100)}%`,
                                  background: 'linear-gradient(90deg, #F59E0B 0%, #D97706 100%)',
                                  borderRadius: '1px',
                                }}
                              />
                            </Box>
                          </Box>
                        )}

                        {/* Fine Payment Received */}
                        {calculations.finePaymentReceived > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                              <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#64748b', fontSize: '0.8rem' }}>
                                Fine Payment Received
                              </Typography>
                              <Typography sx={{ fontWeight: 600, color: '#10b981', fontSize: '0.9rem' }}>
                                - {calculations.finePaymentReceived.toFixed(3)} gm
                              </Typography>
                            </Stack>
                            <Box
                              sx={{
                                height: '2px',
                                borderRadius: '1px',
                                background: alpha(isDark ? '#10b981' : '#10b981', 0.2),
                                overflow: 'hidden',
                              }}
                            >
                              <Box
                                sx={{
                                  height: '100%',
                                  width: `${calculations.totalFineWeight > 0 ? (calculations.finePaymentReceived / calculations.totalFineWeight) * 100 : 0}%`,
                                  background: 'linear-gradient(90deg, #10B981 0%, #059669 100%)',
                                  borderRadius: '1px',
                                }}
                              />
                            </Box>
                          </Box>
                        )}

                        {/* Fine Balance */}
                        {calculations.totalFineWeight > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                              <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#64748b', fontSize: '0.8rem' }}>
                                Fine Balance
                              </Typography>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  color: calculations.fineBalance > 0 ? (isDark ? '#f87171' : '#dc2626') : (isDark ? '#34d399' : '#059669'),
                                  fontSize: '0.9rem'
                                }}
                              >
                                {calculations.fineBalance.toFixed(3)} gm
                              </Typography>
                            </Stack>
                            <Box
                              sx={{
                                height: '2px',
                                borderRadius: '1px',
                                background: alpha(isDark ? '#ef4444' : '#ef4444', 0.2),
                                overflow: 'hidden',
                              }}
                            >
                              <Box
                                sx={{
                                  height: '100%',
                                  width: `${calculations.totalFineWeight > 0 ? (Math.abs(calculations.fineBalance) / calculations.totalFineWeight) * 100 : 0}%`,
                                  background: calculations.fineBalance > 0
                                    ? 'linear-gradient(90deg, #EF4444 0%, #DC2626 100%)'
                                    : 'linear-gradient(90deg, #10B981 0%, #059669 100%)',
                                  borderRadius: '1px',
                                }}
                              />
                            </Box>
                          </Box>
                        )}

                        <Box
                          sx={{
                            height: '4px',
                            borderRadius: '2px',
                            background: alpha(
                              isDark ? '#60a5fa' : '#3b82f6',
                              isDark ? 0.3 : 0.2
                            ),
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              height: '100%',
                              width: '100%',
                              background: isDark
                                ? 'linear-gradient(90deg, #60A5FA 0%, #3B82F6 100%)'
                                : 'linear-gradient(90deg, #2563EB 0%, #1D4ED8 100%)',
                              borderRadius: '2px',
                            }}
                          />
                        </Box>
                      </Box>

                      {/* GST BREAKDOWN */}
                      {showGST && (
                        <>
                          {gstType === 'igst' ? (
                            <Box sx={{ mb: 2 }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#64748b', fontSize: '0.8rem' }}>
                                  IGST (3%)
                                </Typography>
                                <Typography sx={{ color: isDark ? '#e2e8f0' : '#1e293b', fontSize: '0.9rem' }}>
                                  ₹{calculations.igst.toFixed(2)}
                                </Typography>
                              </Stack>
                              <Box
                                sx={{
                                  height: '3px',
                                  borderRadius: '1.5px',
                                  background: alpha(
                                    isDark ? '#8b5cf6' : '#8b5cf6',
                                    isDark ? 0.3 : 0.2
                                  ),
                                  overflow: 'hidden',
                                }}
                              >
                                <Box
                                  sx={{
                                    height: '100%',
                                    width: '100%',
                                    background: isDark
                                      ? 'linear-gradient(90deg, #A78BFA 0%, #8B5CF6 100%)'
                                      : 'linear-gradient(90deg, #8B5CF6 0%, #7C3AED 100%)',
                                    borderRadius: '1.5px',
                                  }}
                                />
                              </Box>
                            </Box>
                          ) : (
                            <>
                              <Box sx={{ mb: 2 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                  <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#64748b', fontSize: '0.8rem' }}>
                                    CGST (1.5%)
                                  </Typography>
                                  <Typography sx={{ color: isDark ? '#e2e8f0' : '#1e293b', fontSize: '0.9rem' }}>
                                    ₹{calculations.cgst.toFixed(2)}
                                  </Typography>
                                </Stack>
                                <Box
                                  sx={{
                                    height: '3px',
                                    borderRadius: '1.5px',
                                    background: alpha(
                                      isDark ? '#34d399' : '#10b981',
                                      isDark ? 0.3 : 0.2
                                    ),
                                    overflow: 'hidden',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      height: '100%',
                                      width: '100%',
                                      background: isDark
                                        ? 'linear-gradient(90deg, #34D399 0%, #10B981 100%)'
                                        : 'linear-gradient(90deg, #10B981 0%, #059669 100%)',
                                      borderRadius: '1.5px',
                                    }}
                                  />
                                </Box>
                              </Box>

                              <Box sx={{ mb: 2 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                  <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#64748b', fontSize: '0.8rem' }}>
                                    SGST (1.5%)
                                  </Typography>
                                  <Typography sx={{ color: isDark ? '#e2e8f0' : '#1e293b', fontSize: '0.9rem' }}>
                                    ₹{calculations.sgst.toFixed(2)}
                                  </Typography>
                                </Stack>
                                <Box
                                  sx={{
                                    height: '3px',
                                    borderRadius: '1.5px',
                                    background: alpha(
                                      isDark ? '#34d399' : '#10b981',
                                      isDark ? 0.3 : 0.2
                                    ),
                                    overflow: 'hidden',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      height: '100%',
                                      width: '100%',
                                      background: isDark
                                        ? 'linear-gradient(90deg, #34D399 0%, #10B981 100%)'
                                        : 'linear-gradient(90deg, #10B981 0%, #059669 100%)',
                                      borderRadius: '1.5px',
                                    }}
                                  />
                                </Box>
                              </Box>
                            </>
                          )}
                        </>
                      )}

                      {/* DISCOUNT */}
                      {formik.values.discount > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#64748b', fontSize: '0.8rem' }}>
                              Discount
                            </Typography>
                            <Typography sx={{ fontWeight: 600, color: 'error.main', fontSize: '0.9rem' }}>
                              -₹{parseFloat(formik.values.discount).toFixed(2)}
                            </Typography>
                          </Stack>
                          <Box
                            sx={{
                              height: '3px',
                              borderRadius: '1.5px',
                              background: alpha(
                                isDark ? theme.palette.error.main : theme.palette.error.main,
                                isDark ? 0.3 : 0.2
                              ),
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                height: '100%',
                                width: '100%',
                                background: isDark
                                  ? 'linear-gradient(90deg, #F87171 0%, #EF4444 100%)'
                                  : 'linear-gradient(90deg, #EF4444 0%, #DC2626 100%)',
                                borderRadius: '1.5px',
                              }}
                            />
                          </Box>
                        </Box>
                      )}

                      {/* GRAND TOTAL */}
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 1,
                          background: isDark
                            ? 'linear-gradient(135deg, rgba(37, 99, 235, 0.15) 0%, rgba(29, 78, 216, 0.1) 100%)'
                            : 'linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(29, 78, 216, 0.08) 100%)',
                          border: `1px solid ${alpha(
                            isDark ? '#3b82f6' : '#2563eb',
                            isDark ? 0.2 : 0.15
                          )}`,
                          mt: 2,
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography sx={{ fontWeight: 700, color: isDark ? '#e2e8f0' : '#1e293b' }}>
                            Grand Total
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              fontSize: '1.25rem',
                              background: isDark
                                ? 'linear-gradient(135deg, #93C5FD 0%, #60A5FA 100%)'
                                : 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                              backgroundClip: 'text',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                            }}
                          >
                            ₹{calculations.grandTotal.toFixed(2)}
                          </Typography>
                        </Stack>
                      </Box>
                    </Box>

                    {/* PAYMENT SUMMARY */}
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            color: isDark ? '#e2e8f0' : '#1e293b',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Payments fontSize="small" />
                          Payment Summary
                        </Typography>
                      </Box>

                      <Grid container spacing={1.5}>
                        <Grid item xs={6}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              background: alpha(
                                isDark ? '#10b981' : '#10b981',
                                isDark ? 0.15 : 0.1
                              ),
                              border: `1px solid ${alpha(
                                isDark ? '#10b981' : '#059669',
                                isDark ? 0.2 : 0.15
                              )}`,
                              textAlign: 'center',
                            }}
                          >
                            <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', display: 'block', mb: 0.5 }}>
                              Paid
                            </Typography>
                            <Typography sx={{ fontWeight: 700, color: isDark ? '#34d399' : '#059669', fontSize: '1rem' }}>
                              ₹{formik.values.paymentDone.toFixed(2)}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={6}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              background: pendingAmount > 0
                                ? alpha(
                                  isDark ? theme.palette.error.main : theme.palette.error.main,
                                  isDark ? 0.15 : 0.1
                                )
                                : alpha(
                                  isDark ? '#10b981' : '#10b981',
                                  isDark ? 0.15 : 0.1
                                ),
                              border: `1px solid ${pendingAmount > 0
                                ? alpha(
                                  isDark ? theme.palette.error.main : theme.palette.error.main,
                                  isDark ? 0.2 : 0.15
                                )
                                : alpha(
                                  isDark ? '#10b981' : '#059669',
                                  isDark ? 0.2 : 0.15
                                )}`,
                              textAlign: 'center',
                            }}
                          >
                            <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', display: 'block', mb: 0.5 }}>
                              {pendingAmount > 0 ? 'Pending' : 'Balance'}
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: pendingAmount > 0
                                  ? isDark ? '#f87171' : '#dc2626'
                                  : isDark ? '#34d399' : '#059669',
                                fontSize: '1rem',
                              }}
                            >
                              ₹{pendingAmount.toFixed(2)}
                            </Typography>
                          </Box>
                        </Grid>

                        {/* Add this after the other payment summaries */}
                        {calculations.finePaymentReceived > 0 && (
                          <Box sx={{ mb: 1 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#64748b', fontSize: '0.7rem' }}>
                                Fine Payment (gm)
                              </Typography>
                              <Typography sx={{ fontWeight: 500, color: '#10b981', fontSize: '0.8rem' }}>
                                {calculations.finePaymentReceived.toFixed(3)} gm
                              </Typography>
                            </Stack>
                          </Box>
                        )}
                      </Grid>
                    </Box>

                    {/* QUICK STATS */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            color: isDark ? '#e2e8f0' : '#1e293b',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <InfoIcon fontSize="small" />
                          Quick Stats
                        </Typography>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              background: alpha(
                                isDark ? '#1e293b' : '#f8fafc',
                                isDark ? 0.4 : 0.6
                              ),
                              border: `1px solid ${alpha(
                                isDark ? theme.palette.divider : '#e2e8f0',
                                isDark ? 0.2 : 0.3
                              )}`,
                              textAlign: 'center',
                            }}
                          >
                            <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', display: 'block', mb: 0.5 }}>
                              Items
                            </Typography>
                            <Typography sx={{ fontWeight: 800, color: isDark ? '#60a5fa' : '#3b82f6', fontSize: '1.2rem' }}>
                              {items.length}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={4}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              background: alpha(
                                isDark ? '#1e293b' : '#f8fafc',
                                isDark ? 0.4 : 0.6
                              ),
                              border: `1px solid ${alpha(
                                isDark ? theme.palette.divider : '#e2e8f0',
                                isDark ? 0.2 : 0.3
                              )}`,
                              textAlign: 'center',
                            }}
                          >
                            <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', display: 'block', mb: 0.5 }}>
                              GST
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 800,
                                color: showGST
                                  ? isDark ? '#34d399' : '#059669'
                                  : isDark ? '#94a3b8' : '#64748b',
                                fontSize: '1.2rem',
                              }}
                            >
                              {showGST ? gstType === 'igst' ? 'IGST' : 'CGST/SGST' : 'OFF'}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={4}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              background: alpha(
                                isDark ? '#1e293b' : '#f8fafc',
                                isDark ? 0.4 : 0.6
                              ),
                              border: `1px solid ${alpha(
                                isDark ? theme.palette.divider : '#e2e8f0',
                                isDark ? 0.2 : 0.3
                              )}`,
                              textAlign: 'center',
                            }}
                          >
                            <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', display: 'block', mb: 0.5 }}>
                              Mode
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 800,
                                color: showGST && gstMode === 'adjust'
                                  ? isDark ? '#f59e0b' : '#d97706'
                                  : isDark ? '#94a3b8' : '#64748b',
                                fontSize: '1.2rem',
                              }}
                            >
                              {showGST ? (gstMode === 'add' ? 'ADD' : 'ADJ') : 'N/A'}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                </Card>

                {/* Pending Adjustments Summary */}
                {Object.keys(weightAdjustments).length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          color: isDark ? "#e2e8f0" : "#1e293b",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <ScaleIcon fontSize="small" />
                        Pending Weight Adjustments
                      </Typography>
                    </Box>

                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                      {Object.keys(weightAdjustments).length} item(s) have
                      weight adjustments pending. These adjustments will be
                      applied when the transaction is saved.
                    </Alert>

                    <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Item</TableCell>
                            <TableCell>Original Weight</TableCell>
                            <TableCell>New Weight</TableCell>
                            <TableCell>Adjustment</TableCell>
                            <TableCell>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.values(weightAdjustments).map(
                            ({ item, adjustment }) => (
                              <TableRow key={item.id}>
                                {console.log("Rendering adjustment row for item:", item, "with adjustment:", adjustment)}
                                <TableCell>{item.name}</TableCell>
                                <TableCell>
                                  {item?.originalWeight}g
                                </TableCell>
                                <TableCell>{item?.newWeight}g</TableCell>
                                <TableCell>
                                  <Chip
                                    label={
                                      adjustment.action === "ignore"
                                        ? "Ignored"
                                        : adjustment.action === "gross_stock"
                                          ? `Gross Stock (${adjustment.metal})`
                                          : adjustment.action === "another_item"
                                            ? `Another Item (${adjustment.item?.name})`
                                            : "New Item Created"
                                    }
                                    size="small"
                                    color={
                                      adjustment.action === "ignore"
                                        ? "default"
                                        : "success"
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleManualWeightAdjustment(item)
                                    }
                                    sx={{
                                      borderRadius: 1,
                                      backgroundColor: alpha(
                                        theme.palette.warning.main,
                                        0.1,
                                      ),
                                      color: theme.palette.warning.main,
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ),
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* ACTION BUTTONS */}
                <Card
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    backdropFilter: 'blur(20px)',
                    backgroundColor: alpha(
                      isDark ? theme.palette.background.paper : '#ffffff',
                      isDark ? 0.3 : 0.6
                    ),
                    border: `1px solid ${alpha(
                      isDark ? theme.palette.divider : '#e2e8f0',
                      isDark ? 0.2 : 0.3
                    )}`,
                    boxShadow: theme.shadows[4],
                  }}
                >
                  <form onSubmit={formik.handleSubmit}>
                    <Stack direction="row" spacing={2}>
                      {!isTransactionSaved && (
                        <Button
                          type="submit"
                          variant="contained"
                          startIcon={<SaveIcon />}
                          size="large"
                          fullWidth
                          sx={{
                            py: 1.4,
                            fontWeight: 700,
                            borderRadius: 2,
                            background: isDark
                              ? 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)'
                              : 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                            '&:hover': {
                              background: isDark
                                ? 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)'
                                : 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)',
                            }
                          }}
                        >
                          Save Transaction
                        </Button>
                      )}

                      <Button
                        type="button"
                        variant="outlined"
                        startIcon={<Summarize />}
                        size="large"
                        fullWidth
                        onClick={() => setBillDetailsOpen(true)}
                        disabled={items.length === 0 && Object.keys(consolidatedItemGroups).length === 0 && transactionType !== "total"}
                        sx={{
                          py: 1.4,
                          fontWeight: 600,
                          borderRadius: 2,
                          backdropFilter: 'blur(10px)',
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.3 : 0.8
                          ),
                          border: `1.5px solid ${alpha(
                            isDark ? '#f59e0b' : '#f59e0b',
                            isDark ? 0.4 : 0.5
                          )}`,
                          color: isDark ? '#fbbf24' : '#d97706',
                        }}
                      >
                        View Bill Details
                      </Button>


                      <Button
                        type="button"
                        variant="outlined"
                        startIcon={<SaveIcon />}
                        size="large"
                        fullWidth
                        onClick={handleDownloadExcel}
                        disabled={items.length === 0 && Object.keys(consolidatedItemGroups).length === 0 && transactionType !== "total"}
                        sx={{
                          py: 1.4,
                          fontWeight: 600,
                          borderRadius: 2,
                          backdropFilter: 'blur(10px)',
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.3 : 0.8
                          ),
                          border: `1.5px solid ${alpha(
                            isDark ? '#10b981' : '#10b981',
                            isDark ? 0.4 : 0.5
                          )}`,
                          color: isDark ? '#34d399' : '#059669',
                        }}
                      >
                        Download Excel
                      </Button>

                      <Button
                        type="button"
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        size="large"
                        fullWidth
                        onClick={resetForm}
                        sx={{
                          py: 1.4,
                          fontWeight: 600,
                          borderRadius: 2,
                          backdropFilter: 'blur(10px)',
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : '#ffffff',
                            isDark ? 0.3 : 0.8
                          ),
                          border: `1.5px solid ${alpha(
                            isDark ? theme.palette.divider : '#e2e8f0',
                            isDark ? 0.3 : 0.4
                          )}`,
                          color: isDark ? '#e2e8f0' : '#475569',
                        }}
                      >
                        Reset Form
                      </Button>
                    </Stack>
                  </form>
                </Card>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <WeightAdjustmentDialog
          open={adjustmentDialogOpen}
          onClose={() => {
            setAdjustmentDialogOpen(false);
            setCurrentAdjustmentItem(null);
          }}
          item={currentAdjustmentItem}
          originalWeight={
            currentAdjustmentItem?.originalWeight ||
            currentAdjustmentItem?.weight ||
            0
          }
          newWeight={
            currentAdjustmentItem?.newWeight ||
            currentAdjustmentItem?.weight ||
            0
          }
          weightDifference={Math.abs(
            (currentAdjustmentItem?.newWeight ||
              currentAdjustmentItem?.weight ||
              0) -
            (currentAdjustmentItem?.originalWeight ||
              currentAdjustmentItem?.weight ||
              0),
          )}
          isWeightIncreased={
            (currentAdjustmentItem?.newWeight || 0) >
            (currentAdjustmentItem?.originalWeight || 0)
          }
          onAdjustmentComplete={handleAdjustmentComplete}
          onSkip={handleSkipAdjustment}
        />

        <WeightEditDialog
          open={weightEditDialogOpen}
          onClose={() => {
            setWeightEditDialogOpen(false);
            setEditingItemWeight(null);
          }}
          item={editingItemWeight}
          onConfirm={handleWeightChangeConfirm}
        />
        {/* Conditional Delete Dialog - Simple version for items without weight adjustments */}
        <SimpleDeleteDialog
          open={
            deleteConfirmDialog.open && !deleteConfirmDialog.hasWeightAdjustment
          }
          onClose={() =>
            setDeleteConfirmDialog({
              open: false,
              itemToDelete: null,
              hasWeightAdjustment: false,
            })
          }
          onConfirm={confirmDeleteItem}
          item={deleteConfirmDialog.itemToDelete}
        />

        {/* Warning Delete Dialog - For items with weight adjustments */}
        <WeightAdjustmentDeleteDialog
          open={
            deleteConfirmDialog.open && deleteConfirmDialog.hasWeightAdjustment
          }
          onClose={() =>
            setDeleteConfirmDialog({
              open: false,
              itemToDelete: null,
              hasWeightAdjustment: false,
            })
          }
          onConfirm={confirmDeleteItem}
          item={deleteConfirmDialog.itemToDelete}
        />

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
            sx={{
              width: "100%",
              borderRadius: 2,
              boxShadow: theme.shadows[3],
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
        {/* Advance Payment Selector Dialog */}
        <AdvancePaymentSelectorDialog
          open={advanceSelectorOpen}
          onClose={() => {
            setAdvanceSelectorOpen(false);
            // Mark as shown even when skipped to prevent reopening
            if (selectedCustomer?.id) {
              hasShownAdvanceDialogRef.current = selectedCustomer.id;
            }
          }}
          customerId={selectedCustomer?.id}
          customerName={selectedCustomer?.name}
          advanceBalance={customerAdvanceBalance}
          billTotal={calculations.grandTotal}
          onUseAdvance={(usageData) => {
            handleUseAdvance(usageData);
            setAdvanceSelectorOpen(false);
            // Mark as shown after using advance
            if (selectedCustomer?.id) {
              hasShownAdvanceDialogRef.current = selectedCustomer.id;
            }
          }}
          onSkip={() => {
            setAdvanceSelectorOpen(false);
            // Mark as shown when skipped
            if (selectedCustomer?.id) {
              hasShownAdvanceDialogRef.current = selectedCustomer.id;
            }
          }}
        />
        {/* Custom Alert Dialog */}
        <CustomAlertDialog
          open={customAlert.open}
          onClose={() => setCustomAlert({ ...customAlert, open: false })}
          title={customAlert.title}
          message={customAlert.message}
          severity={customAlert.severity}
          onConfirm={customAlert.onConfirm}
        />

        <BillDetailsDialog
          open={billDetailsOpen}
          onClose={() => setBillDetailsOpen(false)}
          consolidatedItemGroups={consolidatedItemGroups}
          calculations={calculations}
          items={items}
          transactionType={transactionType}
          itemConsolidationMode={itemConsolidationMode}
          showGST={showGST}
          gstMode={gstMode}
          gstType={gstType}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default CustomerTransaction;